import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import HistoricoZproInline from '../components/contatos/HistoricoZproInline';
import MessageMenu from '../components/MessageMenu';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar'; // Import specific parts
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'; // Import specific parts
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea'; // Import Textarea
import SpellCheckInput from '../components/SpellCheckInput'; // Import SpellCheckInput
import { toast } from '../hooks/use-toast';
import api from '../services/api';
import { authService } from '../services/authService';
import { mensagemService } from '../services/mensagemService';
import { kanbanService } from '../services/kanbanService';
import { ConversaResumo } from '../types/conversa';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { format } from "date-fns";
import { ptBR } from 'date-fns/locale';
import { io, Socket } from 'socket.io-client';
import socketService from '../services/socketService';
import fundoConversa from '../assets/fundo_conversa.png';
import fundoConversaDark from '../assets/fundo_conversa_dark.jpg';
import MensagemItem from '../components/MensagemItem';
import {
  ArrowLeft, Paperclip, Send, Smile, Mic, Search, MoreVertical,
  ChevronDown,
  Clock, AlertCircle, MessageSquare, Info, StickyNote,
  User, Phone, Mail, Tag, Users, LogOut, Settings, X, Video, FileText, Image as ImageIcon,
  UserIcon, Crop, Pencil,
  ALargeSmallIcon,
  Edit2,
  Wifi,
  WifiOff,
  MessageSquarePlus,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  LayoutGrid,
  Pen,
  Loader2,
  Forward,
  Zap,
  Pin,
  Layers3,
  CalendarClock
} from 'lucide-react';
import { ScrollArea } from '../components/ui/scroll-area'; // Import ScrollArea
import Header from '../components/Header';
import EmojiPicker from 'emoji-picker-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import Cropper from 'react-easy-crop';
import { Label } from '../components/ui/label';
import { renderEmojiWithFallback } from '../utils/emojiUtils';
import { DraftManager } from '../utils/draftManager';
import { formatUltimaInteracao } from '../utils/dashboardHelpers';
import { TagBadge } from '../components/ui/tag-badge';
import { SelectWithSearch } from '../components/ui/select-with-search';
import MetaTemplateSendDialog from '../components/instancias/MetaTemplateSendDialog';

// Função auxiliar para extrair nome do arquivo do caminho
const getFileNameFromPath = (path: string | null | undefined): string | null => {
  if (!path) return null;
  // Remove query params e extrai apenas o nome do arquivo
  const cleanPath = path.split('?')[0];
  const fileName = cleanPath.split('/').pop();
  return fileName || null;
};

// --- Tipos (Refinados) ---
interface Mensagem {
  id: string;
  idExterno?: string; // ID da mensagem na Evolution API
  apiMessageId?: string; // Alias for external message ID
  texto?: string;
  conteudo?: string;
  conteudoOriginal?: string; // Conteúdo original antes da edição
  dataEdicao?: string; // Data da edição
  tipo: 'texto' | 'reaction' | 'imagem' | 'audio' | 'video' | 'documento' | 'localizacao' | 'contato' | 'botao' | 'pix' | 'sticker';
  midiaUrl?: string;
  remetente: 'cliente' | 'atendente' | 'sistema';
  remetenteTipo?: 'cliente' | 'atendente' | 'sistema';
  usuarioId?: string; // ID do usuário que enviou a mensagem (se for atendente)
  timestamp: string | Date;
  status?: 'enviando' | 'enviada' | 'entregue' | 'lida' | 'erro' | 'desconhecido' | 'falhou'; // Status de entrega
  statusEntrega?: 'enviando' | 'enviada' | 'entregue' | 'lida' | 'falhou' | 'erro' | 'desconhecido' | null; // Status do backend
  deletedAt?: string; // Timestamp quando mensagem foi deletada
  updatedAt?: string; // Timestamp quando mensagem foi atualizada
  mensagemRespondidaId?: string;
  evolutionMessageId?: string;
  conversaId: string;
  contatoId: string;
  empresaId: string;
  ticketId: string;
  ticket?: {
    protocolo: string;
  };
  reactions?: any[];
  contactInfo?: any;
}

interface Contato {
  id: string;
  nome: string;
  telefone: string; // Renomeado de telefone para clareza
  avatarUrl?: string;
  email?: string;
  empresaId: string;
}

interface Instance {
  id: string;
  nome: string;
  statusConexao: string;
  numeroConectado?: string;
  apiEndpoint?: string;
  apiKey?: string;
  tipoApi?: 'EVOLUTION' | 'UAZAPI' | 'META';
  instanciaUazapiId?: string; // Meta instance → linked UAZAPI instance
}

interface TicketInfo {
  id: string;
  protocolo: string;
  instanciaId: string;
  instanciaNome: string;
  numeroConectado: string;
  dataCriacao: string | Date;
  status: string;
}

interface Conversa {
  id: string;
  contatoId: string;
  contato: Contato;
  status: 'aberto' | 'pendente' | 'fechado' | 'transferido';
  prioridade?: 'baixa' | 'media' | 'alta' | 'urgente';
  assunto?: string;
  dataCriacao: string | Date;
  dataAtualizacao: string | Date;
  dataFechamento?: string | Date;
  atendenteId?: string;
  empresaId: string;
  protocolo?: string;
  instanciaEvolutionId?: string;
  instancia?: {
    id: string;
    nome: string;
    statusConexao: string;
  };
  instanciaUazapi?: {
    id: string;
    nome: string;
    statusConexao: string;
  };
  mensagens?: Mensagem[];
}

// --- Componente Principal ---
interface AtendimentoPageProps {
  refresh: (chatResumo: ConversaResumo) => void;
  chatResumo: ConversaResumo;
  setChatResumo: React.Dispatch<React.SetStateAction<ConversaResumo | null>>;
  setStartChat: ((telefone: string) => Promise<void>) | React.Dispatch<React.SetStateAction<string>>;
}
const AtendimentoPage: React.FC<AtendimentoPageProps> = ({ refresh, chatResumo, setChatResumo, setStartChat }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversa, setConversa] = useState<Conversa | null>(null);
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [pinnedMessage, setPinnedMessage] = useState<Mensagem | null>(null);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [participants, setParticipants] = useState<any[]>([]);
  const [selectedMentions, setSelectedMentions] = useState<any[]>([]);
  const socketCallbacksRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [enviandoArquivo, setEnviandoArquivo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('detalhes');
  const [pagina, setPagina] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const [showEmoji, setShowEmoji] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showMentionModal, setShowMentionModal] = useState(false);
  const [systemUsersForMention, setSystemUsersForMention] = useState<any[]>([]);
  const [loadingSystemUsers, setLoadingSystemUsers] = useState(false);
  const [mentionReason, setMentionReason] = useState('');
  const [mentionParticipantId, setMentionParticipantId] = useState('');
  const [showAttach, setShowAttach] = useState(false); // Adicione esta linha se não existir
  const [showAttachDrop, setShowAttachDrop] = useState(false); // Corrigido: estado para drag & drop
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [showContatoModal, setShowContatoModal] = useState(false);
  const [contatoIdEdit, setContatoIdEdit] = useState('');
  const [nomeContato, setNomeContato] = useState('');
  const [telefoneContato, setTelefoneContato] = useState('');
  const [emailContato, setEmailContato] = useState('');
  // Estados para gerenciar múltiplas instâncias e tickets
  const [instances, setInstances] = useState<Instance[]>([]);
  const [selectedInstance, setSelectedInstance] = useState<Instance | null>(null);
  const [ticketsDoContato, setTicketsDoContato] = useState<TicketInfo[]>([]);
  const [showInstanceSelector, setShowInstanceSelector] = useState(false);
  const [loadingInstances, setLoadingInstances] = useState(false);
  const [notasContato, setNotasContato] = useState('');
  const [instanciaContato, setInstanciaContato] = useState('');
  const [filePreview, setFilePreview] = useState<{
    file: File;
    type: string;
    url: string;
    name: string;
    size?: number;
    crop?: { x: number; y: number };
    zoom?: number;
    readonly?: boolean;
    croppedAreaPixels?: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
  } | null>(null);
  const [fileDescription, setFileDescription] = useState('');
  const [fileQueue, setFileQueue] = useState<File[]>([]);
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioRecording, setAudioRecording] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [contatos, setContatos] = useState<any[]>([]);
  const [contatosSelecionados, setContatosSelecionados] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRefText = useRef<HTMLInputElement>(null);
  const [editMode, setEditMode] = useState<'none' | 'crop' | 'draw' | 'text' | 'sticker'>('none');
  const [drawPoints, setDrawPoints] = useState<{ x: number; y: number }[]>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [textOverImage, setTextOverImage] = useState('');
  const [textPosition, setTextPosition] = useState<{ x: number; y: number }>({ x: 50, y: 50 });
  const [isDraggingText, setIsDraggingText] = useState(false);
  const [imagePan, setImagePan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isPanningImage, setIsPanningImage] = useState(false);
  const imagePanStartRef = useRef<{ x: number; y: number; panX: number; panY: number }>({ x: 0, y: 0, panX: 0, panY: 0 });
  const imageCanvasRef = useRef<HTMLCanvasElement>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [searchTabTerm, setSearchTabTerm] = useState('');
  const [searchTabResults, setSearchTabResults] = useState<any[]>([]);
  const [searchTabLoading, setSearchTabLoading] = useState(false);
  const [searchTabError, setSearchTabError] = useState<string | null>(null);
  const [sidebarInfoCollapsed, setSidebarInfoCollapsed] = useState(true);
  const [param, setParam] = useState(true);
  const [chatConfig, setChatConfig] = useState<any>(null);
  const [tags, setTags] = useState<any[]>([]);
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [availableTagsPage, setAvailableTagsPage] = useState(1);
  const [availableTagsHasMore, setAvailableTagsHasMore] = useState(true);
  const [availableTagsLoading, setAvailableTagsLoading] = useState(false);
  const [availableTagsSearch, setAvailableTagsSearch] = useState('');
  const [contatoTags, setContatoTags] = useState<any[]>([]);
  const [kanbanColunas, setKanbanColunas] = useState<any[]>([]);
  const [notas, setNotas] = useState<any[]>([]);
  const [novaNota, setNovaNota] = useState('');
  const [editingNota, setEditingNota] = useState<any>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferToUser, setTransferToUser] = useState('');
  const [transferToQueue, setTransferToQueue] = useState('');
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [availableQueues, setAvailableQueues] = useState<any[]>([]);
  const [loadingTransfer, setLoadingTransfer] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedShareUsers, setSelectedShareUsers] = useState<string[]>([]);
  const [shareUsers, setShareUsers] = useState<any[]>([]);
  const [loadingShare, setLoadingShare] = useState(false);
  const sendButtonRef = useRef<HTMLButtonElement>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
  const [isOwnMessage, setIsOwnMessage] = useState(false);
  const [replyingTo, setReplyingTo] = useState<any | null>(null);
  const [reactingTo, setReactingTo] = useState<any | null>(null);
  const [editingMessage, setEditingMessage] = useState<any | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.classList.contains('dark'));
  const observerRef = useRef<MutationObserver | null>(null);
  const user = authService.getCurrentUser();
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const scrollViewportRef = useRef<HTMLDivElement | null>(null);
  const fetchMoreLockRef = useRef(false);
  const scrollRestoreRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);
  const [activeDate, setActiveDate] = useState<string | null>(null);
  const [reactItems, setReactItems] = useState(false);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [imageZoom, setImageZoom] = useState(1);
  const [showMetaTemplateDialog, setShowMetaTemplateDialog] = useState(false);

  // Signature toggle state for per-conversation control
  const [sendSignature, setSendSignature] = useState<boolean>(false);

  // Define isGrupo from chatResumo
  const isGrupo = chatResumo.ultimaMensagem?.isGrupo || false;
  // Grupo atendido pela Groups API da Meta (ticket de instância Meta): a API não permite editar/apagar mensagens
  const grupoMeta =
    !!((chatResumo as any)?.instanciaMeta || (chatResumo as any)?.instanciaMetaId) &&
    !(chatResumo as any)?.instanciaUazapi &&
    !(chatResumo as any)?.instanciaUazapiId &&
    !!(chatResumo?.ultimaMensagem?.isGrupo || chatResumo?.contato?.telefone?.includes('@g.us'));

  // Linked Meta instance for hybrid mode (UAZAPI ticket with linked Meta instance)
  const linkedMetaForHybrid = useMemo(() => {
    if (selectedInstance?.tipoApi !== 'META' && selectedInstance?.tipoApi !== 'UAZAPI') return null;
    // If current instance is Meta and has a linked UAZAPI, we know it's hybrid
    if (selectedInstance?.tipoApi === 'META' && selectedInstance?.instanciaUazapiId) return selectedInstance;
    // If current instance is UAZAPI, find a Meta instance linked to this UAZAPI
    if (selectedInstance?.tipoApi === 'UAZAPI') {
      return instances.find(i => i.tipoApi === 'META' && i.instanciaUazapiId === selectedInstance.id) || null;
    }
    return null;
  }, [selectedInstance, instances]);

  const isWithin24hWindow = useMemo(() => {
    if (selectedInstance?.tipoApi !== 'META' && selectedInstance?.tipoApi !== 'UAZAPI') return true;
    
    // For hybrid UAZAPI instances (linked to Meta), also check 24h window
    // because we need to allow template sends to reopen the window
    if (selectedInstance?.tipoApi === 'UAZAPI' && !linkedMetaForHybrid) return true;

    // A janela de 24h da Meta abre com mensagem do cliente.
    // Considerar apenas a última mensagem do cliente (ignora atendente/sistema).
    const lastMsg = mensagens
      .slice()
      .reverse()
      .find(m => m.remetenteTipo === 'cliente');
    if (!lastMsg) return false;
    
    const lastMsgTime = new Date(lastMsg.timestamp).getTime();
    const now = new Date().getTime();
    const diffHours = (now - lastMsgTime) / (1000 * 60 * 60);
    
    return diffHours <= 24;
  }, [mensagens, selectedInstance, linkedMetaForHybrid]);

  // Bloqueio de digitação só é obrigatório na API Oficial da Meta (fora da janela 24h).
  // UAZAPI (inclusive híbrido) consegue enviar mensagens regulares normalmente.
  const isMetaRestrictedInput = useMemo(() => {
    return selectedInstance?.tipoApi === 'META' && !isWithin24hWindow;
  }, [selectedInstance, isWithin24hWindow]);

  // Exibição do balão de 24h: vale para Meta puro e híbrido UAZAPI+META (guia para reabrir janela via template)
  const show24hBalloon = useMemo(() => {
    return !isWithin24hWindow && (selectedInstance?.tipoApi === 'META' || (selectedInstance?.tipoApi === 'UAZAPI' && linkedMetaForHybrid));
  }, [isWithin24hWindow, selectedInstance, linkedMetaForHybrid]);

  // Forward modal states
  const [showForwardModal, setShowForwardModal] = useState(false);
  const [forwardMessage, setForwardMessage] = useState<any | null>(null);
  const [forwardContacts, setForwardContacts] = useState<any[]>([]);
  const [forwardSearchTerm, setForwardSearchTerm] = useState('');
  const [forwardLoading, setForwardLoading] = useState(false);
  const [forwardLoadingMore, setForwardLoadingMore] = useState(false);
  const [selectedForwardContacts, setSelectedForwardContacts] = useState<string[]>([]);
  // Estados para paginação
  const [forwardPage, setForwardPage] = useState(1);
  const [forwardHasMore, setForwardHasMore] = useState(true);
  const [forwardTotalCount, setForwardTotalCount] = useState(0);
  const [forwardScrollPosition, setForwardScrollPosition] = useState(0);
  const forwardScrollRef = useRef<HTMLDivElement>(null);

  // Reset pagination when search term changes
  useEffect(() => {
    if (showForwardModal) {
      const timeoutId = setTimeout(() => {
        loadForwardContacts(1, true);
      }, 300); // Debounce search

      return () => clearTimeout(timeoutId);
    }
  }, [forwardSearchTerm, showForwardModal]);

  // Estado para loading de início de atendimento
  const [startingAttendance, setStartingAttendance] = useState(false);

  // Estados para mensagens rápidas
  const [showQuickMessages, setShowQuickMessages] = useState(false);
  const [quickMessages, setQuickMessages] = useState<any[]>([]);
  const [quickMessageFilter, setQuickMessageFilter] = useState('');
  const [loadingQuickMessages, setLoadingQuickMessages] = useState(false);

  // Estados para envio em bloco
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [blockCategories, setBlockCategories] = useState<any[]>([]);
  const [loadingBlockCategories, setLoadingBlockCategories] = useState(false);
  const [blockSendingState, setBlockSendingState] = useState<{ blocoId: string; sending: boolean; message?: string } | null>(null);

  // Estados para agendamento de envio de mensagem
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleMessage, setScheduleMessage] = useState('');
  const [scheduleDateTime, setScheduleDateTime] = useState('');
  const [scheduleFile, setScheduleFile] = useState<File | null>(null);
  const [schedulingMessage, setSchedulingMessage] = useState(false);
  const scheduleFileInputRef = useRef<HTMLInputElement>(null);
  const [scheduledMessages, setScheduledMessages] = useState<any[]>([]);

  // Estados para mensagens interativas (botões/listas/enquetes)
  const [showInteractiveMode, setShowInteractiveMode] = useState(false);
  const [interactiveType, setInteractiveType] = useState<'buttons' | 'list'>('buttons');
  const [interactiveText, setInteractiveText] = useState('');
  const [interactiveOptions, setInteractiveOptions] = useState<string[]>(['', '']);
  const [interactiveFooter, setInteractiveFooter] = useState('');
  const [interactiveListButton, setInteractiveListButton] = useState(t('atendimento.options'));
  const [interactiveSending, setInteractiveSending] = useState(false);

  // Load chat configuration
  useEffect(() => {
    const loadChatConfig = async () => {
      try {
        const empresaId = user?.empresaId;
        if (!empresaId) return;

        const response = await api.get(`/configuracoes-chat/empresa/${empresaId}`);
        setChatConfig(response?.data?.configuracao);
      } catch (error) {
        console.error('Erro ao carregar configurações do chat:', error);
      }
    };
    loadChatConfig();
  }, [user?.empresaId]);

  // Update sendSignature when chatConfig loads
  useEffect(() => {
    if (chatConfig) {
      const shouldSendSignature = chatConfig.assinaturaAtendente || false;
      setSendSignature(shouldSendSignature);
    }
  }, [chatConfig]);

  const loadPinnedMessage = async () => {
    if (!chatResumo?.id) return;
    try {
      const response = await api.get(`/mensagens/ticket/${chatResumo.id}/pinned`);
      if (response.data.success) {
        setPinnedMessage(response.data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar mensagem fixada:', error);
    }
  };

  const handlePinMessage = async (messageId: string, pin: boolean) => {
    try {
      const response = await api.post(`/mensagens/${messageId}/pin`, { pin });
      if (response.data.success) {
        // A atualização real ocorrerá via socket
        setPinnedMessage(pin ? response.data.data : null);
      }
      setIsMenuOpen(false);
      setSelectedMessage(null);
    } catch (error) {
      console.error('Erro ao fixar/desafixar mensagem:', error);
      toast({ title: t('common.error'), description: t('atendimento.toastErrorPinState'), variant: "destructive" });
    }
  };

  // Load tags and notes when chat opens
  useEffect(() => {
    if (chatResumo?.id) {
      loadTags();
      loadContatoTags();
      loadNotas();
      loadPinnedMessage();
      syncWhatsAppLabels();
      if (chatConfig?.permitirKanban) loadKanbanColunas(); // Sincronizar labels do WhatsApp

      // Carregar rascunho se existir
      const draftContent = DraftManager.loadDraft(chatResumo.id);
      if (draftContent) {
        setNovaMensagem(draftContent);
      } else {
        setNovaMensagem('');
      }
    }
  }, [chatResumo?.id]);

  // Load available tags for empresa
  useEffect(() => {
    if (user?.empresaId) {
      loadAvailableTags(1, '', false);
    }
  }, [user?.empresaId]);

  // Carregar instâncias disponíveis
  const loadInstances = async () => {
    try {
      setLoadingInstances(true);
      const response = await api.get('/instancias');
      const instancesData = response.data.instancias || [];

      // Para todos os usuários (admin e comuns), usar o endpoint que já aplica a lógica correta
      if (user?.role === 'Admin' || user?.role === 'SuperAdmin') {
        // Admins veem todas as instâncias
        setInstances(instancesData);

        // Definir instância atual como selecionada
        const instanciaAtualId = chatResumo?.instancia?.id || chatResumo?.instanciaUazapi?.id || chatResumo?.instanciaMeta?.id;
        if (instanciaAtualId) {
          const currentInstance = instancesData.find((inst: Instance) =>
            inst.id === instanciaAtualId
          );
          setSelectedInstance(currentInstance || null);
        }
      } else {
        // Usuários comuns usam o endpoint que já aplica a lógica de permissão
        try {
          const responsePermissoes = await api.get(`/instancias/usuario/${user.id}/acesso`);
          const instanciasPermitidas = responsePermissoes.data || [];

          setInstances(instanciasPermitidas);

          // Definir instância atual como selecionada
          const instanciaAtualId = chatResumo?.instancia?.id || chatResumo?.instanciaUazapi?.id || chatResumo?.instanciaMeta?.id;
          if (instanciaAtualId) {
            const currentInstance = instanciasPermitidas.find((inst: Instance) =>
              inst.id === instanciaAtualId
            );
            setSelectedInstance(currentInstance || null);
          }
        } catch (error) {
          console.error('Erro ao carregar instâncias com permissões:', error);
          // Se falhar, mostrar todas as instâncias como fallback
          setInstances(instancesData);

          const instanciaAtualId = chatResumo?.instancia?.id || chatResumo?.instanciaUazapi?.id || chatResumo?.instanciaMeta?.id;
          if (instanciaAtualId) {
            const currentInstance = instancesData.find((inst: Instance) =>
              inst.id === instanciaAtualId
            );
            setSelectedInstance(currentInstance || null);
          }
        }
      }
    } catch (error) {
      console.error('Erro ao carregar instâncias:', error);
      setInstances([]);
    } finally {
      setLoadingInstances(false);
    }
  };

  // Carregar todos os tickets do contato atual
  const loadTicketsDoContato = async () => {
    if (!chatResumo?.contato?.telefone) return;

    try {
      const response = await api.get(`/chats/contato/${chatResumo.contato.telefone}/tickets`);
      const tickets = response.data.tickets || [];
      setTicketsDoContato(tickets);
    } catch (error) {
      console.error('Erro ao carregar tickets do contato:', error);
    }
  };

  // Carregar mensagens rápidas
  const loadQuickMessages = async () => {
    if (!user?.empresaId) return;

    try {
      setLoadingQuickMessages(true);
      const response = await api.get('/mensagens-rapidas', {
        params: {
          pageSize: 100, // Buscar todas as mensagens
          ativa: true,
          sortBy: 'chave',
          sortOrder: 'asc'
        }
      });

      // A API retorna um objeto paginado com 'data' e 'pagination'
      const apiResponse = response.data;
      console.log('Quick Messages API Response:', apiResponse);
      const messages = apiResponse?.data || [];
      console.log('Quick Messages loaded:', messages);
      setQuickMessages(Array.isArray(messages) ? messages : []);
    } catch (error) {
      console.error('Erro ao carregar mensagens rápidas:', error);
      setQuickMessages([]);
    } finally {
      setLoadingQuickMessages(false);
    }
  };

  // Carregar blocos para envio em bloco
  const loadBlockCategories = async () => {
    if (!user?.empresaId) return;
    try {
      setLoadingBlockCategories(true);
      const response = await api.get('/mensagens-rapidas/categorias', { params: { pageSize: 50 } });
      setBlockCategories(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar blocos:', error);
      setBlockCategories([]);
    } finally {
      setLoadingBlockCategories(false);
    }
  };

  // Enviar bloco de mensagens
  const sendBlock = async (blocoId: string) => {
    if (!chatResumo?.id) return;
    try {
      setBlockSendingState({ blocoId, sending: true, message: t('atendimento.addingToQueue') });
      const response = await api.post('/mensagens-rapidas/send-block', {
        ticketId: chatResumo.id,
        blocoId,
      });
      setBlockSendingState({ blocoId, sending: false, message: response.data.message });
      toast({
        title: t('atendimento.toastBlockSent'),
        description: response.data.message,
      });
      setTimeout(() => setBlockSendingState(null), 3000);
    } catch (error: any) {
      console.error('Erro ao enviar bloco:', error);
      setBlockSendingState({ blocoId, sending: false, message: error?.response?.data?.message || t('atendimento.toastBlockSendError') });
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastBlockSendError'),
        variant: 'destructive',
      });
    }
  };

  // Função para detectar comando slash e filtrar mensagens
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNovaMensagem(value);

    // Detectar comando slash
    const slashMatch = value.match(/\/(\w*)$/);
    if (slashMatch) {
      const searchTerm = slashMatch[1];
      setQuickMessageFilter(searchTerm);
      setShowQuickMessages(true);
    } else {
      setShowQuickMessages(false);
      setQuickMessageFilter('');
    }
  };

  // Função para obter saudação baseada no horário
  const getSaudacao = () => {
    const hora = new Date().getHours();
    if (hora >= 6 && hora < 12) {
      return 'Bom dia';
    } else if (hora >= 12 && hora < 18) {
      return 'Boa tarde';
    } else {
      return 'Boa noite';
    }
  };

  // Função para selecionar mensagem rápida
  const selectQuickMessage = (mensagem: any) => {
    const currentValue = novaMensagem;
    const slashMatch = currentValue.match(/\/(\w*)$/);

    let mensagemProcessada = mensagem.mensagem;

    // Substituir variáveis
    if (mensagemProcessada.includes('{nome_atendente}') && user?.nome) {
      mensagemProcessada = mensagemProcessada.replace(/{nome_atendente}/g, user.nome);
    }

    if (mensagemProcessada.includes('{empresa}') && (user as any).empresa?.nome) {
      mensagemProcessada = mensagemProcessada.replace(/{empresa}/g, (user as any).empresa?.nome);
    }

    // Substituir nome do contato se tiver
    if (mensagemProcessada.includes('{nome_contato}')) {
      const nome_contato = chatResumo?.contato?.nome || '';
      if (nome_contato) {
        mensagemProcessada = mensagemProcessada.replace(/{nome_contato}/g, nome_contato);
      }
    }

    // Substituir saudação
    if (mensagemProcessada.includes('{saudacao}')) {
      const saudacao = getSaudacao();
      mensagemProcessada = mensagemProcessada.replace(/{saudacao}/g, saudacao);
    }

    // Limpar variáveis não substituídas (remover as chaves)
    mensagemProcessada = mensagemProcessada.replace(/{nome_atendente}/g, '').replace(/{empresa}/g, '').replace(/{nome_contato}/g, '').replace(/{saudacao}/g, '');

    let newValue = currentValue;
    if (slashMatch) {
      // Substituir o comando /chave pela mensagem processada
      newValue = currentValue.replace(/\/\w*$/, mensagemProcessada);
    } else {
      // Adicionar mensagem ao final
      newValue = currentValue + (currentValue ? ' ' : '') + mensagemProcessada;
    }

    setNovaMensagem(newValue);
    setShowQuickMessages(false);
    setQuickMessageFilter('');

    // Focar no input
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // Filtrar mensagens rápidas
  const filteredQuickMessages = (quickMessages || []).filter(msg =>
    msg && (
      (msg.chave && msg.chave.toLowerCase().includes(quickMessageFilter.toLowerCase())) ||
      (msg.mensagem && msg.mensagem.toLowerCase().includes(quickMessageFilter.toLowerCase()))
    )
  );

  // Carregar instâncias e tickets quando abrir chat
  useEffect(() => {
    if (chatResumo?.id) {
      setSelectedInstance(null);
      loadInstances();
      loadTicketsDoContato();
      loadQuickMessages();

      // Tentar obter os dados completos do ticket para garantir que temos a instância
      const fetchTicketDetails = async () => {
        try {
          const response = await api.get(`/chats/${chatResumo.id}`);
          // O backend retorna { conversa: { ... } } ou o ticket direto
          const ticketCompleto = response.data.conversa || response.data;

          if (ticketCompleto) {
            // Tentar IDs primeiro, depois objetos (Evolution, UAZAPI, Meta)
            const instId = ticketCompleto.instanciaId || ticketCompleto.instanciaUazapiId || ticketCompleto.instanciaMetaId;
            const instObj = ticketCompleto.instancia || ticketCompleto.instanciaUazapi || ticketCompleto.instanciaMeta;

            console.log('Dados do ticket para auto-seleção:', { instId, instObj });

            if (instObj) {
              setSelectedInstance(instObj as any);
            } else if (instId) {
              // Se tiver só o ID, buscar na lista de instâncias carregadas
              const found = instances.find(i => i.id === instId);
              if (found) {
                setSelectedInstance(found);
              }
            }
          }
        } catch (error) {
          console.error('Erro ao buscar detalhes do ticket para auto-seleção:', error);
          // Fallback para o que já temos no chatResumo
          const fallbackId = (chatResumo as any).instanciaId || (chatResumo as any).instanciaUazapiId || (chatResumo as any).instanciaMetaId;
          const fallbackObj = (chatResumo as any).instancia || (chatResumo as any).instanciaUazapi || (chatResumo as any).instanciaMeta;

          if (fallbackObj) {
            setSelectedInstance(fallbackObj as any);
          } else if (fallbackId) {
            const found = instances.find(i => i.id === fallbackId);
            if (found) setSelectedInstance(found);
          }
        }
      };

      fetchTicketDetails();
    }
  }, [chatResumo?.id]);

  // Fechar seletor de instâncias quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showInstanceSelector && !(event.target as HTMLElement).closest('.instance-selector-dropdown')) {
        setShowInstanceSelector(false);
      }
    };

    if (showInstanceSelector) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showInstanceSelector]);

  // Função para mudar instância de envio
  const handleMudarInstancia = async (instance: Instance) => {
    try {
      if (chatResumo?.id) {
        const response = await api.put(`/chats/${chatResumo.id}/instancia`, {
          instanciaId: instance.id
        });
        if (response.data) {
          setChatResumo({
            ...chatResumo,
            ...response.data,
            instancia: response.data.instancia || response.data.instanciaUazapi || response.data.instanciaMeta || {
              id: instance.id,
              nome: instance.nome,
              statusConexao: instance.statusConexao,
              tipoApi: instance.tipoApi
            }
          });
        }
      } else {
        // Apenas atualizar a instância de envio no ticket atual
        setChatResumo({
          ...chatResumo,
          instancia: {
            id: instance.id,
            nome: instance.nome,
            statusConexao: instance.statusConexao,
            tipoApi: instance.tipoApi
          }
        });
      }

      setSelectedInstance(instance);
      setShowInstanceSelector(false);

      toast({
        title: t('atendimento.toastInstanceChanged'),
        description: t('atendimento.toastInstanceChangedDesc', { instance: instance.nome }),
        variant: "default"
      });
    } catch (error) {
      console.error('Erro ao mudar instância:', error);
toast({
        title: t('common.error'),
        description: t('atendimento.toastInstanceChangeError'),
        variant: "destructive",
      });
    }
  };
  // Handle starting a new chat with a contact
  const handleStartNewChatFromContato = useCallback(async (telefone: string) => {
    if (typeof setStartChat === 'function') {
      if (setStartChat.length === 1) {
        // It's a setState function
        (setStartChat as React.Dispatch<React.SetStateAction<string>>)(telefone);
      } else {
        // It's an async function
        await (setStartChat as (telefone: string) => Promise<void>)(telefone);
      }
    }
  }, [setStartChat]);

  const loadAvailableTags = async (page = 1, search = '', append = false) => {
    if (availableTagsLoading) return;
    setAvailableTagsLoading(true);
    try {
      const response = await api.get('/tags', { params: { page, pageSize: 20, search } });
      const items = response.data?.data || response.data?.tags || [];
      const total = response.data?.pagination?.total ?? response.data?.total ?? items.length;
      const normalizedItems = Array.isArray(items) ? items : [];
      setAvailableTags(prev => append ? [...prev, ...normalizedItems] : normalizedItems);
      setAvailableTagsPage(page);
      setAvailableTagsHasMore((append ? availableTags.length + normalizedItems.length : normalizedItems.length) < total);
    } catch (error) {
      console.error('Erro ao carregar tags disponíveis:', error);
      if (!append) setAvailableTags([]);
    } finally {
      setAvailableTagsLoading(false);
    }
  };

  const loadTags = async () => {
    if (!chatResumo?.id) return null;
    try {
      const response = await api.get(`/tags/ticket/${chatResumo.id}`);
      const updatedTags = response.data.tags || [];
      setTags(updatedTags);
      return updatedTags;
    } catch (error) {
      console.error('Erro ao carregar tags do ticket:', error);
      return null;
    }
  };

  const loadContatoTags = async () => {
    if (!chatResumo?.contato?.id) return;
    try {
      const response = await api.get(`/tags/contato/${chatResumo.contato.id}`);
      setContatoTags(response.data.tags || []);
    } catch (error) {
      console.error('Erro ao carregar tags do contato:', error);
    }
  };

  const syncWhatsAppLabels = async () => {
    if (!chatResumo?.id) return;
    try {
      await api.post(`/tags/ticket/${chatResumo.id}/sync`);
    } catch (error) {
      console.error('Erro ao sincronizar labels do WhatsApp:', error);
      // Não mostrar erro ao usuário, é uma operação em background
    }
  };

  const addTag = async (tagId: string) => {
    if (!chatResumo?.id || !user?.empresaId) return;
    try {
      await api.post('/tags/ticket', {
        ticketId: chatResumo.id,
        tagId,
        empresaId: user.empresaId,
      });
      await loadTags();
      toast({
        title: t('common.success'),
        description: t('atendimento.toastTagAdded'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastTagAddError'),
        variant: 'destructive',
      });
    }
  };

  const removeTag = async (tagId: string) => {
    if (!chatResumo?.id) return;
    try {
      await api.delete(`/tags/ticket/${chatResumo.id}/${tagId}`);
      await loadTags();
      toast({
        title: t('common.success'),
        description: t('atendimento.toastTagRemoved'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastTagRemoveError'),
        variant: 'destructive',
      });
    }
  };

  const loadKanbanColunas = async () => {
    if (!user?.empresaId) return;
    try {
      const response = await kanbanService.listarColunas();
      setKanbanColunas(response.data.data || []);
    } catch (error) {
      console.error('Erro ao carregar colunas Kanban:', error);
    }
  };

  const handleKanbanColunaChange = async (selected: { value: string }[]) => {
    if (!chatResumo?.id || selected.length === 0) return;
    const colunaId = selected[0].value;
    const coluna = kanbanColunas.find((c: any) => c.id === colunaId);
    const novoStatus = coluna?.titulo === 'Pendentes' ? 'pendente' : coluna?.titulo === 'Fechados' ? 'fechado' : 'aberto';
    try {
      await kanbanService.moverTicket(chatResumo.id, { kanbanColunaId: colunaId });
      setChatResumo((prev: any) => ({
        ...prev,
        kanbanColunaId: colunaId,
        status: novoStatus,
        statusTicket: novoStatus,
      }));
      if (refresh && typeof refresh === 'function') {
        refresh({
          ...chatResumo,
          kanbanColunaId: colunaId,
          status: novoStatus,
          statusTicket: novoStatus,
        } as any);
      }
      toast({
        title: t('common.success'),
        description: t('atendimento.toastTicketMoved', { column: coluna?.titulo || t('atendimento.column') }),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastTicketMoveError'),
        variant: 'destructive',
      });
    }
  };

  const loadNotas = async () => {
    if (!chatResumo?.id) return;
    try {
      const response = await api.get(`/notas/ticket/${chatResumo.id}`);
      setNotas(response.data.notas || []);
    } catch (error) {
      console.error('Erro ao carregar notas do ticket:', error);
    }
  };

  const loadParticipants = useCallback(async () => {
    if (chatResumo?.id && (chatResumo?.contato?.telefone?.includes('@g.us') || chatResumo?.isGroup || chatResumo?.ultimaMensagem?.isGrupo)) {
      try {
        const response = await api.get(`/chats/${chatResumo.id}/participants`);
        if (response.data && Array.isArray(response.data)) {
          setParticipants(response.data);
        } else if (response.data && response.data.participants) {
          setParticipants(response.data.participants);
        } else {
          setParticipants([]);
        }
      } catch (error) {
        console.error('Erro ao buscar participantes do grupo:', error);
        setParticipants([]);
      }
    } else {
      setParticipants([]);
    }
  }, [chatResumo?.id, chatResumo?.contato?.telefone, chatResumo?.isGroup, chatResumo?.ultimaMensagem?.isGrupo]);

  useEffect(() => {
    if (chatResumo?.id) {
      loadParticipants();
    }
  }, [chatResumo?.id, loadParticipants]);

  const createNota = async () => {
    if (!chatResumo?.id || !novaNota.trim()) return;
    try {
      await api.post('/notas', {
        ticketId: chatResumo.id,
        conteudo: novaNota,
      });
      setNovaNota('');
      await loadNotas();
      toast({
        title: t('common.success'),
        description: t('atendimento.toastNoteCreated'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastNoteCreateError'),
        variant: 'destructive',
      });
    }
  };

  const deleteNota = async (notaId: string) => {
    try {
      await api.delete(`/notas/${notaId}`);
      await loadNotas();
      toast({
        title: t('common.success'),
        description: t('atendimento.toastNoteDeleted'),
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastNoteDeleteError'),
        variant: 'destructive',
      });
    }
  };

  // Load users and queues for transfer (called when opening modal)
  const loadUsersAndQueues = useCallback(async () => {
    if (!user?.empresaId) return;
    setLoadingTransfer(true);
    try {
      const [usersResponse, queuesResponse] = await Promise.all([
        api.get('/usuarios/transferencia'),
        api.get('/filas/transferencia')
      ]);
      const users = Array.isArray(usersResponse.data.usuarios) ? usersResponse.data.usuarios : [];
      const queues = Array.isArray(queuesResponse.data.filas) ? queuesResponse.data.filas : [];
      setAvailableUsers(users);
      setAvailableQueues(queues);
    } catch (error) {
      console.error('Erro ao carregar usuários e filas:', error);
      setAvailableUsers([]);
      setAvailableQueues([]);
    } finally {
      setLoadingTransfer(false);
    }
  }, [user?.empresaId]);

  const handleTransferTicket = async () => {
    if (!chatResumo?.id) return;

    if (!transferToUser && !transferToQueue) {
      toast({
        title: t('common.error'),
        description: t('atendimento.toastTransferNoTarget'),
        variant: 'destructive',
      });
      return;
    }

    try {
      if (transferToUser) {
        // Transferência para usuário (mantém regra de negócio existente)
        await api.post('/tickets/transfer', {
          ticketId: chatResumo.id,
          novoUsuarioId: transferToUser,
          novaFilaId: null,
        });
      } else if (transferToQueue) {
        // Transferência para fila usando novo endpoint
        await api.patch(`/chats/${chatResumo.id}/fila`, { filaId: transferToQueue });
      }

      // Feedback
      toast({ title: t('common.success'), description: t('atendimento.toastTicketTransfered') });

      // Fechar modal e limpar campos
      setShowTransferModal(false);
      setTransferToUser('');
      setTransferToQueue('');

      // Atualizar estado local da conversa sem recarregar a página
      try {
        const [conversaResp] = await Promise.all([
          api.get(`/chats/${chatResumo.id}`),
        ]);
        const conv = conversaResp.data;
        setConversa(conv);
        // Atualiza resumo minimamente (fila/atendente/status)
        setChatResumo(prev => prev ? {
          ...prev,
          statusTicket: (conv.status || prev.statusTicket),
          atendenteId: conv.atendenteId || prev.atendenteId,
        } : prev as any);
      } catch (e) {
        // silencioso
      }
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastTicketTransferError'),
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    const htmlElement = document.documentElement;
    const updateTheme = () => {
      setIsDarkMode(htmlElement.classList.contains('dark'));
    };

    // Configura o MutationObserver para observar mudanças nas classes do html
    observerRef.current = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          updateTheme();
        }
      });
    });

    // Inicia a observação
    observerRef.current.observe(htmlElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Limpa o observer quando o componente for desmontado
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Função para lidar com o clique direito em uma mensagem (estável)
  const handleContextMenu = useCallback((e: React.MouseEvent, messageId: string, isAtendente: boolean) => {
    e.preventDefault();

    // Obter o elemento da mensagem clicada
    const messageElement = e.currentTarget as HTMLElement;
    messageElement.classList.add('bg-yellow-100', '!bg-yellow-100');
    if (!messageElement) return;

    // O MessageMenu posiciona-se ao lado deste elemento (esquerda p/ atendente, direita p/ cliente)
    setSelectedMessage(messageId);
    setIsOwnMessage(isAtendente);
    setMenuAnchor(messageElement);
    setIsMenuOpen(true);
  }, []);

  // Abrir pré-visualização de mídia (estável)
  const openPreview = useCallback((payload: { type: 'imagem' | 'video' | 'documento' | 'sticker'; url: string; name?: string; readonly?: boolean; file?: File; crop?: any; zoom?: number; croppedAreaPixels?: any }) => {
    const name = payload.name || '';
    const isPdfName = /\.pdf$/i.test(name);
    const syntheticFile = payload.file || ({
      name,
      type: payload.type === 'documento' ? (isPdfName ? 'application/pdf' : 'application/octet-stream') : `${payload.type}/*`
    } as File);
    setFilePreview({
      file: syntheticFile,
      type: payload.type,
      url: payload.url,
      readonly: payload.readonly ?? true,
      name
    });
    setShowFilePreview(true);
  }, []);

  // Funções para as ações do menu
  const handleReply = () => {
    if (!selectedMessage) return;
    const message = mensagens.find(m => m.id === selectedMessage);
    if (editingMessage) setEditingMessage(null);
    setReplyingTo(message || null);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    setSelectedMessage(null);
    setIsMenuOpen(false);
  };

  const handleEdit = () => {
    if (!selectedMessage) return;
    const message = mensagens.find(m => m.id === selectedMessage);
    if (!message) return;
    if (replyingTo) setReplyingTo(null);

    let messageContent = message.conteudo || '';

    // Process emojis to ensure consistent rendering
    messageContent = renderEmojiWithFallback(messageContent);

    // Função auxiliar para escapar caracteres especiais em uma string para uso em RegExp
    function escapeRegExp(conteudo: string) {
      return conteudo.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    }

    // Remove todas as ocorrências do padrão do nome do usuário: *Nome*:\n
    const userNamePattern = escapeRegExp(`*${user.nome}*:\n`);
    const userNameRegex = new RegExp(userNamePattern, 'g');
    messageContent = messageContent.replace(userNameRegex, '');

    // Remove a assinatura do final (se existir e estiver configurada)
    if (chatConfig?.assinaturaAtendente && chatConfig?.textoAssinatura) {
      const signature = chatConfig.textoAssinatura
        .replace('{nome_atendente}', user.nome)
        .replace('{empresa}', (user as any).empresa?.nome || '');
      const signaturePattern = escapeRegExp(`\n\n${signature}`);
      const signatureRegex = new RegExp(`${signaturePattern}$`, '');
      messageContent = messageContent.replace(signatureRegex, '');
    }

    setNovaMensagem(messageContent.trim());
    setEditingMessage(message || null);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    setSelectedMessage(null);
    setIsMenuOpen(false);
  };

  const handleReact = () => {
    if (!selectedMessage) return;
    const messageToReact = mensagens.find(m => m.id === selectedMessage);
    if (messageToReact) {
      setReactingTo(messageToReact || null);
      setReactItems(!reactItems);
    }
  };

  const handleDelete = async () => {
    if (!selectedMessage) return;
    try {
      const instanciaId = selectedInstance?.id || (chatResumo as any).instancia?.id || (chatResumo as any).instanciaUazapi?.id || (chatResumo as any).instanciaMeta?.id || (chatResumo as any).instanciaMetaId || (chatResumo as any).instanciaId;

      await mensagemService.deletar(
        chatResumo.remoteJid,
        selectedMessage,
        instanciaId
      );
    } catch (err: any) {
      console.error("Erro ao deletar mensagem:", err);
      const errorMsg = err.response?.data?.message || t('atendimento.toastDeleteMessageFailed');
      toast({ title: t('common.error'), description: errorMsg, variant: "destructive" });
    } finally {
      setSelectedMessage(null);
      setIsMenuOpen(false);
    }
  };

  const handleForward = useCallback(() => {
    if (!selectedMessage) return;
    const message = mensagens.find(m => m.id === selectedMessage);
    if (message) {
      setForwardMessage(message);
      setSelectedForwardContacts([]);
      setShowForwardModal(true);
      loadForwardContacts(1, true);
    }
    setSelectedMessage(null);
    setIsMenuOpen(false);
  }, [selectedMessage, mensagens]);

  // Sincronizar status do chatResumo com estado local
  useEffect(() => {
    if (chatResumo && conversa) {
      // Se o status do chatResumo for diferente do status da conversa local, atualiza
      if (chatResumo.statusTicket !== conversa.status) {
        console.log('Sincronizando status:', chatResumo.statusTicket, '->', conversa.status);
        setConversa(prev => prev ? {
          ...prev,
          status: chatResumo.statusTicket || prev.status
        } : prev);

        // Forçar re-renderização dos componentes que dependem do status
        setTimeout(() => {
          // Pequeno delay para garantir que o estado foi atualizado
        }, 100);
      }
    }
  }, [chatResumo?.statusTicket, chatResumo?.id]); // Removido conversa?.status para evitar loop infinito

  // Foco correto no input ao abrir conversa e após enviar mensagem
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    // Don't clear novaMensagem here to allow draft to be loaded
    // setNovaMensagem(''); // REMOVED: This was clearing the draft
    setEnviando(false);
    setEnviandoArquivo(false);
    setError(null);
    setActiveTab('detalhes');
    setPagina(1);
    setHasMore(true);
    setIsFetchingMore(false);
    setShowEmoji(false);
    setShowAttach(false);
    setShowAttachDrop(false);
    setShowFilePreview(false);
    setFilePreview(null);
    setFileDescription('');
    setFileQueue([]);
    setShowAudioRecorder(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setAudioRecording(false);
    setSearchTerm('');
    setSearchResults([]);
    setSearchIndex(0);
    setShowContactDialog(false);
    setContatos([]);
    setContatosSelecionados([]);
    setEditMode('none');
    setDrawPoints([]);
    setIsDrawing(false);
    setTextOverImage('');
    setTextPosition({ x: 50, y: 50 });
    setIsDraggingText(false);
    setSearchTabTerm('');
    setSearchTabResults([]);
    setSearchTabLoading(false);
    setSearchTabError(null);
    setSidebarInfoCollapsed(true);
    setParam(true);
  }, [chatResumo.id, enviando]);

  // Buscar usuários do sistema para menção quando o modal abrir
  useEffect(() => {
    const fetchSystemUsers = async () => {
      if (showMentionModal && chatResumo?.id) {
        setLoadingSystemUsers(true);
        try {
          const response = await api.get(`/usuarios/ticket/${chatResumo.id}/mencao`);
          setSystemUsersForMention(response.data.usuarios || []);
        } catch (error) {
          console.error('Erro ao carregar usuários do sistema:', error);
          toast({ title: t('common.error'), description: t('atendimento.toastMentionUsersLoadError'), variant: 'destructive' });
        } finally {
          setLoadingSystemUsers(false);
        }
      }
    };
    fetchSystemUsers();
  }, [showMentionModal, chatResumo?.id, toast]);

  // --- Funções Auxiliares ---
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const viewport = scrollViewportRef.current;
    if (viewport) {
      setTimeout(() => {
        viewport.scrollTo({ top: viewport.scrollHeight, behavior });
      }, 30);
      return;
    }

    if (messagesEndRef.current) {
      // Pequeno timeout para garantir renderização completa
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior, block: 'end', inline: 'nearest' });
        // Ajuste extra: força um pequeno scroll adicional
        if (messagesEndRef.current?.parentElement) {
          messagesEndRef.current.parentElement.scrollTop = messagesEndRef.current.parentElement.scrollHeight;
        }
      }, 30);
    }
  }, []);

  const getInitials = (name: string | null | undefined = '') => {
    return (name ?? '')
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || 'C';
  };

  // Memoizar getFileUrl para evitar chamadas repetidas
  const getFileUrlMemo = useMemo(() => (path?: string) => {
    // console.log('[getFileUrl] Input:', path);
    if (!path) {
      // console.log('[getFileUrl] Path vazio, retornando string vazia');
      return '';
    }
    if (/^https?:\/\//.test(path)) {
      // console.log('[getFileUrl] Já é URL completa, retornando:', path);
      return path;
    }
    let API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001/api').replace(/\/api$/, '');
    if (!path.startsWith('/')) path = '/' + path;
    // Adicionar /uploads se não for URL absoluta e não começar com /uploads
    if (!path.startsWith('/uploads')) path = '/uploads' + path;
    const result = `${API_URL}${path}`;
    // console.log('[getFileUrl] Result:', result);
    return result;
  }, []);

  // Função auxiliar para normalizar status de mensagem do backend
  const normalizeMessageStatus = (msg: any): Mensagem => {
    // Mapear statusEntrega do backend para status do frontend
    let frontendStatus: Mensagem['status'] = 'desconhecido';
    const backendStatus = msg.statusEntrega || msg.status;

    if (backendStatus === 'falhou') {
      frontendStatus = 'erro';
    } else if (backendStatus === 'enviada' || backendStatus === 'entregue' || backendStatus === 'lida') {
      frontendStatus = backendStatus as 'enviada' | 'entregue' | 'lida';
    } else if (msg.status === 'enviando') {
      frontendStatus = 'enviando';
    } else if (msg.status === 'erro') {
      frontendStatus = 'erro';
    }

    return {
      ...msg,
      status: frontendStatus,
      statusEntrega: backendStatus || null
    };
  };

  // --- Marcar mensagens do cliente como lidas ---
  const marcarMensagensComoLidas = useCallback(async () => {
    if (!chatResumo.id) return;
    try {
      await api.post(`/mensagens/marcar-lidas`, {
        conversaId: chatResumo.id
      });
      // Opcional: atualizar localmente o status das mensagens para 'lida'
      setMensagens((prev) =>
        prev.map((msg) =>
          msg.remetente === 'cliente' && msg.status !== 'lida' && msg.statusEntrega !== 'lida'
            ? { ...msg, status: 'lida', statusEntrega: 'lida' }
            : msg
        )
      );
    } catch (err) {
      // Silencie erro, não bloqueia UX
    }
  }, [chatResumo.id, setMensagens]);

  // --- Carregamento Inicial e paginação ---
  const fetchConversaEMensagens = useCallback(
    async (page = 1, append = false) => {
      if (!chatResumo.id) {
        setError(t('atendimento.conversationIdMissing'));
        setLoading(false);
        return;
      }
      if (page === 1) setLoading(true);
      setError(null);
      try {
        // Buscar conversa e mensagens do backend (ajuste para buscar conversa completa)
        const [conversaResponse, mensagensResponse] = await Promise.all([
          api.get(`/chats/${chatResumo.id}`),
          api.get(`/chats/${chatResumo.id}/messages?page=${page}`)
        ]);
        setConversa(conversaResponse.data);
        // Normalizar mensagens do backend para incluir status corretamente
        const normalizedMensagens = mensagensResponse.data.map((msg: any) => normalizeMessageStatus(msg));
        const sortedMensagens = normalizedMensagens.sort(
          (a: Mensagem, b: Mensagem) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        setHasMore(sortedMensagens.length > 0 && sortedMensagens.length >= 100);

        if (append && page > 1) {
          // Normalizar mensagens ao adicionar mais páginas
          const normalizedPrev = sortedMensagens.map((msg: any) => normalizeMessageStatus(msg));
          setMensagens((prev) => [...normalizedPrev, ...prev]);

          const restore = scrollRestoreRef.current;
          if (restore) {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                const viewport = scrollViewportRef.current;
                if (!viewport) return;
                const newScrollHeight = viewport.scrollHeight;
                const delta = newScrollHeight - restore.scrollHeight;
                viewport.scrollTop = restore.scrollTop + delta;
                scrollRestoreRef.current = null;
              });
            });
          }
        } else {
          setMensagens(sortedMensagens);
          // Se há uma mensagem alvo vinda da busca, não sobrepõe a navegação
          if (!localStorage.getItem('targetMessageId')) {
            setTimeout(() => scrollToBottom('auto'), 1000);
          }
          // Marcar como lidas ao entrar na conversa
          marcarMensagensComoLidas();
        }

      } catch (err: any) {
        console.error("Erro ao carregar conversa/mensagens:", err);
        const errorMsg = err.response?.data?.message || t('atendimento.toastConversationLoadError');
        setError(errorMsg);
        toast({ title: t('common.error'), description: errorMsg, variant: "destructive" });
      } finally {
        setLoading(false);
        setIsFetchingMore(false);
        fetchMoreLockRef.current = false;
      }
    },
    [chatResumo.id, chatResumo.remoteJid, marcarMensagensComoLidas, scrollToBottom]
  );

  // Carregar primeira página ao abrir conversa
  useEffect(() => {
    setPagina(1);
    fetchConversaEMensagens(1, false);
  }, [chatResumo.id]);

  // --- Scroll infinito para cima (carregar mais mensagens antigas) ---
  const handleScroll = useCallback(() => {
    const el = scrollViewportRef.current;
    if (!el) return;

    const distanceToBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
    const shouldShow = distanceToBottom > 300;
    setShowScrollToBottom((prev) => (prev !== shouldShow ? shouldShow : prev));

    if (el.scrollTop < 200 && hasMore && !isFetchingMore && !loading && !fetchMoreLockRef.current) {
      fetchMoreLockRef.current = true;
      scrollRestoreRef.current = { scrollTop: el.scrollTop, scrollHeight: el.scrollHeight };
      setIsFetchingMore(true);
      const nextPage = pagina + 1;
      setPagina(nextPage);
      fetchConversaEMensagens(nextPage, true);
    }
  }, [hasMore, isFetchingMore, loading, pagina, fetchConversaEMensagens]);

  useEffect(() => {
    // Scroll para a mensagemId se vier na URL e as mensagens estiverem carregadas
    const searchParams = new URLSearchParams(location.search);
    const mensagemId = searchParams.get('mensagemId');

    if (mensagemId && mensagens.length > 0) {
      const messageElement = document.getElementById(`msg-${mensagemId}`);
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Highlight temporary
        messageElement.classList.add('bg-blue-100', 'dark:bg-blue-900', 'transition-colors', 'duration-1000');
        setTimeout(() => {
          messageElement.classList.remove('bg-blue-100', 'dark:bg-blue-900');
        }, 3000);
      }
    }
  }, [location.search, mensagens.length]);

  useEffect(() => {
    const root = scrollAreaRef.current;
    if (!root) return;
    const viewport = root.querySelector('[data-radix-scroll-area-viewport]') as HTMLDivElement | null;
    if (!viewport) return;

    scrollViewportRef.current = viewport;
    const onScroll = () => handleScroll();
    viewport.addEventListener('scroll', onScroll, { passive: true });

    // Calcula estado inicial do botão ao entrar/trocar de chat
    requestAnimationFrame(() => handleScroll());

    return () => {
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [handleScroll, chatResumo.id]);

  // Active date tracking via IntersectionObserver
  useEffect(() => {
    const viewport = scrollViewportRef.current;
    if (!viewport || mensagens.length === 0) return;

    const sentinels = viewport.querySelectorAll<HTMLElement>('[data-date-sentinel]');
    if (sentinels.length === 0) return;

    // Set initial active date
    const vpRect = viewport.getBoundingClientRect();
    let found: string | null = null;
    for (const sentinel of sentinels) {
      const rect = sentinel.getBoundingClientRect();
      if (rect.top >= vpRect.top - 1) {
        found = sentinel.getAttribute('data-date-sentinel');
        break;
      }
    }
    if (!found && sentinels.length > 0) {
      found = sentinels[sentinels.length - 1].getAttribute('data-date-sentinel');
    }
    if (found && found !== activeDate) setActiveDate(found);

    const observer = new IntersectionObserver(
      (entries) => {
        const intersecting = entries.filter(e => e.isIntersecting);
        if (intersecting.length === 0) return;

        let leader = intersecting[0];
        for (let i = 1; i < intersecting.length; i++) {
          if (intersecting[i].boundingClientRect.top < leader.boundingClientRect.top) {
            leader = intersecting[i];
          }
        }
        const dateStr = leader.target.getAttribute('data-date-sentinel');
        if (dateStr) setActiveDate(dateStr);
      },
      {
        root: viewport,
        rootMargin: '-1px 0px 0px 0px',
        threshold: 0,
      }
    );

    sentinels.forEach(el => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensagens]);

  // --- Efeitos ---

  // --- Navegação até mensagem alvo (vinda da busca do Dashboard) ---
  useEffect(() => {
    const targetMessageId = localStorage.getItem('targetMessageId');
    const targetApiMessageId = localStorage.getItem('targetApiMessageId');
    const targetEvolutionMessageId = localStorage.getItem('targetEvolutionMessageId');

    if (targetMessageId && mensagens.length > 0) {
      let tentativas = 0;
      const maxTentativas = 10; // Limite para evitar loop infinito

      // Função para encontrar e rolar até a mensagem
      const scrollToTargetMessage = () => {
        const messageElements = document.querySelectorAll('[data-message-id]');
        const targetElement = Array.from(messageElements).find(el =>
          el.getAttribute('data-message-id') === targetMessageId ||
          el.getAttribute('data-api-message-id') === targetApiMessageId ||
          el.getAttribute('data-evolution-message-id') === targetEvolutionMessageId
        );

        if (targetElement) {
          targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Destaque visual temporário
          targetElement.classList.add('ring-2', 'ring-green-500', 'ring-offset-2');
          setTimeout(() => {
            targetElement.classList.remove('ring-2', 'ring-green-500', 'ring-offset-2');
          }, 2000);

          // Limpa o localStorage após encontrar
          localStorage.removeItem('targetMessageId');
          localStorage.removeItem('targetApiMessageId');
          localStorage.removeItem('targetEvolutionMessageId');
        } else if (hasMore && tentativas < maxTentativas) {
          // Se não encontrou e ainda há mais páginas, rola para cima para carregar mais
          tentativas++;
          console.log(`[Navegação] Mensagem não encontrada, tentativa ${tentativas}/${maxTentativas}. Rolando para cima para carregar mais mensagens...`);

          const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
          if (scrollArea) {
            // Rola para o topo para disparar o carregamento de mensagens anteriores
            scrollArea.scrollTop = 0;

            // Tenta novamente após um delay para as mensagens carregarem
            setTimeout(scrollToTargetMessage, 1500);
          }
        } else {
          // Se não encontrou após todas as tentativas, limpa o localStorage
          console.log(`[Navegação] Mensagem não encontrada após ${maxTentativas} tentativas. Limpando localStorage.`);
          localStorage.removeItem('targetMessageId');
          localStorage.removeItem('targetApiMessageId');
          localStorage.removeItem('targetEvolutionMessageId');
        }
      };

      // Tenta imediatamente
      scrollToTargetMessage();
    }
  }, [mensagens.length, hasMore]); // Executa quando as mensagens forem carregadas ou quando hasMore mudar

  // --- Configuração do Socket.IO ---
  useEffect(() => {
    if (!chatResumo.id) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error("Token não encontrado, não é possível conectar ao socket.");
      toast({ title: t('atendimento.toastAuthRequiredTitle'), description: t('atendimento.toastAuthRequired'), variant: "destructive" });
      navigate('/login');
      return;
    }

    const setupSocketListeners = async () => {
      try {
        const socket = await socketService.connect(token);

        // Junta-se a uma sala específica da empresa para receber atualizações globais
        if (user?.empresaId) {
          socketService.joinRoom(`empresa_${user.empresaId}`);
        }
        // Junta-se também a uma sala específica da conversa para atualizações diretas
        socketService.joinConversa(chatResumo.id);

        const processarNovaMensagem = (mensagem: any) => {
          if (mensagem.ticketId && !mensagem.conversaId) mensagem.conversaId = mensagem.ticketId;

          if (mensagem.conversaId === chatResumo.id) {
            setMensagens((prev) => {
              // Se for uma reação, atualizamos a mensagem original
              if (mensagem.tipo === 'reaction' && mensagem.mensagemRespondidaId) {
                return prev.map(msg => {
                  // mensagem.mensagemRespondidaId vem como apiMessageId (FK), então devemos casar por apiMessageId/evolutionMessageId/idExterno
                  const isTarget =
                    msg.apiMessageId === mensagem.mensagemRespondidaId ||
                    msg.evolutionMessageId === mensagem.mensagemRespondidaId ||
                    msg.idExterno === mensagem.mensagemRespondidaId;

                  if (isTarget) {
                    const reactions = Array.isArray(msg.reactions) ? [...msg.reactions] : [];
                    // Verifica se já existe uma reação igual
                    const reactionExists = reactions.some(r =>
                      r.conteudo === renderEmojiWithFallback(mensagem.conteudo) &&
                      r.remetenteTipo === mensagem.remetenteTipo
                    );

                    if (!reactionExists) {
                      return {
                        ...msg,
                        reactions: [...reactions, {
                          conteudo: renderEmojiWithFallback(mensagem.conteudo),
                          remetenteTipo: mensagem.remetenteTipo,
                          timestamp: mensagem.timestamp
                        }]
                      };
                    }
                  }
                  return msg;
                });
              }

              // Para mensagens normais, apenas adiciona se não existir
              // evolutionMessageId é nulo nas mensagens Meta: só comparar quando existir,
              // senão null === null marca toda mensagem nova como duplicada.
              if (prev.some(msg =>
                msg.id === mensagem.id ||
                (!!mensagem.evolutionMessageId && msg.evolutionMessageId === mensagem.evolutionMessageId)
              )) {
                return prev;
              }
              // Normalizar mensagem antes de adicionar
              return [...prev, normalizeMessageStatus(mensagem)];
            });

            scrollToBottom();

            // Marcar como lida se a mensagem recebida for do cliente
            if (mensagem.remetente === 'cliente' || mensagem.remetenteTipo === 'cliente') {
              marcarMensagensComoLidas();
            }
          }
        };

        // Unificado para ouvir o evento correto do backend
        const handleNewMessage = (payload: any) => {
          const novaMsg = payload.mensagem || payload;
          processarNovaMensagem(novaMsg);
        };

        const handleNovaMensagemGlobal = (payload: any) => {
          // O backend Meta emite { mensagem, ticket, contato }; os demais, a mensagem direta.
          processarNovaMensagem(payload?.mensagem || payload);
        };

        const handleMensagemAtualizada = (mensagemAtualizada: any) => {
          setMensagens((prev) => {
            // Se for uma reação, atualiza o array de reactions da mensagem alvo
            if (mensagemAtualizada.tipo === 'reaction' && mensagemAtualizada.mensagemRespondidaId) {
              return prev.map((msg) => {
                // Encontra a mensagem alvo da reação
                if (msg.apiMessageId === mensagemAtualizada.mensagemRespondidaId ||
                  msg.evolutionMessageId === mensagemAtualizada.mensagemRespondidaId ||
                  msg.idExterno === mensagemAtualizada.mensagemRespondidaId) {
                  // Busca reactions existentes
                  const existingReactions = msg.reactions || [];

                  // Remove reação antiga do mesmo usuário se existir
                  const filteredReactions = existingReactions.filter((r: any) =>
                    r.remetente !== mensagemAtualizada.remetente
                  );

                  // Adiciona nova reação se não for remoção
                  const newReactions = mensagemAtualizada.deletedAt
                    ? filteredReactions
                    : [...filteredReactions, {
                      emoji: renderEmojiWithFallback(mensagemAtualizada.conteudo),
                      remetente: mensagemAtualizada.remetente,
                      id: mensagemAtualizada.id
                    }];

                  return {
                    ...msg,
                    reactions: newReactions
                  };
                }

                // Atualiza a própria mensagem de reação se ela estiver na lista
                if (msg.id === mensagemAtualizada.id) {
                  return { ...msg, ...mensagemAtualizada };
                }

                return msg;
              });
            }

            // Para mensagens normais, atualiza diretamente
            return prev.map((msg) => {
              if (msg.id === mensagemAtualizada.id || msg.evolutionMessageId === mensagemAtualizada.evolutionMessageId) {
                // Se a mensagem foi marcada como deletada
                if (mensagemAtualizada.deletedAt) {
                  return {
                    ...msg,
                    conteudo: mensagemAtualizada.conteudo,
                    midiaUrl: null,
                    caption: null,
                    deletedAt: mensagemAtualizada.deletedAt,
                    updatedAt: mensagemAtualizada.updatedAt
                  };
                }
                // Se for uma atualização normal
                return { ...msg, ...mensagemAtualizada };
              }
              return msg;
            });
          });
        };

        const handleStatusMensagem = (atualizacao: { id?: string; apiMessageId?: string; status?: string; statusEntrega?: string }) => {
          console.log('[SOCKET] Recebido status_mensagem:', atualizacao);
          setMensagens((prev: Mensagem[]) =>
            prev.map((msg: Mensagem) => {
              // Buscar mensagem por id interno ou apiMessageId
              const matchesId = atualizacao.id && (msg.id === atualizacao.id);
              const matchesApiMessageId = atualizacao.apiMessageId &&
                (msg.apiMessageId === atualizacao.apiMessageId ||
                  msg.evolutionMessageId === atualizacao.apiMessageId ||
                  msg.idExterno === atualizacao.apiMessageId);

              if (matchesId || matchesApiMessageId) {
                console.log(`[SOCKET] Atualizando status da mensagem ${msg.id} para ${atualizacao.statusEntrega || atualizacao.status}`);
                // Mapear status do backend para o formato do frontend
                const backendStatus = (atualizacao.statusEntrega || atualizacao.status) as Mensagem['statusEntrega'];
                let frontendStatus: Mensagem['status'] = 'desconhecido';

                if (backendStatus === 'falhou') {
                  frontendStatus = 'erro';
                } else if (backendStatus === 'enviada' || backendStatus === 'entregue' || backendStatus === 'lida' || backendStatus === 'erro') {
                  frontendStatus = backendStatus;
                } else if (backendStatus === 'enviando') {
                  frontendStatus = 'enviando';
                }

                return {
                  ...msg,
                  status: frontendStatus,
                  statusEntrega: backendStatus || null
                } as Mensagem;
              }
              return msg;
            })
          );
        };

        const handleMensagemDeletada = (data: { id?: string; apiMessageId?: string; ticketId: string; deletedAt: string }) => {
          setMensagens((prev: Mensagem[]) =>
            prev.map((msg: Mensagem) => {
              // Verificar se esta mensagem foi deletada
              const matchesId = data.id && (msg.id === data.id);
              const matchesApiMessageId = data.apiMessageId &&
                (msg.apiMessageId === data.apiMessageId ||
                  msg.evolutionMessageId === data.apiMessageId ||
                  msg.idExterno === data.apiMessageId);

              if (matchesId || matchesApiMessageId) {
                // Atualizar estado da mensagem para deletada em vez de remover
                return {
                  ...msg,
                  deletedAt: data.deletedAt,
                  status: 'erro' // Marcar como erro para indicar que foi deletada
                } as Mensagem;
              }
              return msg;
            })
          );
        };

        const handleMensagemEditada = (data: any) => {
          setMensagens((prev: Mensagem[]) =>
            prev.map((msg: Mensagem) => {
              // Buscar mensagem por id interno ou apiMessageId
              const matchesId = data.id && (msg.id === data.id);
              const matchesApiMessageId = data.apiMessageId &&
                (msg.apiMessageId === data.apiMessageId ||
                  msg.evolutionMessageId === data.apiMessageId ||
                  msg.idExterno === data.apiMessageId);

              if (matchesId || matchesApiMessageId) {
                // Atualizar conteúdo, timestamp e campos de edição
                return {
                  ...msg,
                  conteudo: data.conteudo || msg.conteudo,
                  conteudoOriginal: data.conteudoOriginal || msg.conteudoOriginal,
                  dataEdicao: data.dataEdicao || msg.dataEdicao,
                  updatedAt: data.updatedAt || new Date().toISOString()
                } as Mensagem;
              }
              return msg;
            })
          );
        };

        const handleConversaAtualizada = (conversaAtualizada: any) => {
          if (conversaAtualizada.id === chatResumo.id) {
            setConversa(prev => prev ? { ...prev, ...conversaAtualizada } : conversaAtualizada);
            setMensagens((prev) => [...prev]);
          }
        };

        const handleContatoAtualizado = (data: any) => {
          const contato = data?.contato;
          if (!contato) return;
          setConversa(prev => prev && prev.contato?.id === contato.id
            ? { ...prev, contato: { ...prev.contato, ...contato } }
            : prev);
          setChatResumo(prev => prev && (prev.contato?.id === contato.id || prev.contatoId === contato.id)
            ? { ...prev, contato: { ...prev.contato, ...contato } }
            : prev);
        };

        const handleConversaRemovida = (data: { id: string; motivo: string }) => {
          if (data.id === chatResumo.id) {
            toast({ title: t('atendimento.toastTicketTransferedToAgent'), description: t('atendimento.toastTicketTransferedToAgentDesc') });
            setChatResumo(null);
          }
        };

        socketService.on('newMessage', handleNewMessage);
        socketService.on('nova_mensagem', handleNovaMensagemGlobal);
        socketService.on('mensagem_atualizada', handleMensagemAtualizada);
        socketService.on('status_mensagem', handleStatusMensagem);
        socketService.on('mensagem_deletada', handleMensagemDeletada);
        socketService.on('mensagem_editada', handleMensagemEditada);
        socketService.on('conversa_atualizada', handleConversaAtualizada);
        socketService.on('conversa_removida', handleConversaRemovida);
        socketService.on('contato_atualizado', handleContatoAtualizado);

        const handleMensagemPinada = (payload: any) => {
          if (payload.ticketId === chatResumo.id) {
            setPinnedMessage(payload.fixada ? payload : null);
            // Atualiza também na lista de mensagens se ela estiver lá
            setMensagens((prev: Mensagem[]) =>
              prev.map((msg: Mensagem) =>
                msg.id === payload.mensagemId
                  ? { ...msg, fixada: payload.fixada, pinnedAt: payload.pinnedAt }
                  : { ...msg, fixada: false, pinnedAt: null } // desfixa as outras
              )
            );
          }
        };
        socketService.on('mensagem_pinada', handleMensagemPinada);

        const handleAgendamentosAtualizados = (data: { ticketId: string }) => {
          if (data?.ticketId === chatResumo.id) {
            loadScheduledMessages();
          }
        };
        socketService.on('agendamentos_atualizados', handleAgendamentosAtualizados);

        socketService.on('ticket_compartilhado', () => {
          if (chatResumo?.id) {
            api.get(`/chats/${chatResumo.id}`).then(resp => {
              if (resp.data) setConversa(resp.data);
            }).catch(() => {});
          }
        });
        socketService.on('ticket_compartilhamento_removido', () => {
          setConversa(null);
          navigate('/');
        });

        // Reconnect handler: re-join rooms and re-fetch data
        const handleReconnect = () => {
          if (user?.empresaId) {
            socketService.joinRoom(`empresa_${user.empresaId}`);
          }
          socketService.joinConversa(chatResumo.id);
          fetchConversaEMensagens();
        };
        socketService.onReconnect(handleReconnect);

        socketCallbacksRef.current = {
          handleNewMessage,
          handleNovaMensagemGlobal,
          handleMensagemAtualizada,
          handleStatusMensagem,
          handleMensagemDeletada,
          handleMensagemEditada,
          handleConversaAtualizada,
          handleContatoAtualizado,
          handleMensagemPinada,
          handleConversaRemovida,
          handleAgendamentosAtualizados,
          handleReconnect
        };

      } catch (error) {
        console.error('Error setting up socket listeners:', error);
      }
    };

    setupSocketListeners();

    return () => {
      // Clean up listeners
      if (socketCallbacksRef.current) {
        socketService.off('newMessage', socketCallbacksRef.current.handleNewMessage);
        socketService.off('nova_mensagem', socketCallbacksRef.current.handleNovaMensagemGlobal);
        socketService.off('mensagem_atualizada', socketCallbacksRef.current.handleMensagemAtualizada);
        socketService.off('status_mensagem', socketCallbacksRef.current.handleStatusMensagem);
        socketService.off('mensagem_deletada', socketCallbacksRef.current.handleMensagemDeletada);
        socketService.off('mensagem_editada', socketCallbacksRef.current.handleMensagemEditada);
        socketService.off('conversa_atualizada', socketCallbacksRef.current.handleConversaAtualizada);
        socketService.off('contato_atualizado', socketCallbacksRef.current.handleContatoAtualizado);
        socketService.off('mensagem_pinada', socketCallbacksRef.current.handleMensagemPinada);
        socketService.off('conversa_removida', socketCallbacksRef.current.handleConversaRemovida);
        socketService.off('agendamentos_atualizados', socketCallbacksRef.current.handleAgendamentosAtualizados);
        socketService.off('ticket_compartilhado');
        socketService.off('ticket_compartilhamento_removido');
        socketService.offReconnect(socketCallbacksRef.current.handleReconnect);
      }
      socketService.leaveConversa(chatResumo.id);
    };
  }, [chatResumo.id, navigate]); // Removido scrollToBottom e marcarMensagensComoLidas das dependências para evitar reconexões infinitas das dependências

  useEffect(() => {
    if (showAudioRecorder && param === true) {
      startAudioRecording();
    }
  }, [showAudioRecorder, param]);

  // Substitua a definição do TextFilePreview por um componente React normal (não async)
  // const TextFilePreview: React.FC<{ file: File; url?: string }> = ({ file, url }) => {
  //   const [text, setText] = React.useState<string>('Carregando...');
  //   React.useEffect(() => {
  //     // Se não for um File real, usa a URL
  //     if (!(file instanceof File) && url) {
  //       fetch(url)
  //         .then(response => response.text())
  //         .then(text => {
  //           setText(text);
  //         })
  //         .catch(err => {
  //           console.error('Erro ao carregar arquivo de texto:', err);
  //           setText('Erro ao carregar arquivo');
  //         });
  //     } else if (file instanceof File) {
  //       const reader = new FileReader();
  //       reader.onload = e => {
  //         setText(e.target?.result as string || '');
  //       };
  //       reader.onerror = e => {
  //         setText('Erro ao carregar arquivo');
  //       };
  //       reader.readAsText(file);
  //     }
  //   }, [file, url]);
  //   return (
  //     <div className="bg-gray-100 dark:bg-slate-900 rounded-md p-4 w-full max-w-lg h-80 overflow-auto text-xs text-gray-800 dark:text-gray-100">
  //       <pre>{text}</pre>
  //     </div>
  //   );
  // }

  const handleReactResponse = async (icon: any) => {
    const targetMessage = reactingTo || (selectedMessage ? mensagens.find(m => m.id === selectedMessage) : null);
    if (!targetMessage) {
      toast({ title: t('common.error'), description: t('atendimento.toastReactNoMessage'), variant: "destructive" });
      return;
    }
    handleSendMessage(undefined, { type: 'reaction', reaction: icon }, targetMessage.id);
  };

  // --- Envio de Template Meta ---
  const handleSendMetaTemplate = async (templateName: string, languageCode: string, components: any[], previewText?: string) => {
    if (chatResumo.statusTicket === 'fechado' || chatResumo.statusTicket === 'pendente') {
      toast({
        title: t('atendimento.toastActionNotAllowedTitle'),
        description: t('atendimento.toastActionNotAllowedClosed'),
        variant: "destructive"
      });
      return;
    }

    if (!chatResumo.id || !chatResumo.remoteJid) return;

    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const empresaId = userInfo.empresaId;

    if (!empresaId) {
      toast({ title: t('common.error'), description: t('atendimento.toastCompanyIdMissing'), variant: "destructive" });
      return;
    }

    try {
      setEnviando(true);
      scrollToBottom();

      await api.post('/mensagens/send-message', {
        remoteJid: chatResumo.remoteJid,
        type: 'template',
        templateName,
        languageCode,
        text: previewText,
        conversaId: chatResumo.id,
        instanciaId: selectedInstance?.id || (chatResumo as any).instancia?.id || (chatResumo as any).instanciaUazapi?.id || (chatResumo as any).instanciaMeta?.id || (chatResumo as any).instanciaMetaId || (chatResumo as any).instanciaId,
        components,
      });

      toast({ title: t('atendimento.toastTemplateSentTitle'), description: t('atendimento.toastTemplateSent', { templateName }) });
      setShowMetaTemplateDialog(false);

      // Hybrid: auto-switch to linked UAZAPI instance after template send
      if (selectedInstance?.tipoApi === 'META' && selectedInstance?.instanciaUazapiId) {
        const linkedUazapi = instances.find(i => i.id === selectedInstance.instanciaUazapiId && i.tipoApi === 'UAZAPI');
        if (linkedUazapi) {
          setSelectedInstance(linkedUazapi);
          toast({ title: t('atendimento.toastInstanciaAlteradaUazapi'), description: t('atendimento.toastInstanciaAlteradaUazapiDesc', { nome: linkedUazapi.nome }) });
        }
      }
    } catch (err: any) {
      console.error("Erro ao enviar template:", err);
      toast({
        title: t('atendimento.toastTemplateSendErrorTitle'),
        description: err.response?.data?.message || t('atendimento.toastTemplateSendError'),
        variant: "destructive"
      });
    } finally {
      setEnviando(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  // --- Envio de Mensagem ---
  const handleSendMessage = async (
    e?: React.FormEvent,
    fileData?: { type: Mensagem['tipo'], url?: string, fileName?: string, mimeType?: string, fileBlob?: Blob, reaction?: any },
    replyToMessageId?: string
  ) => {
    // Check if ticket is closed
    if (chatResumo.statusTicket === 'fechado' || chatResumo.statusTicket === 'pendente') {
      toast({
        title: t('atendimento.toastActionNotAllowedTitle'),
        description: t('atendimento.toastSendNotAllowed'),
        variant: "destructive"
      });
      return;
    }

    const messageToReplyTo = replyingTo || reactingTo || (replyToMessageId ? mensagens.find(m => m.id === replyToMessageId) : null);

    if (e) e.preventDefault();
    let trimmedMessage = '';

    // Apply signature if configured and enabled
    if (sendSignature) {
      if (chatConfig?.assinaturaAtendente && chatConfig?.textoAssinatura) {
        let signature = chatConfig.textoAssinatura
          .replace('{nome_atendente}', user.nome)
          .replace('{empresa}', user.empresa?.nome || '');

        // Add support for {saudacao} variable
        if (signature.includes('{saudacao}')) {
          const hora = new Date().getHours();
          let saudacao;
          if (hora >= 6 && hora < 12) {
            saudacao = 'Bom dia';
          } else if (hora >= 12 && hora < 18) {
            saudacao = 'Boa tarde';
          } else {
            saudacao = 'Boa noite';
          }
          signature = signature.replace(/{saudacao}/g, saudacao);
        }

        // Add support for {nome_contato} variable
        if (signature.includes('{nome_contato}') && chatResumo?.contato?.nome) {
          signature = signature.replace(/{nome_contato}/g, chatResumo.contato.nome);
        }

        // Clean unset variables
        signature = signature.replace(/{nome_contato}/g, '').replace(/{saudacao}/g, '');

        trimmedMessage = '*' + signature + '*:\n';
      } else {
        // Fallback: ativado manualmente, usar apenas nome_atendente
        trimmedMessage = '*' + user.nome + '*:\n';
      }
    }

    trimmedMessage += novaMensagem.trim();

    if (!chatResumo.id || !chatResumo.remoteJid) return;

    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const empresaId = userInfo.empresaId;

    if (!empresaId) {
      toast({ title: t('common.error'), description: t('atendimento.toastCompanyIdMissing'), variant: "destructive" });
      return;
    }

    let type: Mensagem['tipo'] = fileData?.type || 'texto';
    if (type === 'reaction') {
      trimmedMessage = fileData?.reaction;
    }
    let conteudo = trimmedMessage;
    let url = undefined, fileName = undefined, mimeType = undefined, fileBlob = undefined;

    if (fileData && type !== 'reaction') {
      url = fileData.url;
      fileName = fileData.fileName;
      mimeType = fileData.mimeType;
      fileBlob = fileData.fileBlob;
      conteudo = fileName || '[Arquivo]';
    }

    if (type === 'texto' && !trimmedMessage) return;

    // Clear reply state after sending
    if (replyingTo) {
      setReplyingTo(null);
    }

    if (reactingTo) {
      setReactingTo(null);
    }

    if (editingMessage) {
      setEditingMessage(null);
    }

    try {
      const repliedMessageId =
        messageToReplyTo?.apiMessageId ||
        messageToReplyTo?.evolutionMessageId ||
        messageToReplyTo?.idExterno ||
        messageToReplyTo?.id;

      if (type === 'reaction' && !repliedMessageId) {
        toast({ title: t('common.error'), description: t('atendimento.toastReactMessageNotFound'), variant: "destructive" });
        return;
      }

      setNovaMensagem('');
      // Limpar rascunho ao enviar mensagem
      if (chatResumo?.id) {
        DraftManager.removeDraft(chatResumo.id);
      }
      setEnviando(true);
      scrollToBottom();

      const messageData: any = {
        id: editingMessage?.id || null,
        remoteJid: chatResumo.remoteJid,
        type,
        text: (type === 'texto' || type === 'reaction') ? trimmedMessage : undefined,
        url,
        fileName,
        mimeType,
        conversaId: chatResumo.id,
        instanciaId: selectedInstance?.id || (chatResumo as any).instancia?.id || (chatResumo as any).instanciaUazapi?.id || (chatResumo as any).instanciaMeta?.id || (chatResumo as any).instanciaMetaId || (chatResumo as any).instanciaId,
        mensagemRespondidaId: repliedMessageId ? String(repliedMessageId) : undefined,
        mentions: selectedMentions.length > 0 ? selectedMentions.map(m => m.id) : undefined
      };

      if (fileBlob) {
        const formData = new FormData();
        formData.append('file', fileBlob, fileName || 'audio.webm');
        Object.keys(messageData).forEach(key => {
          formData.append(key, messageData[key]);
        });

        await api.post('/mensagens/send-message', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else if (editingMessage) {
        await api.put(`/mensagens/edit-message`, messageData);
      } else {
        await api.post('/mensagens/send-message', messageData);
      }

    } catch (err: any) {
      console.error("Erro ao enviar mensagem:", err);
      const errorMsg = err.response?.data?.message || t('atendimento.toastSendMessageError');
      toast({ title: t('common.error'), description: errorMsg, variant: "destructive" });
      setEnviando(false);
    } finally {
      setSelectedMentions([]); // Limpar menções
      setEnviando(false);
      setTimeout(() => inputRef.current?.focus(), 100);
      setReactItems(false);
      setIsMenuOpen(false);
    }
  };

  // Carrega as mensagens agendadas (pendentes) do ticket atual, para exibir o indicador na conversa
  const loadScheduledMessages = useCallback(async () => {
    if (!chatResumo?.id) {
      setScheduledMessages([]);
      return;
    }
    try {
      const resp = await api.get('/agendamentos', {
        params: { ticketId: chatResumo.id, status: 'PENDENTE', limit: 50 }
      });
      setScheduledMessages(resp.data?.data?.agendamentos || []);
    } catch {
      setScheduledMessages([]);
    }
  }, [chatResumo?.id]);

  useEffect(() => {
    loadScheduledMessages();
  }, [loadScheduledMessages]);

  const handleCancelScheduledMessage = async (id: string) => {
    try {
      await api.delete(`/agendamentos/${id}`);
      setScheduledMessages(prev => prev.filter(a => a.id !== id));
      toast({ title: "Agendamento cancelado" });
    } catch (err: any) {
      toast({ title: "Erro", description: err.response?.data?.message || 'Erro ao cancelar agendamento.', variant: "destructive" });
    }
  };

  // Abre o diálogo de agendamento, pré-preenchendo com o texto já digitado
  const handleOpenScheduleDialog = () => {
    setScheduleMessage(novaMensagem);
    setScheduleDateTime('');
    setScheduleFile(null);
    setShowScheduleDialog(true);
  };

  const handleScheduleMessage = async () => {
    if (!chatResumo?.id || !chatResumo?.contato?.id) return;

    if (!scheduleDateTime) {
      toast({ title: t('common.warning'), description: t('atendimento.toastScheduleNoDateTime'), variant: "destructive" });
      return;
    }

    const dataLocal = new Date(scheduleDateTime + ':00');
    if (isNaN(dataLocal.getTime()) || dataLocal <= new Date()) {
      toast({ title: t('common.warning'), description: t('atendimento.toastSchedulePastDate'), variant: "destructive" });
      return;
    }

    if (!scheduleMessage.trim() && !scheduleFile) {
      toast({ title: t('common.warning'), description: t('atendimento.toastScheduleEmpty'), variant: "destructive" });
      return;
    }

    setSchedulingMessage(true);
    try {
      const dataUtc = dataLocal.toISOString();

      if (scheduleFile) {
        const formData = new FormData();
        formData.append('file', scheduleFile);
        formData.append('contatoId', chatResumo.contato.id);
        formData.append('ticketId', chatResumo.id);
        formData.append('dataAgendamento', dataUtc);
        if (scheduleMessage.trim()) formData.append('conteudo', scheduleMessage.trim());
        await api.post('/agendamentos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await api.post('/agendamentos', {
          contatoId: chatResumo.contato.id,
          ticketId: chatResumo.id,
          tipo: 'texto',
          conteudo: scheduleMessage.trim(),
          dataAgendamento: dataUtc
        });
      }

      toast({
        title: t('atendimento.toastScheduleCreatedTitle'),
        description: t('atendimento.toastScheduleCreated', { datetime: dataLocal.toLocaleString(i18n.language.startsWith('pt') ? 'pt-BR' : 'en-US') })
      });

      setShowScheduleDialog(false);
      setNovaMensagem('');
      if (chatResumo?.id) DraftManager.removeDraft(chatResumo.id);
      setScheduleMessage('');
      setScheduleFile(null);
      setScheduleDateTime('');
      loadScheduledMessages();
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || t('atendimento.toastScheduleError');
      toast({ title: t('common.error'), description: errorMsg, variant: "destructive" });
    } finally {
      setSchedulingMessage(false);
    }
  };

  // --- Ações da Conversa ---
  const handleCloseConversa = async () => {
    if (!chatResumo.id) return;
    try {
      await api.put(`/chats/${chatResumo.id}/status?status=fechado`).then(() => {
        toast({
          title: t('atendimento.toastAttendanceClosed'),
          variant: 'default',
        });
        // Atualizar status local do chatResumo imediatamente
        setChatResumo(prev => prev ? {
          ...prev,
          statusTicket: 'fechado',
          status: 'fechado'
        } : prev);
        // Atualizar conversa local também
        setConversa(prev => prev ? {
          ...prev,
          status: 'fechado'
        } : prev);
        // Notificar o Dashboard sobre a mudança
        if (refresh && typeof refresh === 'function') {
          refresh({
            ...chatResumo,
            statusTicket: 'fechado',
            status: 'fechado'
          });
        }
      });
      // Fechar conversa automaticamente após finalizar
      setTimeout(() => {
        setChatResumo(null);
      }, 1000); // Pequeno delay para permitir atualização do Dashboard
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || t('atendimento.toastCloseTicketError');
      toast({ title: t('common.error'), description: errorMsg, variant: "destructive" });
    }
  };

  const handleTransferConversa = () => {
    setTransferToUser('');
    setTransferToQueue('');
    loadUsersAndQueues();
    setShowTransferModal(true);
  };

  const loadShareUsers = useCallback(async () => {
    if (!user?.empresaId) return;
    setLoadingShare(true);
    try {
      const resp = await api.get('/usuarios/transferencia?pageSize=100');
      const users = Array.isArray(resp.data.usuarios) ? resp.data.usuarios : [];
      setShareUsers(users.filter((u: any) => u.id !== user.id));
    } catch (err) {
      console.error('Erro ao carregar usuários para compartilhar:', err);
      setShareUsers([]);
    } finally {
      setLoadingShare(false);
    }
  }, [user?.empresaId, user?.id]);

  const handleShareTicket = async () => {
    if (!selectedShareUsers.length || !chatResumo?.id) return;
    setLoadingShare(true);
    try {
      await api.post(`/tickets/compartilhar/${chatResumo.id}`, { usuarioIds: selectedShareUsers });
      toast({ title: t('common.success'), description: t('atendimento.toastTicketShared') });
      setShowShareModal(false);
      setSelectedShareUsers([]);
      refresh(chatResumo);
    } catch (err: any) {
      toast({ title: t('common.error'), description: err?.response?.data?.message || t('atendimento.toastTicketShareError'), variant: 'destructive' });
    } finally {
      setLoadingShare(false);
    }
  };

  const handleStartConversa = async () => {
    if (!chatResumo.id) return;
    setStartingAttendance(true);
    setLoading(true);
    try {
      await api.put(`/chats/${chatResumo.id}/status?status=aberto`).then((response) => {
        toast({
          title: t('atendimento.toastAttendanceStarted'),
          variant: 'default',
        });

        // Buscar dados atualizados do backend
        const updatedConversa = response.data;

        // Atualizar conversa local com dados do backend
        setConversa(updatedConversa);

        // Criar chatResumo atualizado com todos os dados necessários
        const updatedChatResumo = {
          ...chatResumo,
          statusTicket: updatedConversa.status || 'aberto',
          status: updatedConversa.status || 'aberto',
          dataAtualizacao: updatedConversa.dataAtualizacao ? new Date(updatedConversa.dataAtualizacao) : new Date(),
          atendenteId: updatedConversa.atendenteId || updatedConversa.usuarioId,
          atendente: updatedConversa.atendente || updatedConversa.usuario,
          ultimaMensagem: updatedConversa.ultimaMensagem ? {
            ...updatedConversa.ultimaMensagem,
            timestamp: new Date(updatedConversa.ultimaMensagem.timestamp)
          } : chatResumo.ultimaMensagem
        };

        // Atualiza o resumo local para que as ações (menu de mensagem, envio) enxerguem o novo status
        setChatResumo(updatedChatResumo as any);

        // Notificar o Dashboard sobre a mudança (o Dashboard vai atualizar o estado)
        if (refresh && typeof refresh === 'function') {
          console.log('Enviando atualização para Dashboard:', updatedChatResumo);
          refresh(updatedChatResumo);
        }
      });
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastStartAttendanceError'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setStartingAttendance(false);
    }
  };

  // Selecionar contatos
  const handleSelectContacts = async () => {
    setShowContactDialog(true);
    setSearchTabTerm('');
    setSearchTabLoading(true);
    try {
      const resp = await api.get('/chats/contacts');
      setContatos(resp.data);
    } catch {
      setContatos([]);
      toast({ title: t('common.error'), description: t('atendimento.toastContactsLoadError'), variant: "destructive" });
    } finally {
      setSearchTabLoading(false);
    }
    setShowAttach(false);
  };

  const handleSendContacts = async () => {
    if (contatosSelecionados.length === 0) return;
    try {
      await api.post('/chats/send-contacts', {
        conversaId: chatResumo.id,
        remoteJid: chatResumo.remoteJid,
        contatos: contatosSelecionados,
      });
      toast({ title: t('atendimento.toastContactsSent') });
      setShowContactDialog(false);
      setContatosSelecionados([]);
    } catch {
      toast({ title: t('common.error'), description: t('atendimento.toastContactsSendError'), variant: "destructive" });
    }
  };

  // Função para processar um arquivo recebido
  const processFile = (file: File) => {
    let type: 'imagem' | 'video' | 'documento' | 'sticker' = 'documento';
    if (file.type.startsWith('image/')) type = 'imagem';
    else if (file.type.startsWith('video/')) type = 'video';
    else if (file.type.startsWith('sticker/')) type = 'sticker';

    // Verifica o tamanho do arquivo (limite de 100MB)
    const maxSize = 100 * 1024 * 1024; // 100MB
    if (file.size > maxSize) {
      toast({ title: t('common.error'), description: t('atendimento.toastFileTooLarge'), variant: "destructive" });
      return null;
    }

    return {
      file,
      type,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size
    };
  };

  // Anexar arquivo
  const handleAttachClick = () => setShowAttach((v) => !v);

  const handleFileInput = (type: 'imagem' | 'video' | 'documento') => {
    if (fileInputRef.current) {
      fileInputRef.current.accept =
        type === 'imagem'
          ? 'image/*'
          : type === 'video'
            ? 'video/*'
            : type === 'documento'
              ? '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/pdf'
              : '*/*';
      fileInputRef.current.onchange = async (e: any) => {
        const files = Array.from(e.target.files || []) as File[];
        if (files.length === 0) return;
        handleFilesSelect(files, type);
      };
      fileInputRef.current?.click();
      setTimeout(() => fileInputRef.current?.focus(), 100);
    }
  };

  // Função para lidar com a seleção de arquivos (mantida para compatibilidade)
  const handleFileSelect = (file: File, type: 'imagem' | 'video' | 'documento' | 'sticker') => {
    const processedFile = processFile(file);
    if (processedFile) {
      setFileQueue([]);
      setFilePreview(processedFile);
      setEditMode('none');
      setImageZoom(1);
      setImagePan({ x: 0, y: 0 });
      setTextOverImage('');
      setShowFilePreview(true);
      setShowAttach(false);
      fileInputRef.current!.value = '';

      // Transferir texto do inputRef para o campo de legenda
      if (novaMensagem.trim()) {
        setFileDescription(novaMensagem.trim());
        setNovaMensagem(''); // Limpar o campo de digitação
      }

      setTimeout(() => fileInputRefText.current?.focus(), 100);
    }
  };

  // Seleciona vários arquivos de uma vez: exibe o primeiro para pré-visualização
  // e mantém os demais em fila para serem enviados em sequência
  const handleFilesSelect = (files: File[], type: 'imagem' | 'video' | 'documento' | 'sticker') => {
    if (!files || files.length === 0) return;
    const [first, ...rest] = files;
    handleFileSelect(first, type);
    if (rest.length > 0) setFileQueue(rest);
  };

  // --- Envio de mensagem interativa (botões/listas) ---
  const handleSendInteractive = async () => {
    if (!interactiveText.trim() || interactiveOptions.filter(o => o.trim()).length < 1) {
      toast({ title: t('atendimento.toastInteractiveRequiredTitle'), description: t('atendimento.toastInteractiveRequired'), variant: "destructive" });
      return;
    }
    if (!chatResumo?.id) return;
    setInteractiveSending(true);
    try {
      const instanciaId = selectedInstance?.id || (chatResumo as any).instancia?.id || (chatResumo as any).instanciaUazapi?.id || (chatResumo as any).instanciaMeta?.id || (chatResumo as any).instanciaMetaId || (chatResumo as any).instanciaId;
      const choices = interactiveOptions.filter(o => o.trim()).map(label => label.trim());
      const payload: any = {
        ticketId: chatResumo.id,
        instanciaId,
        interactiveData: {
          type: interactiveType === 'list' ? 'list' : 'button',
          text: interactiveText.trim(),
          choices,
          footerText: interactiveFooter.trim() || undefined,
          listButton: interactiveType === 'list' ? (interactiveListButton || t('atendimento.options')) : undefined,
        },
      };
      await api.post('/mensagens/send-interactive', payload);
      toast({ title: t('atendimento.toastInteractiveSentTitle'), description: t('atendimento.toastInteractiveSent', { count: choices.length, items: interactiveType === 'list' ? t('atendimento.listOptionsCount') : t('atendimento.buttonsCount') }) });
      setShowInteractiveMode(false);
      setInteractiveText('');
      setInteractiveOptions(['', '']);
      setInteractiveFooter('');
      setInteractiveListButton(t('atendimento.options'));
    } catch (error: any) {
      console.error('Erro ao enviar mensagem interativa:', error);
      toast({ title: t('common.error'), description: error?.response?.data?.message || t('atendimento.toastInteractiveSendError'), variant: "destructive" });
    } finally {
      setInteractiveSending(false);
    }
  };

  // --- Envio de arquivo após confirmação ---
  const handleSendFile = async () => {
    if (!filePreview) return;
    setEnviandoArquivo(true);
    try {
      const formData = new FormData();
      formData.append('file', filePreview.file);
      formData.append('remoteJid', chatResumo.remoteJid);
      formData.append('conversaId', chatResumo.id);
      if (fileDescription) {
        let caption = '';
        // Apply signature if configured and enabled (same logic as in handleSendMessage)
        if (sendSignature) {
          if (chatConfig?.assinaturaAtendente && chatConfig?.textoAssinatura) {
            let signature = chatConfig.textoAssinatura
              .replace('{nome_atendente}', user.nome)
              .replace('{empresa}', (user as any).empresa?.nome || '');

            // Add support for {saudacao} variable
            if (signature.includes('{saudacao}')) {
              const hora = new Date().getHours();
              let saudacao;
              if (hora >= 6 && hora < 12) {
                saudacao = 'Bom dia';
              } else if (hora >= 12 && hora < 18) {
                saudacao = 'Boa tarde';
              } else {
                saudacao = 'Boa noite';
              }
              signature = signature.replace(/{saudacao}/g, saudacao);
            }

            // Add support for {nome_contato} variable
            if (signature.includes('{nome_contato}') && chatResumo?.contato?.nome) {
              signature = signature.replace(/{nome_contato}/g, chatResumo.contato.nome);
            }

            // Clean unset variables
            signature = signature.replace(/{nome_contato}/g, '').replace(/{saudacao}/g, '');

            caption = '*' + signature + '*:\n';
          } else {
            // Fallback: ativado manualmente, usar apenas nome_atendente
            caption = '*' + user.nome + '*:\n';
          }
        }

        caption += fileDescription;
        setFileDescription(caption);
        formData.append('caption', caption);
      }
      const response = await api.post('/mensagens/send-message', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      console.log(response);

      // Clear draft after successful file send
      if (chatResumo?.id) {
        DraftManager.removeDraft(chatResumo.id);
      }

      // Se houver mais arquivos selecionados, avança para o próximo válido da fila
      let remaining = [...fileQueue];
      let processedNext: ReturnType<typeof processFile> = null;
      while (remaining.length > 0 && !processedNext) {
        const [next, ...rest] = remaining;
        remaining = rest;
        processedNext = processFile(next);
      }
      setFileQueue(remaining);

      if (processedNext) {
        setFileDescription('');
        setEditMode('none');
        setImageZoom(1);
        setImagePan({ x: 0, y: 0 });
        setTextOverImage('');
        setFilePreview(processedNext);
      } else {
        setShowFilePreview(false);
        setFilePreview(null);
        setFileDescription('');
        setNovaMensagem(''); // Limpar o campo de digitação principal também
        setShowAttachDrop(false);
        setShowAttach(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    } catch (err) {
      toast({ title: t('common.error'), description: t('atendimento.toastSendFileError'), variant: "destructive" });
    } finally {
      setEnviandoArquivo(false);
    }
  };

  // Detecta arrastar arquivo para a tela (drag & drop global)
  useEffect(() => {
    let dragCounter = 0;
    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter++;
      if (!showFilePreview && !showAttachDrop) setShowAttachDrop(true);
    };
    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter--;
      if (dragCounter <= 0) {
        setShowAttachDrop(false);
        dragCounter = 0;
      }
    };
    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter = 0;
      setShowAttachDrop(false);
      const files = Array.from(e.dataTransfer?.files || []) as File[];
      if (files.length === 0) return;
      handleFilesSelect(files, 'documento');
    };
    document.body.addEventListener('dragenter', handleDragEnter);
    document.body.addEventListener('dragleave', handleDragLeave);
    document.body.addEventListener('dragover', handleDragOver);
    document.body.addEventListener('drop', handleDrop);
    return () => {
      document.body.removeEventListener('dragenter', handleDragEnter);
      document.body.removeEventListener('dragleave', handleDragLeave);
      document.body.removeEventListener('dragover', handleDragOver);
      document.body.removeEventListener('drop', handleDrop);
    };
  }, [showFilePreview, showAttachDrop]);

  const [audioRecorder, setAudioRecorder] = useState<any>(null);

  const startRecording = async () => {
    setAudioRecording(true);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new (window as any).MediaRecorder(stream);
    setAudioRecorder(mediaRecorder);
    const audioChunks: Blob[] = [];
    mediaRecorder.ondataavailable = (event: any) => {
      audioChunks.push(event.data);
    };
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      setAudioBlob(audioBlob);
      setAudioUrl(URL.createObjectURL(audioBlob));
      setAudioRecording(false);
      setAudioRecorder(null);
    };
    mediaRecorder.onpause = () => setAudioRecording(false);
    mediaRecorder.onresume = () => setAudioRecording(true);
    mediaRecorder.start();
  };

  const stopRecording = () => {
    setAudioRecording(false);
    if (audioRecorder) {
      audioRecorder.stop();
      setAudioRecorder(null);
    }
  };

  const handleSendAudio = async () => {
    if (!audioBlob) return;
    try {
      await handleSendMessage(undefined, {
        type: 'audio',
        fileName: 'audio.webm',
        mimeType: 'audio/webm',
        fileBlob: audioBlob
      });
      setShowAudioRecorder(false);
      setAudioBlob(null);
      setAudioUrl(null);
    } catch (err) {
      toast({ title: t('common.error'), description: t('atendimento.toastSendAudioError'), variant: "destructive" });
    }
  };

  // --- Busca de mensagens na aba lateral ---
  const handleSearchTab = async (term: string) => {
    if (!term.trim() || !chatResumo.remoteJid) {
      setSearchTabResults([]);
      setSearchTabError(null);
      return;
    }
    setSearchTabLoading(true);
    setSearchTabError(null);
    try {
      const resp = await api.get('/chats/search-messages', {
        params: {
          remoteJid: chatResumo.remoteJid,
          term: term.trim()
        }
      });
      setSearchTabResults(resp.data || []);
    } catch (err: any) {
      setSearchTabError(t('atendimento.toastSearchMessagesError'));
      setSearchTabResults([]);
      toast({ title: t('common.error'), description: t('atendimento.toastSearchMessagesError'), variant: "destructive" });
    } finally {
      setSearchTabLoading(false);
    }
  };

  // Função para rolar até a mensagem na conversa
  const scrollToMensagemById = (msgId: string) => {
    const idx = mensagens.findIndex(m => m.id === msgId);
    if (idx !== -1) {
      const el = document.getElementById(`msg-${idx}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Opcional: destaque temporário
        el.classList.add('ring-2', 'ring-green-500');
        setTimeout(() => el.classList.remove('ring-2', 'ring-green-500'), 1500);
      }
    }
  };

  // --- Renderização ---
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error || !conversa) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4 bg-gray-100 dark:bg-gray-900">
        <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
        <h1 className="text-xl font-semibold text-center mb-2 text-gray-800 dark:text-gray-200">{t('atendimento.loadErrorTitle')}</h1>
        <p className="text-gray-600 dark:text-gray-400 text-center mb-6">{error || t('atendimento.conversationNotFoundFallback')}</p>
        <Button onClick={() => setChatResumo(null)}>{t('atendimento.backToDashboard')}</Button>
      </div>
    );
  }

  async function startAudioRecording() {
    setAudioRecording(true);
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new (window as any).MediaRecorder(stream);
    setAudioRecorder(mediaRecorder);
    const audioChunks: Blob[] = [];
    mediaRecorder.ondataavailable = (event: any) => {
      audioChunks.push(event.data);
    };
    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
      setAudioBlob(audioBlob);
      setAudioUrl(URL.createObjectURL(audioBlob));
      setAudioRecording(false);
      setAudioRecorder(null);
    };
    mediaRecorder.onpause = () => setAudioRecording(false);
    mediaRecorder.onresume = () => setAudioRecording(true);
    mediaRecorder.start();
  }

  const resetContatoForm = () => {
    setContatoIdEdit('');
    setNomeContato('');
    setTelefoneContato('');
    setEmailContato('');
    setNotasContato('');
    setInstanciaContato('');
  };

  const handleContatoIsEdit = (contato: any) => {
    setShowContatoModal(true);
    if (contato) {
      setContatoIdEdit(contato.id);
      setNomeContato(contato.nome || '');
      setTelefoneContato(contato.telefone);
      setEmailContato(contato.email || '');
      setNotasContato(contato.notas || '');
    }
  };

  const handleSubmitContato = async () => {
    try {
      setLoading(true);
      const empresaId = user.empresaId;

      const data = {
        empresaId,
        telefone: telefoneContato,
        nome: nomeContato,
        email: emailContato,
        notas: notasContato,
        instanciaId: instanciaContato || null,
      };

      const putResponse = await api.put(`/contatos/${contatoIdEdit}`, data);

      // Refresh contact data after update
      const response = await api.get(`/contatos/${contatoIdEdit}`);
      const contatoAtualizado = response.data?.contato || response.data || putResponse.data?.contato;

      if (contatoAtualizado) {
        const pertenceAoChat = !!chatResumo && (
          chatResumo.contato?.id === contatoAtualizado.id || chatResumo.contatoId === contatoAtualizado.id
        );

        // Atualiza o cabeçalho/painel de detalhes do chat aberto
        setConversa(prev => prev && prev.contato?.id === contatoAtualizado.id
          ? { ...prev, contato: { ...prev.contato, ...contatoAtualizado } }
          : prev);

        if (pertenceAoChat) {
          // Propaga os dados novos (não o chatResumo antigo) para a lista lateral
          const chatResumoAtualizado: ConversaResumo = {
            ...chatResumo,
            nome: renderEmojiWithFallback(contatoAtualizado.nome || contatoAtualizado.telefone || chatResumo.nome),
            avatar: contatoAtualizado.avatarUrl || chatResumo.avatar,
            contato: { ...(chatResumo.contato || {}), ...contatoAtualizado },
          };

          setChatResumo(chatResumoAtualizado);

          if (refresh && typeof refresh === 'function') {
            refresh(chatResumoAtualizado);
          }
        }
      }

      toast({
        title: t('common.success'),
        description: t('atendimento.toastContactUpdated'),
      });

      setShowContatoModal(false);
    } catch (e: any) {
      toast({
        title: t('common.error'),
        description: extractError(e?.response?.data?.message),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const extractError = (e: any) => e?.response?.data?.message || e?.response?.data?.error || e?.message || String(e);

  const loadForwardContacts = async (page = 1, reset = false) => {
    try {
      // Save current scroll position before loading more
      if (!reset && forwardScrollRef.current) {
        setForwardScrollPosition(forwardScrollRef.current.scrollTop);
      }

      if (reset) {
        setForwardLoading(true);
        setForwardPage(1);
        setForwardContacts([]);
        setForwardScrollPosition(0);
      } else {
        setForwardLoadingMore(true);
      }

      const response = await api.get('/contatos', {
        params: {
          page,
          limit: 20,
          search: forwardSearchTerm || undefined
        }
      });

      const contacts = response.data?.contatos || response.data || [];
      const totalCount = response.data?.total || 0;

      // Filter out the current chat contact
      const filteredContacts = contacts.filter((contact: any) =>
        contact.telefone !== chatResumo?.contato?.telefone
      );

      // Remove duplicates when concatenating
      const existingPhoneNumbers = new Set(forwardContacts.map(c => c.telefone));
      const newContacts = filteredContacts.filter((contact: any) => !existingPhoneNumbers.has(contact.telefone));

      const hasMore = newContacts.length >= 20 && (forwardContacts.length + newContacts.length) < totalCount;

      if (reset || page === 1) {
        setForwardContacts(filteredContacts);
      } else {
        setForwardContacts(prev => [...prev, ...newContacts]);

        // Restore scroll position after adding new contacts
        setTimeout(() => {
          if (forwardScrollRef.current && forwardScrollPosition > 0) {
            forwardScrollRef.current.scrollTop = forwardScrollPosition;
          }
        }, 100); // Increased timeout for better scroll restoration
      }

      setForwardHasMore(hasMore);
      setForwardTotalCount(totalCount);
      setForwardPage(page);
    } catch (error) {
      console.error('Erro ao carregar contatos para encaminhamento:', error);
      toast({
        title: t('common.error'),
        description: t('atendimento.toastForwardContactsLoadError'),
        variant: 'destructive'
      });
    } finally {
      setForwardLoading(false);
      setForwardLoadingMore(false);
    }
  };

  const loadMoreForwardContacts = () => {
    if (forwardHasMore && !forwardLoading) {
      loadForwardContacts(forwardPage + 1, false);
    }
  };

  // Helper para obter MIME type completo
  const getMimeType = (tipo: string) => {
    const mimeMap: Record<string, string> = {
      'texto': 'text/plain',
      'imagem': 'image/jpeg',
      'audio': 'audio/ogg',
      'video': 'video/mp4',
      'documento': 'application/octet-stream',
      'sticker': 'image/webp',
      'localizacao': 'text/plain',
      'contato': 'text/vcard',
      'botao': 'text/plain',
      'pix': 'text/plain',
      'reaction': 'text/plain'
    };
    return mimeMap[tipo] || 'application/octet-stream';
  };

  // Helper para converter arquivo para base64
  const convertFileToBase64 = async (url: string): Promise<string | null> => {
    if (!url) return null;

    try {
      const response = await fetch(url);
      if (!response.ok) return null;

      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove prefix data:image/jpeg;base64,
          const base64Data = result.includes(';base64,')
            ? result.split(';base64,')[1]
            : result;
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('Erro ao converter arquivo para base64:', error);
      return null;
    }
  };

  // Helper para obter dados da mensagem para encaminhamento
  const getForwardMessageData = async (msg: any) => {
    const baseData = {
      type: msg.tipo,
      forward: true
    };

    if (msg.tipo === 'texto') {
      return { ...baseData, text: msg.conteudo || '' };
    }

    if (['imagem', 'audio', 'video', 'documento', 'sticker'].includes(msg.tipo)) {
      // Tenta obter o caminho de várias fontes em ordem de prioridade
      let fileUrl = '';

      if (msg.midiaUrl) {
        fileUrl = getFileUrlMemo(msg.midiaUrl);
      } else if (msg.conteudo && msg.conteudo.startsWith('/uploads/')) {
        fileUrl = getFileUrlMemo(msg.conteudo);
      } else if (msg.conteudo && /^https?:\/\//.test(msg.conteudo)) {
        fileUrl = msg.conteudo; // Já é URL completa
      }

      const fileName = getFileNameFromPath(msg.midiaUrl) || getFileNameFromPath(msg.conteudo);

      console.log('[FORWARD] Caminho do arquivo gerado:', fileUrl);
      console.log('[FORWARD] Dados da mensagem original:', { midiaUrl: msg.midiaUrl, conteudo: msg.conteudo });
      console.log('[FORWARD] Objeto da mensagem: ', msg);

      return {
        ...baseData,
        text: fileUrl, // Caminho completo do arquivo no servidor
        caption: msg.caption || '', // Caption/legenda da mensagem original
        fileName: fileName || `arquivo.${msg.tipo === 'imagem' ? 'jpg' : msg.tipo === 'audio' ? 'ogg' : msg.tipo === 'video' ? 'mp4' : 'pdf'}`,
        mimeType: getMimeType(msg.tipo)
      };
    }

    // Outros tipos (localizacao, contato, etc)
    return {
      ...baseData,
      text: msg.conteudo || JSON.stringify(msg)
    };
  };

  const handleForwardMessage = async () => {
    if (!forwardMessage || selectedForwardContacts.length === 0) return;

    try {
      setForwardLoading(true);

      const selectedContacts = forwardContacts.filter(contact =>
        selectedForwardContacts.includes(contact.id)
      );

      let successCount = 0;
      let errorCount = 0;

      // Obter dados da mensagem uma vez (incluindo conversão de arquivos)
      const messageData = await getForwardMessageData(forwardMessage);

      for (const contact of selectedContacts) {
        try {
          // Find or create a chat/ticket with the selected contact
          const chatResponse = await api.post('/chats/find-or-create', {
            telefone: contact.telefone,
            instanciaId: selectedInstance?.id
          });
          const targetChat = chatResponse.data;

          // Se já houver atendimento aberto em outra instância, pular este contato
          if (targetChat?.openOnOtherInstance || targetChat?.alreadyAssigned) {
            errorCount++;
            continue;
          }

          // Send the message to the target chat
          const requestData = {
            remoteJid: contact.telefone,
            conversaId: targetChat.id,
            instanciaId: selectedInstance?.id,
            ...messageData
          };

          await api.post('/mensagens/send-message', requestData);
          successCount++;
        } catch (error) {
          console.error(`Erro ao encaminhar mensagem para ${contact.nome}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast({
          title: t('common.success'),
          description: `${t('atendimento.toastMessageForwarded', { count: successCount })}${errorCount > 0 ? ` (${errorCount} ${t('atendimento.toastMessageForwardedFailed')})` : ''}`,
        });
      }

      if (errorCount > 0) {
        toast({
          title: t('common.warning'),
          description: t('atendimento.toastForwardFailed', { count: errorCount }),
          variant: 'destructive'
        });
      }

      // Close the modal and reset state
      setShowForwardModal(false);
      setForwardMessage(null);
      setForwardContacts([]);
      setForwardSearchTerm('');
      setSelectedForwardContacts([]);
      setForwardScrollPosition(0);
    } catch (error: any) {
      console.error('Erro ao encaminhar mensagem:', error);
      toast({
        title: t('common.error'),
        description: error?.response?.data?.message || t('atendimento.toastForwardError'),
        variant: 'destructive'
      });
    } finally {
      setForwardLoading(false);
    }
  };

  // Função utilitária para exibir label de data WhatsApp-style
  function getDateLabel(date: Date) {
    if (!date) return '';
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const msgDate = new Date(date);
    if (isNaN(msgDate.getTime())) return '';
    msgDate.setHours(0, 0, 0, 0);
    const diffMs = now.getTime() - msgDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return t('atendimento.today');
    if (diffDays === 1) return t('atendimento.yesterday');
    if (diffDays === 2) return t('atendimento.dayBeforeYesterday');
    if (diffDays < 7 && diffDays > 2) {
      if (i18n.language.startsWith('pt')) {
        return format(date, "EEEE, dd 'de' MMMM", { locale: ptBR });
      }
      return format(date, 'EEEE, MMMM d');
    }
    return format(date, "dd/MM/yyyy");
  }

  // Função para renderizar a imagem com edição (desenho/texto)
  const renderImagePreview = () => {
    if (!filePreview || filePreview.type !== 'imagem') return null;

    // Desenho no canvas
    const handleCanvasMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (editMode !== 'draw') return;
      setIsDrawing(true);
      const rect = e.currentTarget.getBoundingClientRect();
      setDrawPoints([{ x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    };

    const handleCanvasMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (!isDrawing || editMode !== 'draw') return;
      const rect = e.currentTarget.getBoundingClientRect();
      setDrawPoints((prev) => [...prev, { x: e.clientX - rect.left, y: e.clientY - rect.top }]);
    };

    const handleCanvasMouseUp = () => {
      setIsDrawing(false);
    };

    const handleCanvasMouseLeave = () => {
      setIsDrawing(false);
    };

    // Texto sobre imagem
    const handleTextDragStart = (e: React.MouseEvent<HTMLDivElement>) => {
      if (editMode !== 'text') return;
      setIsDraggingText(true);
    };

    const handleTextDrag = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isDraggingText || editMode !== 'text') return;
      const container = imageContainerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      setTextPosition({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };

    const handleTextDragEnd = () => {
      setIsDraggingText(false);
    };

    // Pan da imagem quando em zoom
    const handleImageMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (imageZoom <= 1 || editMode !== 'none' || e.button !== 0) return; // Apenas drag primário e zoom > 1
      setIsPanningImage(true);
      imagePanStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panX: imagePan.x,
        panY: imagePan.y,
      };
      e.preventDefault();
    };

    const handleImageMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!isPanningImage || imageZoom <= 1) return;
      const container = imageContainerRef.current;
      if (!container) return;

      const deltaX = e.clientX - imagePanStartRef.current.x;
      const deltaY = e.clientY - imagePanStartRef.current.y;

      // Limita o pan para não ir muito além das bordas
      const maxPanX = (container.clientWidth * (imageZoom - 1)) / 2;
      const maxPanY = (container.clientHeight * (imageZoom - 1)) / 2;

      const newPanX = Math.max(-maxPanX, Math.min(maxPanX, imagePanStartRef.current.panX + deltaX));
      const newPanY = Math.max(-maxPanY, Math.min(maxPanY, imagePanStartRef.current.panY + deltaY));

      setImagePan({ x: newPanX, y: newPanY });
    };

    const handleImageMouseUp = () => {
      setIsPanningImage(false);
    };

    const handleImageMouseLeave = () => {
      setIsPanningImage(false);
    };

    // Zoom com scroll da roda
    const handleImageWheel = (e: React.WheelEvent<HTMLDivElement>) => {
      if (editMode !== 'none') return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newZoom = Math.max(0.5, Math.min(3, imageZoom + delta));
      setImageZoom(newZoom);

      // Se fez zoom out, reseta o pan
      if (newZoom === 1) {
        setImagePan({ x: 0, y: 0 });
      }
    };

    // Limpar edição
    const handleClearEdits = () => {
      setDrawPoints([]);
      setTextOverImage('');
      // Centraliza o texto na imagem ao limpar edição
      if (imageContainerRef.current) {
        const container = imageContainerRef.current;
        const rect = container.getBoundingClientRect();
        setTextPosition({
          x: rect.width / 2,
          y: rect.height / 2,
        });
      } else {
        setTextPosition({ x: 50, y: 50 });
      }
    };

    // Renderização
    return (
      <div
        ref={imageContainerRef}
        className="relative flex items-center justify-center w-full h-full"
        style={{
          minHeight: 300,
          minWidth: 300,
          cursor: isPanningImage ? 'grabbing' : (imageZoom > 1 && editMode === 'none' ? 'grab' : 'default'),
          userSelect: 'none',
        }}
        onClick={(e) => {
          // Fecha se clicar em qualquer área que não seja a imagem
          if (e.target !== e.currentTarget.querySelector('img')) {
            setShowFilePreview(false);
            setFileQueue([]);
            setImageZoom(1);
            setImagePan({ x: 0, y: 0 });
          }
        }}
        onMouseDown={handleImageMouseDown}
        onMouseMove={handleImageMouseMove}
        onMouseUp={handleImageMouseUp}
        onMouseLeave={handleImageMouseLeave}
        onWheel={handleImageWheel}
      >
        {/* Botões de zoom - apenas para imagens e quando não estiver em modo de edição */}
        {filePreview.type === 'imagem' && editMode === 'none' && (
          <div className="absolute bottom-6 right-6 flex flex-col gap-2 z-30" onClick={(e) => e.stopPropagation()}>
            <Button
              size="sm"
              variant="outline"
              className="bg-white/90 hover:bg-white text-black dark:text-white"
              onClick={() => setImageZoom(prev => Math.min(prev + 0.25, 3))}
              disabled={imageZoom >= 3}
            >
              <ZoomIn size={16} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="bg-white/90 hover:bg-white text-black dark:text-white"
              onClick={() => setImageZoom(prev => Math.max(prev - 0.25, 0.5))}
              disabled={imageZoom <= 0.5}
            >
              <ZoomOut size={16} />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="bg-white/90 hover:bg-white text-black dark:text-white"
              onClick={() => {
                setImageZoom(1);
                setImagePan({ x: 0, y: 0 });
              }}
            >
              <RotateCcw size={16} />
            </Button>
          </div>
        )}
        <img
          src={filePreview.url}
          alt={t('atendimento.imagePreviewAlt')}
          className="max-h-[85vh] max-w-[95vw] object-contain rounded bg-black select-none"
          style={{
            boxShadow: '0 2px 16px rgba(0,0,0,0.4)',
            transform: `scale(${imageZoom}) translate(${imagePan.x / imageZoom}px, ${imagePan.y / imageZoom}px)`,
            transition: isPanningImage ? 'none' : 'transform 0.1s ease-out',
          }}
          draggable={false}
          onClick={(e) => e.stopPropagation()} // Impede que o clique na imagem feche o preview
        />
        {editMode === 'crop' && (
          <Cropper
            image={filePreview.url}
            onCropComplete={(croppedArea: any, croppedAreaPixels: any) => {
              // Salva a área de crop para uso posterior
              setFilePreview(prev => prev ? { ...prev, croppedAreaPixels } : prev);
            }}
            onCropChange={(crop) => setFilePreview(prev => prev ? { ...prev, crop } : prev)}
            crop={filePreview.crop || { x: 0, y: 0 }}
            zoom={filePreview.zoom || 1}
            onZoomChange={(zoom) => setFilePreview(prev => prev ? { ...prev, zoom } : prev)}
            aspect={4 / 3}
            style={{
              containerStyle: {
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                width: '100%',
                height: '100%',
                zIndex: 20,
              }
            }}
          />
        )}
        {editMode === 'crop' && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditMode('none')}
            >
              {t('common.cancel')}
            </Button>
            <Button
              size="sm"
              onClick={async () => {
                // Faz o crop da imagem usando canvas
                if (!filePreview?.croppedAreaPixels) return;
                const { croppedAreaPixels } = filePreview;
                if (!croppedAreaPixels) return;
                const img = new window.Image();
                img.src = filePreview.url;
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  canvas.width = croppedAreaPixels.width;
                  canvas.height = croppedAreaPixels.height;
                  const ctx = canvas.getContext('2d');
                  if (ctx) {
                    ctx.drawImage(
                      img,
                      croppedAreaPixels.x,
                      croppedAreaPixels.y,
                      croppedAreaPixels.width,
                      croppedAreaPixels.height,
                      0,
                      0,
                      croppedAreaPixels.width,
                      croppedAreaPixels.height
                    );
                    canvas.toBlob(blob => {
                      if (blob) {
                        const croppedUrl = URL.createObjectURL(blob);
                        setFilePreview({
                          ...filePreview,
                          file: new File([blob], filePreview.file.name, { type: filePreview.file.type }),
                          url: croppedUrl,
                          crop: undefined,
                          zoom: undefined,
                          croppedAreaPixels: undefined,
                        });
                        setEditMode('none');
                        setImageZoom(1); // Reseta zoom após crop
                        setImagePan({ x: 0, y: 0 }); // Reseta pan após crop
                      }
                    }, filePreview.file.type);
                  }
                };
              }}
              className="bg-[#00a884] hover:bg-[#008a6b] text-white"
            >
              {t('atendimento.applyCrop')}
            </Button>
          </div>
        )}
        {/* Canvas para desenho */}
        {editMode === 'draw' && (
          <canvas
            ref={imageCanvasRef}
            width={600}
            height={400}
            className="absolute top-0 left-0 w-full h-full pointer-events-auto"
            style={{ zIndex: 10 }}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={handleCanvasMouseUp}
            onMouseLeave={handleCanvasMouseLeave}
          />
        )}
        {/* Render desenho no canvas */}
        {editMode === 'draw' && imageCanvasRef.current && drawPoints.length > 1 && (() => {
          const canvas = imageCanvasRef.current;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = "#00a884";
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.moveTo(drawPoints[0].x, drawPoints[0].y);
            drawPoints.forEach(pt => ctx.lineTo(pt.x, pt.y));
            ctx.stroke();
          }
          return null;
        })()}
        {/* Texto sobre imagem */}
        {editMode === 'text' && textOverImage && (
          <div
            className="absolute cursor-move select-none"
            style={{
              left: textPosition.x,
              top: textPosition.y,
              color: "#fff",
              fontWeight: "bold",
              fontSize: 28,
              textShadow: "0 2px 8px #000",
              zIndex: 20,
              userSelect: "none"
            }}
            onMouseDown={handleTextDragStart}
            onMouseMove={handleTextDrag}
            onMouseUp={handleTextDragEnd}
            onMouseLeave={handleTextDragEnd}
          >
            {textOverImage}
          </div>
        )}
        {/* Botão para limpar edição */}
        {(editMode === 'draw' || editMode === 'text') && (
          <Button
            size="sm"
            variant="outline"
            className="absolute top-2 right-2 z-30"
            onClick={handleClearEdits}
          >
            {t('atendimento.clear')}
          </Button>
        )}
      </div>
    );
  };

  // Função utilitária para destacar termo buscado
  function highlightTerm(text: string, term: string) {
    if (!term) return text;
    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return (
      <span
        dangerouslySetInnerHTML={{
          __html: text.replace(regex, '<mark class="bg-yellow-200 text-black rounded px-1">$1</mark>')
        }}
      />
    );
  }

  return (
    <div className="flex flex-1 min-h-0 min-w-0 w-full max-w-full bg-gray-100 dark:bg-gray-900 overflow-hidden">
      {/* --- Painel Principal do Chat --- */}
      <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 relative min-h-0 min-w-0 w-full max-w-full">
        {/* Pré-visualização de arquivo antes do envio */}
        {/* Área de Drop de Arquivo (Drag and Drop) */}
        <div
          className="inset-0 z-40 bg-black/70 flex flex-col items-center justify-center w-full h-full absolute pointer-events-none"
          style={{
            display: showAttachDrop ? 'flex' : 'none',
            pointerEvents: showAttachDrop ? 'auto' : 'none',
          }}
          onDragOver={e => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={async e => {
            e.preventDefault();
            e.stopPropagation();
            setShowAttachDrop(false);
            const files = Array.from(e.dataTransfer?.files || []) as File[];
            if (files.length === 0) return;
            handleFilesSelect(files, 'documento');
          }}
        >
          <div className="w-full flex items-center justify-between px-6 py-3 bg-[#202c33] rounded-t-lg">
            <span className="text-white font-medium text-base">
              {t('atendimento.dragDropHint')}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={() => setShowAttachDrop(false)}
            >
              <X size={24} />
            </Button>
          </div>
          <div className="flex flex-col items-center justify-center flex-1 py-10">
            <Paperclip size={60} className="text-white mb-4" />
            <span className="text-white text-lg">{t('atendimento.dropToAttach')}</span>
            <span className="text-white/70 text-sm mt-2">{t('atendimento.dropFileTypes')}</span>
          </div>
        </div>
        {showFilePreview && filePreview && (
          <div
            className="inset-0 z-50 bg-black/80 flex flex-col items-center justify-center w-full h-full absolute"
            onClick={(e) => {
              // Fecha apenas se clicar fora do conteúdo da imagem
              if (e.target === e.currentTarget) {
                setShowFilePreview(false);
                setFileQueue([]);
                setImageZoom(1); // Reseta o zoom ao fechar
                setImagePan({ x: 0, y: 0 }); // Reseta o pan ao fechar
              }
            }}
          >
            {/* Barra superior estilo WhatsApp */}
            <div className="w-full flex items-center justify-between px-6 py-3 bg-[#202c33]">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10"
                  onClick={() => {
                    setShowFilePreview(false);
                    setFileQueue([]);
                    setImageZoom(1); // Reseta o zoom ao fechar
                    setImagePan({ x: 0, y: 0 }); // Reseta o pan ao fechar
                  }}
                >
                  <X size={24} />
                </Button>
                <span className="text-white font-medium text-base truncate max-w-xs">
                  {filePreview.file.name}
                </span>
                {fileQueue.length > 0 && (
                  <span className="text-xs text-white/70 bg-white/10 rounded-full px-2 py-0.5 flex-shrink-0">
                    +{fileQueue.length} {t('atendimento.inQueue')}
                  </span>
                )}
              </div>
              {/* Ícones de edição com lucide */}
              {filePreview.type === 'imagem' && filePreview.readonly !== true && (
                <div className="flex items-center gap-3">
                  <Button
                    variant={editMode === 'crop' ? "default" : "ghost"}
                    size="icon"
                    className="text-white hover:bg-white/10"
                    title={t('atendimento.cropTooltip')}
                    onClick={() => setEditMode(editMode === 'crop' ? 'none' : 'crop')}
                  >
                    <Crop size={20} />
                  </Button>
                  <Button
                    variant={editMode === 'text' ? "default" : "ghost"}
                    size="icon"
                    className="text-white hover:bg-white/10"
                    title={t('atendimento.addTextTooltip')}
                    onClick={() => setEditMode(editMode === 'text' ? 'none' : 'text')}
                  >
                    <ALargeSmallIcon size={20} />
                  </Button>
                  <Button
                    variant={editMode === 'draw' ? "default" : "ghost"}
                    size="icon"
                    className="text-white hover:bg-white/10"
                    title={t('atendimento.drawTooltip')}
                    onClick={() => setEditMode(editMode === 'draw' ? 'none' : 'draw')}
                  >
                    <Pencil size={20} />
                  </Button>
                </div>
              )}
            </div>
            {/* Área de preview centralizada */}
            <div className="flex-1 flex flex-col items-center justify-center w-full overflow-hidden relative">
              {enviandoArquivo && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center z-50">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mb-4"></div>
                  <p className="text-white text-lg">{t('atendimento.uploading')}</p>
                </div>
              )}
              {filePreview.type === 'imagem'
                ? renderImagePreview()
                : filePreview.type === 'video'
                  ? (
                    <video
                      src={filePreview.url}
                      controls
                      className="max-h-[60vh] max-w-[90vw] rounded bg-black"
                      style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.4)' }}
                    />
                  )
                  : filePreview.type === 'documento'
                    ? (() => {
                      const name = (filePreview.file?.name || '').toLowerCase();
                      const url = (filePreview.url || '').toLowerCase();
                      const ftype = (filePreview.file as any)?.type || '';
                      const isPdf = name.endsWith('.pdf') || url.includes('.pdf') || /pdf/.test(ftype);
                      // const isTextFile = (/\.(txt|csv|json|xml|md|log)$/i.test(url) ||
                      //   /text\/(plain|csv|json|xml)/.test(ftype) ||
                      //   /\.(txt|csv|json|xml|md|log)$/i.test(url)) &&
                      //   !isPdf; // Garante que PDF não seja tratado como texto

                      return (
                        <div className="flex flex-col items-center justify-center h-[60vh]">
                          {isPdf ? (
                            <iframe
                              src={filePreview.url}
                              title={filePreview.file.name}
                              className="rounded-md bg-white"
                              style={{ width: 550, height: 700 }}
                            />
                            // ) : isTextFile ? (
                            //   <TextFilePreview file={filePreview.file} url={filePreview.url} />
                          ) : (
                            <div>
                              <FileText size={80} className="text-green-600 mb-4" />
                              <span className="text-lg text-white">{filePreview.file.name}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()
                    : null
              }
              {/* Campo para adicionar texto sobre imagem */}
              {filePreview.type === 'imagem' && editMode === 'text' && filePreview.readonly !== true && (
                <div className="absolute bottom-24 left-1/2 -translate-x-1/2 w-full max-w-xs flex flex-col items-center">
                  <Input
                    placeholder={t('atendimento.typeTextPlaceholder')}
                    value={textOverImage}
                    onChange={e => setTextOverImage(e.target.value)}
                    className="mb-2"
                  />
                  <span className="text-xs text-white">{t('atendimento.textDragHint')}</span>
                </div>
              )}
            </div>
            {/* Área de legenda e botões, fixa na parte inferior */}
            {filePreview.readonly !== true && (
              <div className="w-full flex items-center gap-2 px-6 py-4 bg-[#202c33]">
                <Input
                  placeholder={t('atendimento.addCaption')}
                  ref={fileInputRefText}
                  value={fileDescription}
                  spellCheck={true}
                  lang="pt-BR"
                  onChange={e => setFileDescription(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendFile();
                    }
                  }}
                  className="flex-1 bg-[#2a3942] text-white border-none focus:ring-0 focus:border-none"
                  style={{ minWidth: 0 }}
                />
                <Button
                  variant="outline"
                  className="border-none text-white bg-transparent hover:bg-white/10"
                  onClick={() => { setShowFilePreview(false); setFileQueue([]); }}
                >
                  <X size={20} />
                </Button>
                <Button
                  className="bg-[#00a884] hover:bg-[#008a6b] text-white rounded-full w-10 h-10 flex items-center justify-center"
                  onClick={handleSendFile}
                  title={t('atendimento.send')}
                >
                  <Send size={40} />
                </Button>
              </div>
            )}
          </div>
        )}
        {/* Cabeçalho do Chat */}
        <header className="bg-[#f0f2f5] md:bg-[#00a884] dark:bg-slate-800 md:dark:bg-slate-700 text-gray-900 md:text-white dark:text-gray-100 md:dark:text-white p-3 flex items-center shadow-sm flex-shrink-0 border-b border-gray-200 md:border-black/10 dark:border-slate-700 md:dark:border-white/10 h-[64px] w-full min-w-0 justify-between">
          {/* Lado Esquerdo: Contato */}
          <div className="flex items-center flex-1 md:flex-none md:w-1/3 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-600 dark:text-gray-300 md:text-white mr-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-full flex-shrink-0"
              onClick={() => setChatResumo(null)}
              title={t('atendimento.backCloseTooltip')}
            >
              <ArrowLeft size={20} />
            </Button>
            <Avatar onClick={() => { setActiveTab('detalhes'); setSidebarInfoCollapsed(false); }} className="h-10 w-10 mr-3 cursor-pointer border-2 border-white/50 flex-shrink-0">
              <AvatarImage src={getFileUrlMemo(conversa.contato?.avatarUrl)} alt={conversa.contato?.nome} />
              <AvatarFallback className="bg-gray-500 text-white">{getInitials(conversa.contato?.nome)}</AvatarFallback>
            </Avatar>

            <div className="cursor-pointer overflow-hidden">
              <h2 className="font-semibold text-base truncate leading-tight text-gray-900 md:text-white dark:text-gray-100" title={renderEmojiWithFallback(conversa.contato?.nome || t('atendimento.headerClientFallback'))}>
                {renderEmojiWithFallback(conversa.contato?.nome || t('atendimento.headerClientFallback'))}
              </h2>
              <p className="text-xs text-gray-500 md:text-white/80 dark:text-gray-400 md:dark:text-white/80 truncate" title={conversa.contato?.telefone}>
                {conversa.contato?.telefone}
              </p>
              {/* Tags do Contato */}
              {contatoTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {contatoTags.slice(0, 3).map((contatoTag: any) => (
                    <TagBadge
                      key={contatoTag.tagId}
                      titulo={contatoTag.tag.titulo}
                      cor={contatoTag.tag.cor}
                      prioridade={contatoTag.tag.prioridade}
                      className="text-[10px]"
                    />
                  ))}
                  {contatoTags.length > 3 && (
                    <span className="text-[10px] text-white/70 font-medium">
                      +{contatoTags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Centro: Dados do Ticket e Instância */}
          <div className="flex flex-col items-center justify-center w-auto md:w-1/3 min-w-0 px-2 text-center overflow-hidden">
            <div className="hidden md:flex items-center gap-2 text-xs text-gray-600 md:text-white/90 dark:text-gray-400 md:dark:text-white/90 font-medium mb-1 truncate max-w-full">
              <span>#{conversa.protocolo || chatResumo.id?.slice(0, 8)}</span>
              <span className="text-gray-400 md:text-white/50">•</span>
              <span>
                {conversa.dataCriacao ?
                  format(new Date(conversa.dataCriacao), "dd/MM HH:mm", { locale: ptBR })
                  : format(new Date(), "dd/MM HH:mm", { locale: ptBR })
                }
              </span>
            </div>

            {/* Tags do Ticket */}
            {tags.length > 0 && (
              <div className="flex flex-wrap md:flex-wrap flex-nowrap overflow-hidden gap-1.5 mt-1 justify-center max-w-full">
                {tags.slice(0, 3).map((ticketTag: any) => (
                  <TagBadge
                    key={ticketTag.tagId}
                    titulo={ticketTag.tag.titulo}
                    cor={ticketTag.tag.cor}
                    prioridade={ticketTag.tag.prioridade}
                    className="text-[10px] whitespace-nowrap"
                  />
                ))}
                {tags.length > 3 && (
                  <span className="text-[10px] text-white/70 font-medium whitespace-nowrap">
                    +{tags.length - 3}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Lado Direito: Ações */}
          <div className="flex items-center justify-end min-w-0 flex-shrink-0 md:w-1/3">
            <div className="flex items-center space-x-1">
              <div className="hidden md:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="flex items-center text-xs bg-white/10 hover:bg-white/20 text-white px-3 py-1 rounded-full gap-2 border-none h-auto transition-all disabled:opacity-80"
                    >
                      {selectedInstance?.statusConexao === 'connected' ? (
                        <Wifi className="text-green-400" size={14} />
                      ) : (
                        <WifiOff className="text-orange-400" size={14} />
                      )}
                      <span className="max-w-[150px] truncate font-semibold">
                        {selectedInstance?.nome || conversa.instancia?.nome || t('atendimento.selectInstance')}
                      </span>
                      {/* <ChevronDown size={12} className="opacity-70" /> */}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="center" className="w-64 dark:bg-slate-800">
                    <DropdownMenuLabel className="text-xs">{t('atendimento.availableInstances')}</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {loadingInstances ? (
                      <div className="px-2 py-4 text-center text-xs text-gray-500">{t('atendimento.loading')}</div>
                    ) : instances.length > 0 ? (
                      instances.map((inst) => (
                        <DropdownMenuItem
                          key={inst.id}
                          className={`flex items-center gap-3 cursor-pointer p-2 ${selectedInstance?.id === inst.id ? 'bg-slate-100 dark:bg-slate-700 font-bold' : ''}`}
                          onClick={() => handleMudarInstancia(inst)}
                        >
                          {inst.statusConexao === 'connected' ? (
                            <Wifi className="text-green-600" size={16} />
                          ) : (
                            <WifiOff className="text-orange-600" size={16} />
                          )}
                          <div className="flex-1 overflow-hidden text-left">
                            <div className="text-sm truncate">{inst.nome}</div>
                            <div className="text-[10px] text-gray-500">
                              {inst.statusConexao === 'connected' ? t('atendimento.connected') : t('atendimento.disconnected')}
                            </div>
                          </div>
                          {selectedInstance?.id === inst.id && (
                            <div className="w-2 h-2 rounded-full bg-blue-500" />
                          )}
                        </DropdownMenuItem>
                      ))
                    ) : (
                      <div className="px-2 py-4 text-center text-xs text-gray-500">{t('atendimento.noInstances')}</div>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <div className="relative flex items-center">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-600 md:text-white dark:text-gray-300 md:dark:text-white hover:bg-black/5 md:hover:bg-white/10 rounded-full"
                  title={t('atendimento.searchTooltip')}
                  onClick={() => { setActiveTab('pesquisa'); setSidebarInfoCollapsed(false); }}
                >
                  <Search size={20} />
                </Button>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 md:text-white dark:text-gray-300 md:dark:text-white hover:bg-black/5 md:hover:bg-white/10 rounded-full"
                title={sidebarInfoCollapsed ? t('atendimento.expandSidebar') : t('atendimento.collapseSidebar')}
                onClick={() => setSidebarInfoCollapsed((v) => !v)}
              >
                <MoreVertical size={20} />
              </Button>
            </div>
          </div>
        </header>

        {/* Banner de Mensagem Fixada */}
        {pinnedMessage && (
          <div
            className="flex items-center gap-3 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-gray-700 p-2 px-4 shadow-sm z-10 cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors shrink-0"
            onClick={() => {
              const msgIndex = mensagens.findIndex(m => m.id === pinnedMessage.id);
              if (msgIndex !== -1) {
                const element = document.getElementById(`msg-${msgIndex}`);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  element.classList.add('bg-yellow-100', 'dark:bg-yellow-900/30');
                  setTimeout(() => element.classList.remove('bg-yellow-100', 'dark:bg-yellow-900/30'), 2000);
                }
              }
            }}
          >
            <Pin className="text-gray-500" size={16} />
            <div className="flex-1 overflow-hidden">
              <div className="text-xs font-semibold text-emerald-600">{t('atendimento.pinnedMessage')}</div>
              <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
                {pinnedMessage.conteudo ? renderEmojiWithFallback(pinnedMessage.conteudo) : t('atendimento.pinnedMessageNoText')}
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handlePinMessage(pinnedMessage.id, false);
              }}
              className="text-gray-400 hover:text-gray-600 p-1"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Área de Mensagens com ScrollArea */}
        <div className="relative flex-1 min-h-0 min-w-0 w-full max-w-full flex flex-col">
          {/* Data flutuante no topo */}
          {activeDate && (
            <div className="absolute top-2 left-0 right-0 z-20 flex justify-center pointer-events-none">
              <span className="bg-gray-300 dark:bg-slate-600 text-gray-700 dark:text-gray-100 text-xs px-3 py-1 rounded-full shadow-md pointer-events-auto">
                {getDateLabel(new Date(activeDate))}
              </span>
            </div>
          )}
          <ScrollArea
            ref={scrollAreaRef}
            className="flex-1 w-full min-w-0 max-w-full overflow-hidden overflow-x-hidden bg-[#e5ded8] dark:bg-slate-800 bg-repeat"
            style={{
              backgroundImage: `url(${isDarkMode ? fundoConversaDark : fundoConversa})`
            }}
          >
            <MessageMenu
              isOpen={isMenuOpen}
              onClose={() => { setIsMenuOpen(false); setReactItems(false); }}
              onReply={handleReply}
              onEdit={handleEdit}
              onReact={handleReact}
              onDelete={handleDelete}
              onForward={handleForward}
              onReactResponse={handleReactResponse}
              setSelectedMessage={setSelectedMessage}
              reactItems={reactItems}
              anchorEl={menuAnchor}
              boundaryEl={scrollViewportRef.current}
              messageId={selectedMessage || ''}
              isOwnMessage={isOwnMessage}
              onPin={() => {
                if (selectedMessage) {
                  const isPinned = pinnedMessage?.id === selectedMessage;
                  handlePinMessage(selectedMessage, !isPinned);
                }
              }}
              isPinned={pinnedMessage?.id === selectedMessage}
              isGroup={chatResumo?.ultimaMensagem?.isGrupo || chatResumo?.contato?.telefone?.includes('@g.us') || false}
              hideEdit={grupoMeta}
              hideDelete={grupoMeta}
              onMention={() => setShowMentionModal(true)}
            />
            <div className="p-4 pr-6 flex flex-col gap-1">
              <HistoricoZproInline
                contatoId={(chatResumo as any)?.contato?.id || (chatResumo as any)?.contatoId}
                idZpro={(chatResumo as any)?.contato?.idZpro}
              />
              {mensagens.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 dark:text-gray-400 py-10">
                  <MessageSquare size={48} className="opacity-50 mb-2" />
                  <p>{t('atendimento.noMessagesInConversation')}</p>
                </div>
              ) : (
                mensagens.map((msg, idx) => {
                  // Agrupamento visual: se a mensagem anterior é do mesmo remetente, não mostra avatar
                  const prevMsg = mensagens[idx - 1];
                  const showAvatar =
                    msg.remetente === 'cliente' &&
                    (!prevMsg || prevMsg.remetente !== 'cliente');
                  // Destaque busca
                  const highlight = searchResults.includes(idx);
                  
                  const currDate = new Date(msg.timestamp);
                  const prevDate = prevMsg ? new Date(prevMsg.timestamp) : null;
                  const showDate = !prevMsg || currDate.toDateString() !== prevDate?.toDateString();
                  const showTicketDivider = prevMsg && msg.ticketId !== prevMsg.ticketId;

                  return (
                    <React.Fragment key={msg.id}>
                      {showTicketDivider && (
                        <div className="flex justify-center items-center my-4">
                          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
                          <span className="flex-shrink mx-4 text-xs text-gray-500 dark:text-gray-400">
                            {t('atendimento.ticketStart', { protocol: msg.ticket?.protocolo })}
                          </span>
                          <div className="flex-grow border-t border-gray-300 dark:border-gray-600"></div>
                        </div>
                      )}
                      {showDate && (
                        <div className="flex justify-center my-2" data-date-sentinel={currDate.toDateString()}>
                          <span className="bg-gray-300 dark:bg-slate-600 text-gray-700 dark:text-gray-100 text-xs px-3 py-1 rounded-full shadow-md">
                            {getDateLabel(currDate)}
                          </span>
                        </div>
                      )}
                      <div id={`msg-${idx}`} className={highlight ? "bg-yellow-100 dark:bg-yellow-900/30 rounded" : ""}>
                      <MensagemItem
                        key={`${msg.id}-${msg.dataEdicao || 'no-edit'}`}
                        msg={msg}
                        user={user}
                        conversa={conversa}
                        selectedMessage={selectedMessage}
                        onContextMenu={handleContextMenu}
                        onOpenPreview={openPreview}
                        onStartChat={handleStartNewChatFromContato}
                        onReply={(m) => {
                          setReplyingTo(m);
                          setTimeout(() => inputRef.current?.focus(), 100);
                        }}
                        ticketData={conversa}
                      />
                      </div>
                    </React.Fragment>
                  );
                })
              )}
              {isFetchingMore && (
                <div className="flex justify-center my-2">
                  <span className="text-xs text-gray-400">{t('atendimento.loadingMoreMessages')}</span>
                </div>
              )}
              {scheduledMessages.length > 0 && (
                <div className="flex flex-col gap-1.5 mt-1">
                  {scheduledMessages.map((ag) => (
                    <div key={ag.id} className="flex justify-end">
                      <div className="group relative max-w-[75%] md:max-w-[60%] bg-white/80 dark:bg-slate-700/70 border border-dashed border-blue-300 dark:border-blue-700 rounded-lg rounded-tr-none px-3 py-2 shadow-sm">
                        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-blue-600 dark:text-blue-400 mb-1">
                          <CalendarClock size={13} />
                          Agendada para {new Date(ag.dataAgendamento).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
                          {ag.conteudo || (ag.fileName ? `📎 ${ag.fileName}` : 'Mensagem agendada')}
                        </p>
                        <button
                          type="button"
                          onClick={() => handleCancelScheduledMessage(ag.id)}
                          className="absolute -top-2 -right-2 hidden group-hover:flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white hover:bg-red-600"
                          title="Cancelar agendamento"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div ref={messagesEndRef} className="h-1" /> {/* Elemento para scroll */}
            </div>
          </ScrollArea>

          {/* Balão de mensagens rápidas - Posicionado no footer */}
          {showQuickMessages && (
            <div
              className="absolute z-50 bottom-0 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] md:w-[500px] mb-2 transform bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden"
              ref={el => {
                if (!el) return;
                const handleClickOutside = (event: MouseEvent) => {
                  if (!el.contains(event.target as Node)) {
                    setShowQuickMessages(false);
                    setQuickMessageFilter('');
                  }
                };
                document.addEventListener('mousedown', handleClickOutside);
                return () => {
                  document.removeEventListener('mousedown', handleClickOutside);
                };
              }}
            >
              <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {t('atendimento.quickMessages')}
                </h4>
              </div>
              <div className="max-h-48 overflow-y-auto">
                {loadingQuickMessages ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    <Loader2 size={20} className="animate-spin mx-auto" />
                    <p className="text-sm mt-2">{t('atendimento.loading')}</p>
                  </div>
                ) : filteredQuickMessages.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                    <p className="text-sm">
                      {quickMessageFilter ? t('atendimento.noQuickMessagesFound') : t('atendimento.noQuickMessagesAvailable')}
                    </p>
                  </div>
                ) : (
                  <div className="py-1">
                    {filteredQuickMessages.map((msg) => (
                      <button
                        key={msg.id}
                        className="w-full px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors border-b border-gray-100 dark:border-slate-700 last:border-b-0 group"
                        onClick={() => selectQuickMessage(msg)}
                      >
                        <div className="flex flex-col">
                          <div className="flex items-center justify-between">
                            <span className="font-medium text-sm text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400">
                              /{msg.chave || t('atendimento.noKey')}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {msg.dataCriacao ? new Date(msg.dataCriacao).toLocaleDateString() : ''}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                            {msg.mensagem || t('atendimento.messageNoContent')}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Balão de alerta da janela de 24h (Meta API / híbrido UAZAPI+META) - estilo mensagens rápidas */}
          {show24hBalloon && !showQuickMessages && (
            <div className="absolute z-50 bottom-0 left-1/2 -translate-x-1/2 w-[calc(100vw-2rem)] md:w-[500px] mb-2 transform bg-yellow-50 dark:bg-yellow-900/20 rounded-xl shadow-2xl border border-yellow-300 dark:border-yellow-700 overflow-hidden animate-fade-in-up">
              <div className="px-4 py-3 border-b border-yellow-200 dark:border-yellow-800 bg-yellow-100 dark:bg-yellow-900/40 flex items-center justify-between">
                <h4 className="text-sm font-bold text-yellow-800 dark:text-yellow-300 flex items-center gap-1">
                  <AlertCircle size={16} /> {t('atendimento.windowClosedTitle')}
                </h4>
                <span className="text-[10px] uppercase tracking-wide text-yellow-700 dark:text-yellow-400 bg-yellow-200 dark:bg-yellow-800/70 px-2 py-0.5 rounded-full">
                  {t('atendimento.onlyTemplates')}
                </span>
              </div>
              <div className="px-4 py-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  {selectedInstance?.tipoApi === 'META'
                    ? t('atendimento.windowClosedDescMeta')
                    : t('atendimento.windowClosedDescHybrid')}
                </p>
                <Button
                  type="button"
                  size="sm"
                  className="mt-3 w-full border border-yellow-300 dark:border-yellow-600 text-yellow-800 dark:text-yellow-300 bg-yellow-100 hover:bg-yellow-200 dark:bg-yellow-900/50 dark:hover:bg-yellow-900/70"
                  onClick={() => setShowMetaTemplateDialog(true)}
                  disabled={enviando}
                >
                  <Send className="h-4 w-4 mr-2" />
                  {t('atendimento.selectTemplateToSend')}
                </Button>
              </div>
            </div>
          )}

          {showScrollToBottom && (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              className="absolute right-4 bottom-4 rounded-full shadow-lg bg-white/90 hover:bg-white dark:bg-slate-800/90 dark:hover:bg-slate-800"
              title={t('atendimento.goToEnd')}
              onClick={() => scrollToBottom('smooth')}
            >
              <ChevronDown size={20} />
            </Button>
          )}
        </div>

        {/* Área de Digitação */}
        <footer className="p-3 bg-[#f0f2f5] dark:bg-slate-700 border-t border-gray-200 dark:border-slate-600 flex items-center space-x-1 md:space-x-3 flex-shrink-0 relative w-full min-w-0">

          {/* Ações Mobile (Dropdown Unificado) */}
          <div className="md:hidden relative flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"
                  disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente'}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <Paperclip size={24} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                side="top"
                className="mb-2 dark:bg-slate-800 p-1 w-56"
                onMouseDown={(e) => e.preventDefault()}
              >
                <DropdownMenuItem
                  onClick={() => setShowEmoji((v) => !v)}
                  className="cursor-pointer gap-3 py-3"
                >
                  <Smile size={20} className="text-yellow-500" /> {t('atendimento.emoji')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleFileInput('imagem')} className="cursor-pointer gap-3 py-3">
                  <ImageIcon size={20} className="text-blue-500" /> Imagem
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleFileInput('video')} className="cursor-pointer gap-3 py-3">
                  <Video size={20} className="text-purple-500" /> {t('atendimento.video')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleFileInput('documento')} className="cursor-pointer gap-3 py-3">
                  <FileText size={20} className="text-green-600" /> Documento
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSelectContacts} className="cursor-pointer gap-3 py-3">
<UserIcon size={20} className="text-orange-500" /> {t('atendimento.contact')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { setShowQuickMessages(!showQuickMessages); setQuickMessageFilter(''); }} className="cursor-pointer gap-3 py-3">
                  <Zap size={20} className="text-yellow-600" /> {t('atendimento.quickMessages')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setShowBlockDialog(true); loadBlockCategories(); }} className="cursor-pointer gap-3 py-3">
                  <Layers3 size={20} className="text-purple-600" /> {t('atendimento.blockSend')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { setShowInteractiveMode(!showInteractiveMode); setShowQuickMessages(false); }} className="cursor-pointer gap-3 py-3">
                  <MessageSquarePlus size={20} className="text-blue-600" /> {t('atendimento.interactiveMessage')}
                </DropdownMenuItem>
                {(user.role === 'Admin' || user.role === 'SuperAdmin') && (
                  <DropdownMenuItem onClick={() => setSendSignature(!sendSignature)} className="cursor-pointer gap-3 py-3">
                    <Pen size={20} className={sendSignature ? 'text-blue-500' : 'text-gray-500'} /> {sendSignature ? t('atendimento.removeSignature') : t('atendimento.useSignature')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <input type="file" ref={fileInputRef} multiple style={{ display: 'none' }} />

            {showEmoji && (
              <div
                className="absolute z-50 bottom-12 left-0"
                onMouseDown={(e) => e.preventDefault()}
              >
                <EmojiPicker
                  onEmojiClick={(emojiData) => {
                    const emoji = emojiData.emoji || '';
                    const el = inputRef.current;
                    if (el) {
                      const start = el.selectionStart ?? (novaMensagem?.length ?? 0);
                      const end = el.selectionEnd ?? start;
                      setNovaMensagem((prev) => {
                        const s = prev || '';
                        return s.slice(0, start) + emoji + s.slice(end);
                      });
                    } else {
                      setNovaMensagem((prev) => (prev || '') + emoji);
                    }
                    setShowEmoji(false);
                  }}
                  searchDisabled={false}
                  width={320}
                  height={400}
                  previewConfig={{ defaultEmoji: "1f60a", defaultCaption: t('atendimento.noEmojiSelected') }}
                />
              </div>
            )}
          </div>

          {/* Ações Desktop */}
          <div className="hidden md:flex items-center space-x-2">
            {/* Emoji Picker */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"
                title={t('atendimento.emoji')}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowEmoji((v) => !v)}
                tabIndex={-1}
                disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente'}
                type="button"
              >
                <Smile size={24} />
              </Button>
              {showEmoji && (
                <div
                  className="absolute z-50 bottom-12 left-0"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      const emoji = emojiData.emoji || '';
                      const el = inputRef.current;
                      if (el) {
                        const start = el.selectionStart ?? (novaMensagem?.length ?? 0);
                        const end = el.selectionEnd ?? start;
                        setNovaMensagem((prev) => {
                          const s = prev || '';
                          return s.slice(0, start) + emoji + s.slice(end);
                        });
                      } else {
                        setNovaMensagem((prev) => (prev || '') + emoji);
                      }
                      setShowEmoji(false);
                    }}
                    searchDisabled={false}
                    width={320}
                    height={400}
                    previewConfig={{ defaultEmoji: "1f60a", defaultCaption: t('atendimento.noEmojiSelected') }}
                  />
                </div>
              )}
            </div>
            {/* Anexos */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"
                title={t('atendimento.attach')}
                onClick={handleAttachClick}
                tabIndex={-1}
                disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente'}
                type="button"
              >
                <Paperclip size={24} />
              </Button>
              {showAttach && (
                <div
                  className="absolute z-50 bottom-14 left-0 min-w-[220px] bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-700 flex flex-col py-2 px-2 gap-1 animate-fade-in"
                  tabIndex={-1}
                  ref={el => {
                    if (!el) return;
                    const handleClickOutside = (event: MouseEvent) => {
                      if (!el.contains(event.target as Node)) {
                        setShowAttach(false);
                      }
                    };
                    document.addEventListener('mousedown', handleClickOutside);
                    return () => {
                      document.removeEventListener('mousedown', handleClickOutside);
                    };
                  }}
                >
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition dark:text-white"
                    onClick={() => handleFileInput('imagem')}
                  >
<ImageIcon size={20} className="text-blue-500" /> {t('atendimento.image')}
                  </button>
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition dark:text-white"
                    onClick={() => handleFileInput('video')}
                  >
<Video size={20} className="text-purple-500" /> {t('atendimento.video')}
                  </button>
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition dark:text-white"
                    onClick={() => handleFileInput('documento')}
                  >
<FileText size={20} className="text-green-600" /> {t('atendimento.document')}
                  </button>
                  <button
                    className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition dark:text-white"
                    onClick={handleSelectContacts}
                  >
                    <UserIcon size={20} className="text-orange-500" /> {t('atendimento.contacts')}
                  </button>
                </div>
              )}
            </div>
            {/* Mensagens Rápidas */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"
                title={t('atendimento.quickMessages')}
                onClick={() => {
                  setShowQuickMessages(!showQuickMessages);
                  setQuickMessageFilter('');
                }}
                tabIndex={-1}
                disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente'}
                type="button"
              >
                <Zap size={24} />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-500 dark:text-gray-400 hover:text-purple-600 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"
                title={t('atendimento.blockSend')}
                onClick={() => { setShowBlockDialog(true); loadBlockCategories(); }}
                tabIndex={-1}
                disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente'}
                type="button"
              >
                <Layers3 size={24} />
              </Button>
            </div>

            {/* Mensagem Interativa */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className={`rounded-full ${showInteractiveMode ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400 hover:text-blue-600 hover:bg-gray-200 dark:hover:bg-slate-600'}`}
                title={t('atendimento.interactiveMessage')}
                onClick={() => setShowInteractiveMode(!showInteractiveMode)}
                tabIndex={-1}
                disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente'}
                type="button"
              >
                <MessageSquarePlus size={24} />
              </Button>
            </div>

            {/* Agendar envio */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600"
                title={t('atendimento.scheduleSend')}
                onClick={handleOpenScheduleDialog}
                tabIndex={-1}
                disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente' || isMetaRestrictedInput}
                type="button"
              >
                <CalendarClock size={24} />
              </Button>
            </div>

            {/* Assinatura */}
            {(user.role === 'Admin' || user.role === 'SuperAdmin') && (
              <div className="relative">
                <Button
                  variant="ghost"
                  size="icon"
                  className={`text-gray-500 dark:text-gray-400 hover:text-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-slate-600 ${sendSignature ? 'bg-blue-500 dark:bg-black text-white hover:bg-blue-600' : ''}`}
                  title={t('atendimento.signature')}
                  onClick={() => setSendSignature(!sendSignature)}
                  tabIndex={-1}
                  disabled={chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente'}
                  type="button"
                >
                  <Pen size={24} />
                </Button>
              </div>
            )}
          </div>
          {(replyingTo?.id || editingMessage?.id) && (
            <div
              className="absolute md:relative bottom-[calc(100%+8px)] md:bottom-auto left-1/2 md:left-auto -translate-x-1/2 md:translate-x-0 w-[calc(100%-1.5rem)] md:w-auto md:min-w-[250px] bg-white dark:bg-slate-800 p-3 md:p-2 border-2 md:border-none md:border-l-4 border-blue-500 rounded-2xl md:rounded-none shadow-2xl md:shadow-none z-40 cursor-pointer animate-in fade-in slide-in-from-bottom-4 duration-300"
              onClick={() => {
                const messageElement = document.querySelector(`[data-message-id="${replyingTo?.id || editingMessage?.id}"]`);
                if (messageElement) {
                  messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  messageElement.classList.add('ring-2', 'ring-blue-500', 'ring-opacity-50');
                  setTimeout(() => {
                    messageElement.classList.remove('ring-2', 'ring-blue-500', 'ring-opacity-50');
                  }, 2000);
                }
              }}>
              <div className="flex justify-between items-center mb-1 gap-2">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-4 bg-blue-500 rounded-full md:hidden" />
                  <span className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                    {replyingTo ? t('atendimento.replyingTo', { name: replyingTo.remetenteTipo !== 'cliente' ? t('common.you') : conversa?.contato?.nome }) : t('atendimento.editingMessage')}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setReplyingTo(null);
                    setEditingMessage(null);
                  }}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full text-gray-500 dark:text-gray-400 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="text-sm text-gray-700 dark:text-gray-300 truncate opacity-90 italic">
                {(() => {
                  const message = replyingTo || editingMessage;
                  if (!message) return '';

                  let content = '';
                  if (message.tipo && message.tipo !== 'texto') {
                    content = `[${message.tipo}]`;
                  } else if (message.conteudo) {
                    content = typeof message.conteudo === 'string'
                      ? message.conteudo
                      : JSON.stringify(message.conteudo);
                  } else {
                    content = t('atendimento.message');
                  }

                  return content.length > 35
                    ? `${content.slice(0, 35)}...`
                    : content;
                })()}
              </div>
            </div>
          )}
          {/* Input de mensagem */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
            }}
            className="flex-1 flex items-center"
          >
            {showInteractiveMode ? (
              <div className="flex-1 bg-white dark:bg-slate-700 rounded-2xl border border-gray-200 dark:border-slate-600 p-3 space-y-2">
                {/* Tipo */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs rounded-full font-medium ${interactiveType === 'buttons' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-gray-400'}`}
                    onClick={() => setInteractiveType('buttons')}
                  >
                    {t('atendimento.buttons')}
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-xs rounded-full font-medium ${interactiveType === 'list' ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300' : 'bg-gray-100 dark:bg-slate-600 text-gray-600 dark:text-gray-400'}`}
                    onClick={() => setInteractiveType('list')}
                  >
                    {t('atendimento.list')}
                  </button>
                </div>
                {/* Texto da mensagem */}
                <textarea
                  value={interactiveText}
                  onChange={(e) => setInteractiveText(e.target.value)}
                  placeholder={t('atendimento.interactiveTextPlaceholder')}
                  className="w-full text-sm rounded-lg px-3 py-2 border border-gray-200 dark:border-slate-500 bg-gray-50 dark:bg-slate-600 dark:text-white resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
                  rows={2}
                  disabled={interactiveSending}
                />
                {/* Opções (botões ou itens de lista) */}
                <div className="max-h-48 overflow-y-auto space-y-2 pr-1">
                  {interactiveOptions.map((opt, idx) => (
                    <div key={idx} className="flex gap-2 items-center">
                      <input
                        value={opt}
                        onChange={(e) => {
                          const newOpts = [...interactiveOptions];
                          newOpts[idx] = e.target.value;
                          setInteractiveOptions(newOpts);
                        }}
                        placeholder={`${interactiveType === 'list' ? t('atendimento.item') : t('atendimento.button')} ${idx + 1}`}
                        className="flex-1 text-sm rounded-lg px-3 py-2 border border-gray-200 dark:border-slate-500 bg-gray-50 dark:bg-slate-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                        disabled={interactiveSending}
                      />
                      {interactiveOptions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => setInteractiveOptions(interactiveOptions.filter((_, i) => i !== idx))}
                          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setInteractiveOptions([...interactiveOptions, ''])}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    disabled={interactiveOptions.length >= (interactiveType === 'buttons' ? 3 : 10)}
                  >
                    + {interactiveType === 'list' ? t('atendimento.addItem') : t('atendimento.addButton')}
                  </button>
                </div>
                {/* Footer (opcional) */}
                <input
                  value={interactiveFooter}
                  onChange={(e) => setInteractiveFooter(e.target.value)}
                  placeholder={t('atendimento.footerTextPlaceholder')}
                  className="w-full text-sm rounded-lg px-3 py-2 border border-gray-200 dark:border-slate-500 bg-gray-50 dark:bg-slate-600 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                  disabled={interactiveSending}
                />
                {/* Ações */}
                <div className="flex gap-2 pt-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setShowInteractiveMode(false);
                      setInteractiveText('');
                      setInteractiveOptions(['', '']);
                      setInteractiveFooter('');
                    }}
                    disabled={interactiveSending}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSendInteractive}
                    disabled={interactiveSending || !interactiveText.trim() || interactiveOptions.filter(o => o.trim()).length < 1}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {interactiveSending ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
                    {t('atendimento.sendInteractive', { type: interactiveType === 'list' ? t('atendimento.list') : t('atendimento.buttons') })}
                  </Button>
                </div>
              </div>
            ) : (
            <>
            <div className="relative flex-1">
              <SpellCheckInput
                ref={inputRef}
                type="textarea"
                value={novaMensagem}
                onChange={setNovaMensagem}
                placeholder={replyingTo ? t('atendimento.replyPlaceholder') : t('atendimento.messagePlaceholder')}
                className="w-full rounded-3xl px-4 py-2 pr-20 min-h-[30px] max-h-24 border-transparent focus:border-transparent focus:ring-0 bg-white dark:bg-slate-600 dark:text-white dark:placeholder-gray-400 resize-none overflow-hidden"
                disabled={enviando || chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente' || isMetaRestrictedInput}
                showSuggestions={true}
                autoCorrect={false}
                options={{
                  language: 'pt-BR',
                  ignoreNumbers: true,
                  ignoreEmails: true,
                  ignoreUrls: false,
                  ignoreCase: true,
                  minWordLength: 2
                }}
                mentionsList={participants}
                onMentionsChange={setSelectedMentions}
                rows={1}
                onKeyDown={(e: any) => {
                  const isMobile = window.innerWidth < 768;
                  if (e.key === 'Enter' && !e.shiftKey) {
                    if (!isMobile) {
                      e.preventDefault();
                      if (novaMensagem.trim()) {
                        handleSendMessage();
                      }
                    }
                  }
                  if (e.key === 'Escape' && showQuickMessages) {
                    setShowQuickMessages(false);
                    setQuickMessageFilter('');
                  }
                }}
                onPaste={(e: any) => {
                  const items = e.clipboardData.items;
                  if (!items) return;

                  // Verifica por imagens, vídeos ou arquivos na área de transferência
                  for (let i = 0; i < items.length; i++) {
                    const item = items[i];

                    if (item.kind === 'file') {
                      e.preventDefault();
                      const file = item.getAsFile();
                      if (!file) continue;

                      let type: 'imagem' | 'video' | 'documento' = 'documento';
                      if (item.type.startsWith('image/')) type = 'imagem';
                      else if (item.type.startsWith('video/')) type = 'video';

                      handleFileSelect(file, type);
                      return;
                    }
                  }
                }}
                onInput={(e: any) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height = `${Math.min(target.scrollHeight, 128)}px`;

                  const value = e.target.value;
                  if (chatResumo?.id && user?.id) {
                    DraftManager.saveDraft(chatResumo.id, value, user.id);
                  }
                }}
                autoFocus
              />
            </div>
            {/* Botão de envio/áudio */}
            {novaMensagem.trim() ? (
              <Button
                type="submit"
                size="icon"
                className="ml-2 rounded-full bg-[#00a884] hover:bg-[#008a6b] text-white flex-shrink-0 w-10 h-10"
                disabled={enviando || !novaMensagem.trim() || chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente' || isMetaRestrictedInput}
                title={t('atendimento.send')}
                ref={sendButtonRef}
                onClick={(e) => {
                  e.preventDefault();
                  if (novaMensagem.trim()) {
                    handleSendMessage(e);
                  }
                }}
              >
                {enviando ? <Clock size={20} className="animate-spin" /> : <Send size={20} />}
              </Button>
            ) : (
              <Button
                type="button"
                size="icon"
                className="ml-2 rounded-full bg-[#00a884] hover:bg-[#008a6b] text-white flex-shrink-0 w-10 h-10"
                title={t('atendimento.recordAudio')}
                onClick={() => setShowAudioRecorder(true)}
                disabled={enviando || chatResumo?.status === 'fechado' || chatResumo?.status === 'pendente' || isMetaRestrictedInput}
              >
                <Mic size={22} />
              </Button>
            )}
            </>)}
          </form>
          {/* Gravação de áudio */}
          {showAudioRecorder && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-xs w-full flex flex-col items-center relative">
                <button className="absolute top-2 right-2 text-gray-400 hover:text-red-500" onClick={() => setShowAudioRecorder(false)}>
                  <X size={20} />
                </button>
                <div className="mb-2">
                  {audioRecording ? (
                    <span className="text-red-500">{t('atendimento.recording')}</span>
                  ) : audioUrl ? (
                    <audio src={audioUrl} controls />
                  ) : (
                    <span className="text-gray-500">{t('atendimento.clickToRecord')}</span>
                  )}
                </div>
                <div className="flex gap-2 w-full">
                  {!audioRecording && !audioUrl && (
                    <Button className="flex-1" onClick={startRecording}>{t('atendimento.start')}</Button>
                  )}
                  {audioRecording && (
                    <Button className="flex-1" variant="destructive" onClick={stopRecording}>{t('atendimento.stop')}</Button>
                  )}
                  {audioUrl && (
                    <>
                      <Button className="flex-1" onClick={handleSendAudio}>{t('atendimento.sendAudio')}</Button>
                      <Button className="flex-1" variant="outline" onClick={() => { setAudioBlob(null); setAudioUrl(null); }}>{t('atendimento.discard')}</Button>
                    </>
                  )}
                  <Button className="flex-1" variant="outline" onClick={() => setShowAudioRecorder(false)}>{t('atendimento.close')}</Button>
                </div>
              </div>
            </div>
          )}
          {/* Gravação de áudio estilo WhatsApp Web */}
          {showAudioRecorder && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl p-6 max-w-md w-full flex flex-col items-center relative">
                <button
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                  onClick={() => {
                    setShowAudioRecorder(false);
                    setAudioRecording(false);
                    setAudioBlob(null);
                    setAudioUrl(null);
                  }}
                >
                  <X size={20} />
                </button>
                <div className="w-full flex flex-col">
                  {/* Barra de gravação igual WhatsApp Web */}
                  <div className="flex items-center w-full gap-4 py-2">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
                      <Mic size={28} className={audioRecording ? "animate-pulse text-green-600" : "text-gray-400"} />
                    </div>
                    <div className="flex-1 flex flex-col">
                      {audioRecording ? (
                        <span className="text-red-500 font-semibold">{t('atendimento.recording')}</span>
                      ) : audioUrl ? (
                        <audio src={audioUrl} controls className="w-full" />
                      ) : (
                        <span className="text-gray-500">{t('atendimento.readyToRecord')}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Pausar/Retomar */}
                      {audioRecording && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-600 hover:bg-gray-100"
                          title={t('atendimento.pause')}
                          onClick={() => {
                            if (audioRecorder && audioRecorder.state === "recording") {
                              audioRecorder.pause();
                              setAudioRecording(false);
                            }
                          }}
                        >
                          <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><rect x="6" y="5" width="4" height="14" rx="1" fill="currentColor" /><rect x="14" y="5" width="4" height="14" rx="1" fill="currentColor" /></svg>
                        </Button>
                      )}
                      {!audioRecording && audioRecorder && audioRecorder.state === "paused" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-600 hover:bg-gray-100"
                          title={t('atendimento.resume')}
                          onClick={() => {
                            audioRecorder.resume();
                            setAudioRecording(true);
                          }}
                        >
                          <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21" fill="currentColor" /></svg>
                        </Button>
                      )}
                      {/* Parar */}
                      {audioRecording && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:bg-red-100"
                          title={t('atendimento.stop')}
                          onClick={() => {
                            stopRecording();
                          }}
                        >
                          <svg width="22" height="22" fill="none" viewBox="0 0 24 24"><rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" /></svg>
                        </Button>
                      )}
                      {/* Descartar */}
                      {(audioUrl || (!audioRecording && audioBlob)) && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-gray-400 hover:text-red-500"
                          title={t('atendimento.discard')}
                          onClick={() => {
                            setAudioBlob(null);
                            setAudioUrl(null);
                            setAudioRecording(false);
                            if (audioRecorder && audioRecorder.state !== "inactive") {
                              audioRecorder.stop();
                            }
                          }}
                        >
                          <X size={22} />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Botões de ação */}
                  <div className="flex w-full justify-end gap-2 mt-4">
                    {!audioRecording && !audioUrl && (
                      <Button
                        className="bg-[#00a884] hover:bg-[#008a6b] text-white rounded-full px-6"
                        onClick={startAudioRecording}
                      >
                        <Mic size={20} className="mr-2" /> {t('atendimento.startRecording')}
                      </Button>
                    )}
                    {audioUrl && (
                      <>
                        <Button
                          className="bg-[#00a884] hover:bg-[#008a6b] text-white rounded-full px-6"
                          onClick={handleSendAudio}
                        >
                          <Send size={20} className="mr-2" /> {t('atendimento.sendAudio')}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setAudioBlob(null);
                            setAudioUrl(null);
                            setAudioRecording(false);
                          }}
                        >
                          {t('atendimento.discard')}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </footer>
        {/* Modal de agendamento de envio */}
        <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
          <DialogContent className="sm:max-w-md dark:bg-slate-800 dark:text-white">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarClock size={20} className="text-blue-600" /> {t('atendimento.scheduleMessageDialogTitle')}
              </DialogTitle>
              <DialogDescription>
                {t('atendimento.scheduleDialogDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="schedule-message">{t('atendimento.message')}</Label>
                <Textarea
                  id="schedule-message"
                  value={scheduleMessage}
                  onChange={(e) => setScheduleMessage(e.target.value)}
                  placeholder={t('atendimento.typeMessage')}
                  rows={4}
                  className="resize-none dark:bg-slate-700 dark:text-white dark:border-slate-600"
                  disabled={schedulingMessage}
                />
              </div>
              <div className="grid gap-2">
                <Label>{t('atendimento.attachmentOptional')}</Label>
                {scheduleFile ? (
                  <div className="flex items-center justify-between text-sm bg-gray-100 dark:bg-slate-700 rounded-lg px-3 py-2">
                    <span className="truncate">{scheduleFile.name}</span>
                    <button
                      type="button"
                      onClick={() => setScheduleFile(null)}
                      className="p-1 text-gray-400 hover:text-red-500 shrink-0"
                      disabled={schedulingMessage}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => scheduleFileInputRef.current?.click()}
                    disabled={schedulingMessage}
                    className="w-fit dark:bg-slate-700 dark:text-white dark:border-slate-600"
                  >
                    <Paperclip size={16} className="mr-2" /> {t('atendimento.attachFile')}
                  </Button>
                )}
                <input
                  ref={scheduleFileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) setScheduleFile(f);
                    e.target.value = '';
                  }}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="schedule-datetime">{t('atendimento.sendDateTime')}</Label>
                <Input
                  id="schedule-datetime"
                  type="datetime-local"
                  value={scheduleDateTime}
                  min={(() => {
                    const now = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
                    return now.toISOString().slice(0, 16);
                  })()}
                  onChange={(e) => setScheduleDateTime(e.target.value)}
                  disabled={schedulingMessage}
                  className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowScheduleDialog(false)}
                disabled={schedulingMessage}
                className="dark:bg-slate-700 dark:text-white dark:border-slate-600"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleScheduleMessage}
                disabled={schedulingMessage || (!scheduleMessage.trim() && !scheduleFile) || !scheduleDateTime}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {schedulingMessage ? <Loader2 size={16} className="animate-spin mr-2" /> : <CalendarClock size={16} className="mr-2" />}
                {t('atendimento.schedule')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* Modal de envio em bloco */}
        <Dialog open={showBlockDialog} onOpenChange={(open) => {
          setShowBlockDialog(open);
          if (open) loadBlockCategories();
        }}>
          <DialogContent className="sm:max-w-lg dark:bg-slate-800 dark:text-white">
            <DialogHeader>
              <DialogTitle>{t('atendimento.blockSend')}</DialogTitle>
              <DialogDescription>
                {t('atendimento.blockSendDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-80 overflow-y-auto space-y-2 my-3">
              {loadingBlockCategories ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 size={20} className="animate-spin mr-2" />
                  {t('atendimento.loadingCategories')}
                </div>
              ) : blockCategories.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                  {t('atendimento.noBlocksFound')}
                </p>
              ) : (
                blockCategories.map((cat) => {
                  const sending = blockSendingState?.blocoId === cat.id;
                  return (
                    <div
                      key={cat.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700/50"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {cat.nome || t('atendimento.unnamed')}
                        </p>
                        {cat.descricao && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                            {cat.descricao}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                          {t('atendimento.countMessages', { count: cat._count?.mensagensRapidas || 0 })}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => sendBlock(cat.id)}
                        disabled={!!blockSendingState?.sending}
                        className="ml-3 shrink-0"
                      >
                        {sending && blockSendingState?.sending ? (
                          <Loader2 size={14} className="animate-spin mr-1" />
                        ) : null}
                        {sending && !blockSendingState?.sending ? t('atendimento.sent') : t('atendimento.sendBlock')}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
        {/* Modal de seleção de contatos */}
        <Dialog open={showContactDialog} onOpenChange={setShowContactDialog}>
          <DialogContent className="max-w-lg p-0 bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-2xl max-h-[90vh] flex flex-col">
            <div className="flex flex-col w-full max-h-[90vh] overflow-hidden">
              {/* Header estilo WhatsApp */}
              <div className="flex items-center px-6 py-4 border-b border-gray-200 bg-white dark:bg-slate-800 shrink-0">
                <span className="font-semibold text-lg flex-1">{t('atendimento.sendContacts')}</span>
              </div>
              {/* Campo de busca */}
              <div className="px-6 py-3 border-b border-gray-100 bg-white dark:bg-slate-800 shrink-0">
                <div className="relative">
                  <Input
placeholder={t('atendimento.searchContacts')}
                    value={searchTabTerm}
                    onChange={async (e) => {
                      const term = e.target.value;
                      setSearchTabTerm(term);
                      //para o caso de limpar a busca, recarrega todos os contatos
                      if (!term.trim()) {
                        setSearchTabLoading(true);
                        try {
                          const resp = await api.get('/chats/contacts');
                          setContatos(resp.data);
                        } catch {
                          setContatos([]);
                        } finally {
                          setSearchTabLoading(false);
                        }
                      } else {
                        setContatos((prev) =>
                          prev.filter(
                            (c) =>
                              (c.nome || '').toLowerCase().includes(term.toLowerCase()) ||
                              (c.telefone || '').includes(term)
                          )
                        );
                      }
                    }}
                    className="rounded-full bg-gray-100 border-none focus:ring-0 focus:border-none pl-10"
                  />
                  <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                </div>
              </div>
              {/* Lista de contatos */}
              <div className="flex-1 overflow-y-auto px-0 py-2 min-h-0">
                {searchTabLoading ? (
<div className="flex items-center justify-center py-10 text-gray-400">{t('atendimento.loading')}</div>
                ) : (
                  <ul>
                    {contatos.map((c) => (
                      <li
                        key={c.id}
                        className="flex items-center px-6 py-2 hover:bg-gray-50 transition group dark:bg-slate-800"
                      >
                        <input
                          type="checkbox"
                          checked={contatosSelecionados.includes(c.id)}
                          onChange={(e) => {
                            setContatosSelecionados((prev) =>
                              e.target.checked
                                ? [...prev, c.id]
                                : prev.filter((id) => id !== c.id)
                            );
                          }}
                          className="mr-3 accent-green-600 h-5 w-5"
                        />
                        <Avatar className="h-10 w-10 mr-3">
                          <AvatarImage src={getFileUrlMemo(c.avatarUrl)} alt={c.nome} />
                          <AvatarFallback>{getInitials(c.nome)}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-medium truncate dark:text-white">{c.nome || c.telefone}</span>
                          <span className="text-xs text-gray-500 truncate dark:text-gray-400">{c.notas || t('atendimento.defaultStatus')}</span>
                        </div>
                      </li>
                    ))}
                    {contatos.length === 0 && (
<li className="text-center text-gray-400 py-10">{t('atendimento.noContactsFound')}</li>
                    )}
                  </ul>
                )}
              </div>
              {/* Footer com botão de envio */}
              <div className="flex items-center justify-end px-6 py-4 border-t border-gray-100 bg-white dark:bg-slate-800 shrink-0">
                <Button
                  onClick={handleSendContacts}
                  disabled={contatosSelecionados.length === 0}
                  className="rounded-full px-6 bg-[#00a884] hover:bg-[#008a6b] text-white font-semibold"
                >
                  <Send size={22} />
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* --- Painel Lateral de Informações (Refinado) --- */}
      <aside
        className={`transition-all duration-300 
          ${sidebarInfoCollapsed
            ? "w-0 min-w-0 max-w-0 opacity-0 pointer-events-none md:hidden"
            : "fixed inset-0 z-50 md:relative md:z-auto md:w-80 md:min-w-[320px] md:max-w-[400px] opacity-100 pointer-events-auto"
          } bg-gray-50 dark:bg-slate-800 border-l border-gray-200 dark:border-slate-700 flex flex-col flex-shrink-0 h-[100dvh] md:h-full`}
        style={{ overflow: sidebarInfoCollapsed ? 'hidden' : undefined }}
      >
        {/* Header Mobile Only */}
        {!sidebarInfoCollapsed && (
          <div className="md:hidden flex items-center justify-between p-4 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 flex-shrink-0 shadow-sm">
            <h3 className="font-semibold text-lg text-gray-800 dark:text-gray-200">{t('atendimento.conversationDetails')}</h3>
            <Button
              variant="ghost"
              size="icon"
              className="text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full"
              onClick={() => setSidebarInfoCollapsed(true)}
            >
              <X size={24} />
            </Button>
          </div>
        )}

        {!sidebarInfoCollapsed && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 h-full">
            <TabsList className={`grid w-full ${chatConfig?.permitirNotas ? 'grid-cols-3' : 'grid-cols-2'} shrink-0`}>
              <TabsTrigger value="detalhes"><Info className={`w-4 h-4 mr-1 ${activeTab === 'detalhes' && 'text-green-500'}`} /> <span className={`${activeTab === 'detalhes' && 'text-green-500'}`}>{t('atendimento.details')}</span> </TabsTrigger>
              {chatConfig?.permitirNotas && <TabsTrigger value="notas"><StickyNote className={`w-4 h-4 mr-1 ${activeTab === 'notas' && 'text-green-500'}`} /> <span className={`${activeTab === 'notas' && 'text-green-500'}`}>{t('atendimento.notes')}</span></TabsTrigger>}
              <TabsTrigger value="pesquisa"><Search className={`w-4 h-4 mr-1 ${activeTab === 'pesquisa' && 'text-green-500'}`} /> <span className={`${activeTab === 'pesquisa' && 'text-green-500'}`}>{t('atendimento.search')}</span></TabsTrigger>
            </TabsList>

            {/* Tab: Detalhes */}
            {activeTab === 'detalhes' && (
              <ScrollArea className="flex-1 h-full">
                <TabsContent value="detalhes" className="p-4 space-y-5 dark:bg-slate-900">
                  {/* Informações do Contato */}
                  <Card className="dark:bg-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center"><User className="w-4 h-4 mr-2" /> {t('atendimento.contact')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col items-center text-center mb-3">
                        <Avatar className="h-20 w-20 mb-3 border-4 border-gray-200 dark:border-slate-600">
                          <AvatarImage src={getFileUrlMemo(conversa.contato?.avatarUrl)} alt={conversa.contato?.nome} />
                          <AvatarFallback className="bg-gray-500 text-white text-xl">{getInitials(conversa.contato?.nome)}</AvatarFallback>
                        </Avatar>
                        <h3 className="font-semibold text-lg">{conversa.contato?.nome || t('atendimento.headerClientFallback')}</h3>
                        <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                          <Phone className="w-3 h-3 mr-1" /> {conversa.contato?.telefone}
                        </div>
                        {conversa.contato?.email &&
                          <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center mt-1">
                            <Mail className="w-3 h-3 mr-1" /> {conversa.contato.email}
                          </div>
                        }
                      </div>
                      <Button variant="outline" size="sm" className="w-full" onClick={() => handleContatoIsEdit(conversa.contato)}><Edit2 className="w-3 h-3 mr-1" /> {t('atendimento.editContact')}</Button>
                    </CardContent>
                  </Card>

                  {/* Detalhes da Conversa */}
                  <Card className="dark:bg-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center"><MessageSquare className="w-4 h-4 mr-2" /> {t('atendimento.conversation')}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      {conversa.protocolo && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 dark:text-gray-400">{t('atendimento.protocolLabel')}</span>
                          <span className="font-medium">{conversa.protocolo}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 dark:text-gray-400">{t('atendimento.statusLabel')}</span>
                        <Badge variant={
                          conversa.status === 'aberto' ? 'default' :
                            conversa.status === 'pendente' ? 'warning' :
                              conversa.status === 'fechado' ? 'success' : 'secondary'
                        } className="capitalize">
                          {conversa.status}
                        </Badge>
                      </div>
                      {conversa.prioridade && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-500 dark:text-gray-400">{t('atendimento.priorityLabel')}</span>
                          <Badge variant={
                            conversa.prioridade === 'baixa' ? 'outline' :
                              conversa.prioridade === 'media' ? 'default' :
                                conversa.prioridade === 'alta' ? 'warning' : 'destructive'
                          } className="capitalize">
                            {conversa.prioridade}
                          </Badge>
                        </div>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 dark:text-gray-400">{t('atendimento.createdAtLabel')}</span>
                        <span>{conversa.dataCriacao ? format(new Date(conversa.dataCriacao), 'dd/MM/yyyy HH:mm') : '-'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-500 dark:text-gray-400">{t('atendimento.updatedAtLabel')}</span>
                        <span>{formatUltimaInteracao(conversa)}</span>
                      </div>
                      {/* TODO: Adicionar Atendente Responsável */}
                      {/* <div className="flex justify-between items-center">
                    <span className="text-gray-500 dark:text-gray-400">Atendente:</span>
                    <span>Nome Atendente</span>
                  </div> */}
                    </CardContent>
                  </Card>

                  {/* Ações da Conversa */}
                  {chatResumo?.status === 'pendente' && (
                    <Card className="dark:bg-slate-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center"><Settings className="w-4 h-4 mr-2" /> {t('atendimento.actions')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <Button disabled={loading} onClick={handleStartConversa} className="w-full bg-green-600 hover:bg-green-700">
                          <MessageSquarePlus className="w-4 h-4 mr-1" />
                          {loading ? t('atendimento.starting') : t('atendimento.startService')}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {chatResumo?.status === 'fechado' && (
                    <Card className="dark:bg-slate-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center"><Settings className="w-4 h-4 mr-2" /> {t('atendimento.actions')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        <Button disabled={loading} onClick={handleStartConversa} className="w-full bg-blue-600 hover:bg-blue-700">
                          <MessageSquarePlus className="w-4 h-4 mr-1" />
                          {loading ? t('atendimento.reopening') : t('atendimento.reopenService')}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {chatResumo?.status !== 'fechado' && chatResumo?.status !== 'pendente' &&
                    <Card className="dark:bg-slate-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center"><Settings className="w-4 h-4 mr-2" /> {t('atendimento.actions')}</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {((user.role === 'Admin' || user.role === 'SuperAdmin' || chatConfig?.usuarioPodeTransferirTicket) && (!isGrupo || chatConfig?.permitirTransferenciaGrupos)) && (
                          <Button disabled={loading} onClick={handleTransferConversa} variant="outline" className="w-full"><Users className="w-4 h-4 mr-1" /> {t('atendimento.transfer')}</Button>
                        )}
                        {((chatResumo?.status as string) !== 'fechado' && (chatResumo?.status as string) !== 'pendente') && (
                          <Button disabled={loading} onClick={() => { setSelectedShareUsers([]); loadShareUsers(); setShowShareModal(true); }} variant="outline" className="w-full"><Users className="w-4 h-4 mr-1" /> {t('atendimento.share')}</Button>
                        )}
                        {(user.role === 'Admin' || user.role === 'SuperAdmin' || chatConfig?.usuarioPodeFinalizarTicket) && (
                          <Button disabled={loading} onClick={handleCloseConversa} variant="destructive" className="w-full"><LogOut className="w-4 h-4 mr-1" /> {t('atendimento.finishService')}</Button>
                        )}
                      </CardContent>
                    </Card>
                  }

                  {/* Tags */}
                  {chatConfig?.permitirTags && (
                    <Card className="dark:bg-slate-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center"><Tag className="w-4 h-4 mr-2" /> {t('atendimento.tags')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {tags.map((ticketTag: any) => (
                            <TagBadge
                              key={ticketTag.tagId}
                              titulo={ticketTag.tag?.titulo || ticketTag.tagId}
                              cor={ticketTag.tag?.cor}
                              prioridade={ticketTag.tag?.prioridade}
                              className="cursor-pointer"
                              onClick={() => removeTag(ticketTag.tagId)}
                            />
                          ))}
                        </div>
                        <SelectWithSearch
                          options={availableTags
                            .filter(tag => !tags.find(t => t.tagId === tag.id))
                            .map(tag => ({
                              value: tag.id,
                              label: tag.titulo
                            }))}
                          value={[]}
                          onChange={(selected) => {
                            if (selected.length > 0) {
                              addTag(selected[0].value);
                            }
                          }}
                          onSearchChange={(search) => {
                            setAvailableTagsSearch(search);
                            loadAvailableTags(1, search, false);
                          }}
                          onLoadMore={() => {
                            loadAvailableTags(availableTagsPage + 1, availableTagsSearch, true);
                          }}
                          hasMore={availableTagsHasMore}
                          loading={availableTagsLoading}
                          placeholder={t('atendimento.addTag')}
                          emptyMessage={t('atendimento.noTagsAvailable')}
                          className="w-full"
                        />
                      </CardContent>
                    </Card>
                  )}

                  {chatConfig?.permitirKanban && kanbanColunas.length > 0 &&
                    (user.role === 'Admin' || user.role === 'SuperAdmin' || chatConfig?.usuarioPodeMoverKanban) && (
                    <Card className="dark:bg-slate-700">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center"><LayoutGrid className="w-4 h-4 mr-2" /> {t('atendimento.kanbanColumn')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <SelectWithSearch
                          options={kanbanColunas.map((col: any) => ({
                            value: col.id,
                            label: col.titulo,
                          }))}
                          value={(chatResumo as any)?.kanbanColunaId
                            ? [{ value: (chatResumo as any).kanbanColunaId, label: kanbanColunas.find((c: any) => c.id === (chatResumo as any).kanbanColunaId)?.titulo || '' }]
                            : []}
                          onChange={handleKanbanColunaChange}
                          placeholder={t('atendimento.selectColumn')}
                          single={true}
                          className="w-full"
                        />
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                          {t('atendimento.kanbanHint')}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                </TabsContent>
              </ScrollArea>
            )}

            {/* Tab: Notas */}
            {activeTab === 'notas' && chatConfig?.permitirNotas && (
              <ScrollArea className="flex-1 h-full">
                <TabsContent value="notas" className="p-4 space-y-5 dark:bg-slate-900">
                  {/* Adicionar Nota */}
                  <Card className="dark:bg-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center"><StickyNote className="w-4 h-4 mr-2" /> {t('atendimento.addInternalNote')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <SpellCheckInput
                        type="textarea"
                        value={novaNota}
                        onChange={setNovaNota}
                        placeholder={t('atendimento.typeInternalNote')}
                        className="w-full p-2 border rounded-md h-24 text-sm dark:bg-slate-700 dark:text-white mb-2"
                        showSuggestions={true}
                        autoCorrect={false}
                        options={{
                          language: 'pt-BR',
                          ignoreNumbers: true,
                          ignoreEmails: true,
                          ignoreUrls: true,
                          ignoreCase: true,
                          minWordLength: 2
                        }}
                        rows={4}
                      />
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={createNota}
                        disabled={!novaNota.trim()}
                      >
                        {t('atendimento.saveNote')}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Histórico de Notas */}
                  <Card className="dark:bg-slate-700">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center"><Info className="w-4 h-4 mr-2" /> {t('atendimento.notesHistory')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {notas.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                            {t('atendimento.noNotes')}
                          </p>
                        ) : (
                          notas.map((nota: any) => (
                            <div key={nota.id} className="p-3 border rounded-md bg-yellow-50 dark:bg-yellow-900/30 text-sm relative group">
                              <p className="mb-1 pr-8">{renderEmojiWithFallback(nota.conteudo)}</p>
                              <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                                <span>{nota.usuario?.nome || t('atendimento.unknown')}</span>
                                <span>{nota.dataCriacao ? format(new Date(nota.dataCriacao), i18n.language.startsWith('pt') ? "dd/MM/yyyy 'às' HH:mm" : "MM/dd/yyyy 'at' HH:mm") : '-'}</span>
                              </div>
                              {nota.usuarioId === user.id && (
                                <button
                                  onClick={() => deleteNota(nota.id)}
                                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                                  title={t('atendimento.deleteNote')}
                                >
                                  ×
                                </button>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </ScrollArea>
            )}

            {/* Tab: Pesquisa */}
            {activeTab === 'pesquisa' && (
              <ScrollArea className="flex-1 h-full">
                <TabsContent value="pesquisa" className="p-4 space-y-5 dark:bg-slate-900">
                  <div className="flex flex-col gap-2">
                    <Input
                      placeholder={t('atendimento.searchMessagesPlaceholder')}
                      value={searchTabTerm}
                      onChange={e => setSearchTabTerm(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleSearchTab(searchTabTerm);
                      }}
                      className="mb-2 dark:text-white"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleSearchTab(searchTabTerm)}
                      disabled={searchTabLoading}
                    >
                      <Search className="w-4 h-4 mr-1" />
                      {t('atendimento.searchButton')}
                    </Button>
                  </div>
                  {searchTabLoading && (
                    <div className="flex items-center justify-center py-8">
                      <span className="text-gray-400">{t('atendimento.searching')}</span>
                    </div>
                  )}
                  {searchTabError && (
                    <div className="text-red-500 text-sm">{searchTabError}</div>
                  )}
                  {!searchTabLoading && searchTabResults.length === 0 && searchTabTerm && !searchTabError && (
                    <div className="text-gray-400 text-sm py-8 text-center">{t('atendimento.noMessagesFound')}</div>
                  )}
                  <div className="flex flex-col gap-2">
                    {searchTabResults.map((msg, idx) => (
                      <div
                        key={msg.id}
                        className="p-2 rounded hover:bg-green-50 dark:hover:bg-slate-700 cursor-pointer border-b border-gray-100 dark:border-slate-700"
                        onClick={() => scrollToMensagemById(msg.id)}
                        title={msg.timestamp ? format(new Date(msg.timestamp), "dd/MM/yyyy HH:mm") : ''}
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{msg.timestamp ? format(new Date(msg.timestamp), "dd/MM/yyyy HH:mm") : '-'}</span>
                        </div>
                        <div className="text-sm mt-1">
                          {highlightTerm(msg.texto || (msg.tipo === 'texto' ? msg.conteudo : msg.caption) || '', searchTabTerm)}
                        </div>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              </ScrollArea>
            )}
          </Tabs>
        )}
      </aside>

      {/* Modal de Contato */}
      {showContatoModal && (
        <Dialog open={showContatoModal} onOpenChange={(open) => { setShowContatoModal(open); if (!open) resetContatoForm(); }}>
          <DialogContent className="dark:bg-slate-700">
            <DialogHeader>
              <DialogTitle className="dark:text-white">{contatoIdEdit ? t('atendimento.editContact') : t('atendimento.newContact')}</DialogTitle>
              <DialogDescription className="dark:text-white">
                {contatoIdEdit ? t('atendimento.updateContactDesc') : t('atendimento.newContactDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="telefoneContato" className="dark:text-white">
                  {t('atendimento.phoneLabel')}
                </Label>
                <Input
                  id="telefoneContato"
                  value={telefoneContato}
                  className="col-span-3 dark:bg-slate-700 dark:text-white"
                  placeholder="5511999999999"
                  disabled
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="nomeContato" className="dark:text-white">
                  {t('atendimento.nameLabel')}
                </Label>
                <Input
                  id="nomeContato"
                  value={nomeContato}
                  onChange={(e) => setNomeContato(e.target.value)}
                  className="col-span-3 dark:bg-slate-700 dark:text-white"
                  placeholder={t('atendimento.contactNamePlaceholder')}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="emailContato" className="dark:text-white">
                  {t('atendimento.emailLabel')}
                </Label>
                <Input
                  id="emailContato"
                  type="email"
                  value={emailContato}
                  onChange={(e) => setEmailContato(e.target.value)}
                  className="col-span-3 dark:bg-slate-700 dark:text-white"
                  placeholder={t('atendimento.emailPlaceholder')}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="notasContato" className="dark:text-white">
                  {t('atendimento.notesLabel')}
                </Label>
                <Input
                  id="notasContato"
                  value={notasContato}
                  onChange={(e) => setNotasContato(e.target.value)}
                  className="col-span-3 dark:bg-slate-700 dark:text-white"
                  placeholder={t('atendimento.contactNotesPlaceholder')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="dark:text-white dark:bg-slate-700"
                onClick={() => {
                  resetContatoForm();
                  setShowContatoModal(false);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                onClick={handleSubmitContato}
                disabled={!telefoneContato || loading}
              >
                {loading ? t('atendimento.saving') : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Transferência */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="sm:max-w-[500px] dark:bg-slate-800 dark:text-white">
          <DialogHeader>
            <DialogTitle>{t('atendimento.transferTicket')}</DialogTitle>
            <DialogDescription>
              {t('atendimento.transferDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{t('atendimento.transferToUser')}</Label>
              <SelectWithSearch
                options={availableUsers
                  .filter((u: any) => u.id !== user?.id)
                  .map((u: any) => ({ value: u.id, label: `${u.nome} (${u.email})` }))}
                value={transferToUser ? [{ value: transferToUser, label: '' }] : []}
                onChange={(val) => {
                  const id = val[0]?.value || '';
                  setTransferToUser(id);
                  if (id) setTransferToQueue('');
                }}
                placeholder={t('atendimento.selectUserPlaceholder')}
                loading={loadingTransfer}
                disabled={loadingTransfer}
                single
              />
            </div>
            <div className="flex items-center justify-center text-sm text-gray-500">
              {t('atendimento.or')}
            </div>
            <div className="grid gap-2">
              <Label>{t('atendimento.transferToQueue')}</Label>
              <SelectWithSearch
                options={availableQueues.map((f: any) => ({ value: f.id, label: f.nome }))}
                value={transferToQueue ? [{ value: transferToQueue, label: '' }] : []}
                onChange={(val) => {
                  const id = val[0]?.value || '';
                  setTransferToQueue(id);
                  if (id) setTransferToUser('');
                }}
                placeholder={t('atendimento.selectQueuePlaceholder')}
                loading={loadingTransfer}
                disabled={loadingTransfer}
                single
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setShowTransferModal(false);
                setTransferToUser('');
                setTransferToQueue('');
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              onClick={handleTransferTicket}
              disabled={!transferToUser && !transferToQueue}
            >
              {t('atendimento.transfer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Encaminhamento */}
      <Dialog open={showForwardModal} onOpenChange={(open) => {
        setShowForwardModal(open);
        if (!open) {
          setForwardMessage(null);
          setForwardContacts([]);
          setForwardSearchTerm('');
          setSelectedForwardContacts([]);
          setForwardScrollPosition(0);
        }
      }}>
        <DialogContent className="sm:max-w-lg dark:bg-slate-800 dark:text-white">
          <DialogHeader>
            <DialogTitle>{t('atendimento.forwardMessageTitle')}</DialogTitle>
            <DialogDescription>
              {t('atendimento.forwardDesc', { count: selectedForwardContacts.length })}
            </DialogDescription>
          </DialogHeader>

          {/* Preview da mensagem sendo encaminhada */}
          {forwardMessage && (
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 dark:bg-slate-700 dark:border-slate-600">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                  <Forward size={16} className="text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">{t('atendimento.forwardingMessage')}:</p>

                  {forwardMessage.tipo === 'texto' && (
                    <div className="p-2 bg-white dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-500">
                      <p className="text-sm text-gray-800 dark:text-gray-200 break-words">
                        {forwardMessage.conteudo}
                      </p>
                    </div>
                  )}

                  {forwardMessage.tipo === 'imagem' && forwardMessage.midiaUrl && (
                    <div className="space-y-2">
                      <div className="relative inline-block">
                        <img
                          src={getFileUrlMemo(forwardMessage.midiaUrl)}
                          alt={t('atendimento.image')}
                          className="max-w-full h-20 object-cover rounded-lg border border-gray-200 dark:border-slate-500"
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder-image.png';
                          }}
                        />
                      </div>
                      {forwardMessage.conteudo && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                          "{forwardMessage.conteudo}"
                        </p>
                      )}
                    </div>
                  )}

                  {forwardMessage.tipo === 'audio' && (
                    <div className="flex items-center space-x-2 p-2 bg-white dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-500">
                      <Mic size={16} className="text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {getFileNameFromPath(forwardMessage.midiaUrl) || getFileNameFromPath(forwardMessage.conteudo) || t('atendimento.audio')}
                      </span>
                    </div>
                  )}

                  {forwardMessage.tipo === 'video' && (
                    <div className="flex items-center space-x-2 p-2 bg-white dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-500">
                      <Video size={16} className="text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {getFileNameFromPath(forwardMessage.midiaUrl) || getFileNameFromPath(forwardMessage.conteudo) || t('atendimento.video')}
                      </span>
                    </div>
                  )}

                  {forwardMessage.tipo === 'documento' && (
                    <div className="flex items-center space-x-2 p-2 bg-white dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-500">
                      <FileText size={16} className="text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {getFileNameFromPath(forwardMessage.midiaUrl) || getFileNameFromPath(forwardMessage.conteudo) || t('atendimento.document')}
                      </span>
                    </div>
                  )}

                  {forwardMessage.tipo === 'sticker' && (
                    <div className="flex items-center space-x-2 p-2 bg-white dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-500">
                      <Smile size={16} className="text-gray-600 dark:text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">{t('atendimento.sticker')}</span>
                    </div>
                  )}

                  {!['texto', 'imagem', 'audio', 'video', 'documento', 'sticker'].includes(forwardMessage.tipo) && (
                    <div className="p-2 bg-white dark:bg-slate-600 rounded-lg border border-gray-200 dark:border-slate-500">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('atendimento.typeLabel', { type: forwardMessage.tipo })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Campo de busca */}
          <div className="px-4 py-2 border-b border-gray-200 bg-white dark:bg-slate-800">
            <div className="relative">
              <Input
                placeholder={t('atendimento.searchContacts')}
                value={forwardSearchTerm}
                onChange={(e) => setForwardSearchTerm(e.target.value)}
                className="rounded-full bg-gray-100 dark:bg-slate-700 border-none focus:ring-0 focus:border-none pl-10 pr-10 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              {forwardSearchTerm && (
                <button
                  type="button"
                  onClick={() => setForwardSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Lista de contatos */}
          <div
            ref={forwardScrollRef}
            className="flex-1 overflow-y-auto px-0 py-2"
            style={{ maxHeight: 400 }}
            onScroll={(e) => {
              const target = e.target as HTMLDivElement;
              if (target.scrollHeight - target.scrollTop <= target.clientHeight + 50) {
                loadMoreForwardContacts();
              }
            }}
          >
            {forwardLoading ? (
              <div className="flex items-center justify-center py-10 text-gray-400">{t('atendimento.loading')}</div>
            ) : (
              <ul>
                {forwardContacts
                  .filter((contact: any) =>
                    forwardSearchTerm === '' ||
                    (contact.nome || '').toLowerCase().includes(forwardSearchTerm.toLowerCase()) ||
                    (contact.telefone || '').toLowerCase().includes(forwardSearchTerm.toLowerCase())
                  )
                  .map((contact: any) => {
                    const isSelected = selectedForwardContacts.includes(contact.id);
                    const isDisabled = !isSelected && selectedForwardContacts.length >= 10;

                    return (
                      <li
                        key={contact.id}
                        className={`flex items-center px-6 py-3 hover:bg-gray-50 dark:hover:bg-slate-700 transition ${isDisabled ? 'opacity-50' : 'cursor-pointer'
                          }`}
                        onClick={() => {
                          if (isDisabled && !isSelected) return;

                          // Save current scroll position
                          const scrollPosition = forwardScrollRef.current?.scrollTop || 0;

                          setSelectedForwardContacts(prev =>
                            isSelected
                              ? prev.filter(id => id !== contact.id)
                              : [...prev, contact.id]
                          );

                          // Restore scroll position after state update
                          setTimeout(() => {
                            if (forwardScrollRef.current) {
                              forwardScrollRef.current.scrollTop = scrollPosition;
                            }
                          }, 0);
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => { }}
                          className="mr-3 accent-green-600 h-5 w-5"
                          disabled={isDisabled && !isSelected}
                        />
                        <Avatar className="h-10 w-10 mr-3">
                          <AvatarImage src={getFileUrlMemo(contact.avatarUrl)} alt={contact.nome} />
                          <AvatarFallback>{getInitials(contact.nome)}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-medium truncate dark:text-white">{contact.nome || contact.telefone}</span>
                          <span className="text-xs text-gray-500 truncate dark:text-gray-400">{contact.telefone}</span>
                        </div>
                      </li>
                    );
                  })}
                {forwardContacts.length === 0 && !forwardLoading && (
                  <li className="text-center text-gray-400 py-10">{t('atendimento.noContactsFound')}</li>
                )}
                {forwardContacts.length > 0 && forwardHasMore && (
                  <li className="text-center py-4">
                    <div className="flex items-center justify-center text-gray-400">
                      {forwardLoadingMore ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-green-500 mr-2"></div>
                      ) : (
                        <ChevronDown className="h-4 w-4 mr-2" />
                      )}
                      <span className="text-sm">
                        {forwardLoadingMore ? t('atendimento.loading') : t('atendimento.showingContacts', { shown: forwardContacts.length, total: forwardTotalCount })}
                      </span>
                    </div>
                  </li>
                )}
              </ul>
            )}
          </div>

          <DialogFooter>
            <div className="flex justify-between w-full">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {selectedForwardContacts.length > 0 && t('atendimento.contactsSelected', { count: selectedForwardContacts.length })}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowForwardModal(false);
                    setForwardMessage(null);
                    setForwardContacts([]);
                    setForwardSearchTerm('');
                    setSelectedForwardContacts([]);
                    setForwardScrollPosition(0);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="button"
                  onClick={handleForwardMessage}
                  disabled={selectedForwardContacts.length === 0 || forwardLoading}
                >
                  {forwardLoading ? t('atendimento.forwarding') : t('atendimento.forward', { count: selectedForwardContacts.length })}
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de envio de Template Meta (fora da janela de 24h) */}
      <MetaTemplateSendDialog
        open={showMetaTemplateDialog}
        onClose={() => setShowMetaTemplateDialog(false)}
        instanciaId={selectedInstance?.tipoApi === 'META' ? (selectedInstance?.id || '') : (linkedMetaForHybrid?.id || '')}
        onSend={handleSendMetaTemplate}
        disabled={enviando}
      />

      {/* Overlay de loading para início de atendimento */}
      {
        startingAttendance && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg p-6 flex items-center space-x-3">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="text-gray-700 dark:text-gray-300">{t('atendimento.startingAttendance')}</span>
            </div>
          </div>
        )
      }

      {/* Modal Marcar Alguém */}
      {showMentionModal && (
        <Dialog open={showMentionModal} onOpenChange={setShowMentionModal}>
          <DialogContent className="sm:max-w-[425px] dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="dark:text-white">{t('atendimento.mentionTitle')}</DialogTitle>
              <DialogDescription className="dark:text-gray-400">
                {t('atendimento.mentionDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <label className="text-right text-sm dark:text-gray-300">
                  {t('atendimento.mentionParticipant')}
                </label>
                <select
                  className="col-span-3 flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  value={mentionParticipantId}
                  onChange={(e) => setMentionParticipantId(e.target.value)}
                  disabled={loadingSystemUsers}
                >
                  <option value="">{loadingSystemUsers ? t('atendimento.loadingUsers') : t('atendimento.selectParticipant')}</option>
                  {systemUsersForMention.map(p => (
                    <option key={p.id} value={p.id}>{p.nome || p.id}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <label className="text-right text-sm dark:text-gray-300">
                  {t('atendimento.reason')}
                </label>
                <Input
                  className="col-span-3 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                  value={mentionReason}
                  onChange={(e) => setMentionReason(e.target.value)}
                  placeholder={t('atendimento.reasonPlaceholder')}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowMentionModal(false)} className="dark:border-slate-700 dark:text-gray-300">
                {t('common.cancel')}
              </Button>
              <Button
                onClick={async () => {
                  if (!mentionParticipantId) {
                    toast({ title: t('common.error'), description: t('atendimento.selectParticipantError'), variant: 'destructive' });
                    return;
                  }
                  try {
                    await api.post('/notas/mencao', {
                      ticketId: chatResumo?.id,
                      usuarioId: mentionParticipantId,
                      motivo: mentionReason
                    });
                    toast({ title: t('common.success'), description: t('atendimento.mentionSuccess') });
                    setShowMentionModal(false);
                    setMentionReason('');
                    setMentionParticipantId('');
                  } catch (error: any) {
                    toast({ title: t('common.error'), description: error.response?.data?.message || t('atendimento.mentionError'), variant: 'destructive' });
                  }
                }}
                disabled={!mentionParticipantId}
              >
                {t('atendimento.confirmMention')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Modal de Compartilhar Ticket */}
      <Dialog open={showShareModal} onOpenChange={(open) => { if (!open) { setSelectedShareUsers([]); } setShowShareModal(open); }}>
        <DialogContent className="sm:max-w-[500px] dark:bg-slate-800 dark:text-white">
          <DialogHeader>
            <DialogTitle>{t('atendimento.shareTicketTitle')}</DialogTitle>
            <DialogDescription>
              {t('atendimento.shareTicketDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>{t('atendimento.shareWithUsers')}</Label>
              <SelectWithSearch
                options={shareUsers.map((u: any) => ({ value: u.id, label: `${u.nome} (${u.email})` }))}
                value={shareUsers.filter(u => selectedShareUsers.includes(u.id)).map((u: any) => ({ value: u.id, label: `${u.nome} (${u.email})` }))}
                onChange={(val) => setSelectedShareUsers(val.map(v => v.value))}
                placeholder={t('atendimento.selectUsersPlaceholder')}
                loading={loadingShare}
                disabled={loadingShare}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setShowShareModal(false); setSelectedShareUsers([]); }}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleShareTicket} disabled={!selectedShareUsers.length || loadingShare}>
              {loadingShare ? t('atendimento.sharing') : t('atendimento.shareWithCount', { count: selectedShareUsers.length })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AtendimentoPage;
